import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FJELLTOPPER as _TOPPER_RAW, LISTER as LISTER_RAW, VERSJON as _PEAKS_VERSJON } from "./peaks";
import { nb as _NB, nn as _NN, ordbokFor } from "./sprak";
import { supabase, supabaseKlar } from "./supabase";

// Fast, kompakt breidde for element som ALLTID skal halde seg sentrerte og
// samla (nav-raden, kart-popup-panel, admin-panelet) — sjølv om sjølve app-
// ramma (s.app) vert breiare på store skjermar. Éin kjelde, så me slepp å
// endre 12 stader kvar gong talet skal justerast.
const KOMPAKT_BREDDE = 480;

// ─── SPRÅK ───────────────────────────────────────────────────────────────────
// Bokmål er hovudspråk. tr() les frå gjeldande ordbok på modulnivå, så vi slepp
// å sende «t» som prop gjennom alle komponentane. App held ein språk-state som
// tvingar re-render når brukaren byter språk.
let _GJELDANDE_SPRAK = "nb";
let _ORDBOK = _NB;
function settAktivtSprak(kode) {
  _GJELDANDE_SPRAK = kode === "nn" ? "nn" : "nb";
  _ORDBOK = ordbokFor(_GJELDANDE_SPRAK);
}
// Les telefonspråket: er einga sett til nynorsk (nn / nn-NO) → "nn", elles "nb".
function oppdagSpråk() {
  try {
    const liste = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || ""];
    for (const l of liste) {
      if ((l || "").toLowerCase().startsWith("nn")) return "nn";
    }
  } catch (e) {}
  return "nb";
}
// tr("nøkkel") eller tr("nøkkel", { n: 3 }) for interpolasjon av {n}
function tr(key, vars) {
  let s = _ORDBOK[key];
  if (s === undefined) s = _NB[key]; // fallback til bokmål
  if (s === undefined) return key;   // siste utveg: vis nøkkelen
  if (vars) for (const k in vars) s = s.replaceAll("{" + k + "}", vars[k]);
  return s;
}
// Badge-tekst via id → språkfil (BADGES-arrayet held berre metadata + fallback).
// Dynamisk genererte badges (t.d. kommune-trofé) kan i staden setje namnDirekte/
// skildringDirekte, sidan kommunenamn ikkje skal omsetjast og id-en varierer.
const badgeNamn = (b) => b.namnDirekte ?? tr(`badge.${b.id}.namn`);
const badgeSkildring = (b) => b.skildringDirekte ?? tr(`badge.${b.id}.skildring`);
const badgeHemSkildring = (b) => tr(`badge.${b.id}.hemSkildring`);
const badgeHemAvsloring = (b) => tr(`badge.${b.id}.hemAvsloring`);
const _BASE_TOPPER = (_TOPPER_RAW || []).filter(t => t.lat && t.lng && (t.høgde > 0 || t.type === "hytte"));
const HYTTE_POENG = 50;   // fast poengsum per dagsturhytte (ingen høgde å rekne frå)
const HYTTE_RADIUS = 150; // større GPS-radius enn toppar (hytte ligg sjeldan på eit punkt)

// FIX: Trygge localStorage-hjelparar — localStorage kan vere utilgjengeleg
// (privat modus) eller full (QuotaExceededError). Må aldri krasje appen.
function lsGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, value); return true; } catch { return false; }
}
function lsRemove(key) {
  try { localStorage.removeItem(key); } catch {}
}

// ─── SYNK-KØ (5b) ─────────────────────────────────────────────────────────────
// Skriving er optimistisk lokalt; Supabase er sanninga. Feilar skrivinga
// (offline/feil), hamnar rada i ein kø som blir tømt når nett kjem tilbake.
// Kvar kø-rad ber bruker_id, så ein annan brukar ikkje sender ein føregåande
// brukar sine rader (RLS ville uansett avvist det).
const SYNK_KO_KEY = "fjell_synk_ko";
function lesSynkKo() {
  try { return JSON.parse(lsGet(SYNK_KO_KEY) || "[]"); } catch { return []; }
}
function skrivSynkKo(ko) { lsSet(SYNK_KO_KEY, JSON.stringify(ko)); }

// Stabil klient-id (uuid) → blir primærnøkkel i Supabase. Gjer skriving idempotent:
// same registrering kan aldri bli to rader (kø-retry/race gir duplikatnøkkel-feil).
function lagId() {
  try { if (crypto?.randomUUID) return crypto.randomUUID(); } catch (e) {}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

async function skrivRadTilSupabase(rad) {
  if (!supabaseKlar()) return false;
  try {
    const { error } = await supabase.from("registreringar").insert({
      id: rad.id, bruker_id: rad.bruker_id, topp_id: rad.topp_id, dato: rad.dato,
    });
    if (!error) return true;
    if (error.code === "23505") return true; // duplikatnøkkel = allereie skrive (idempotent)
    return false;
  } catch (e) { return false; }
}

// Skriv ei ny registrering: prøv straks, kø ved feil. Returnerer true om lagra no.
async function koEllerSkriv(rad) {
  const ok = await skrivRadTilSupabase(rad);
  if (!ok) { const ko = lesSynkKo(); ko.push(rad); skrivSynkKo(ko); }
  return ok;
}

// ─── TOPPBILETE → SUPABASE STORAGE ───────────────────────────────────────────
// Lastar opp eit toppbilete (base64) til bucket «toppbilete» og skriv URL-en til
// registreringa. Returnerer URL ved suksess, null ved feil (t.d. offline) — då
// blir base64 verande i lokal cache og prøvd på nytt ved neste app-opning (5c).
// Fil: <bruker_id>/<besøk_id>.jpg — same eigarskapsmønster som profilbilete.
async function lastOppToppbilete(brukerId, besøkId, base64) {
  if (!supabaseKlar() || !brukerId || !besøkId) return null;
  try {
    const blob = await (await fetch(base64)).blob();
    const sti = `${brukerId}/${besøkId}.jpg`;
    const { error: upErr } = await supabase.storage.from("toppbilete").upload(sti, blob, { upsert: true, contentType: "image/jpeg" });
    if (upErr) return null;
    const { data } = supabase.storage.from("toppbilete").getPublicUrl(sti);
    const url = `${data.publicUrl}?v=${Date.now()}`;
    const { error: dbErr } = await supabase.from("registreringar").update({ bilde_url: url }).eq("id", besøkId);
    if (dbErr) return null; // rad ikkje synka enno (offline-kø) → prøv att seinare
    return url;
  } catch (e) { return null; }
}

// Slettar toppbiletet frå Storage og nullstiller bilde_url. Best effort —
// feilar det (offline), er verste utfall ei foreldrelaus fil i bucket.
// FIX: nullstiller òg er_hovudbilde — elles kan ein sletta besøk-rad stå att
// som «hovudbilete» med bilde_url:null, og toppen ville aldri fått nytt hero.
async function slettToppbilete(brukerId, besøkId) {
  if (!supabaseKlar() || !brukerId || !besøkId) return;
  try {
    await supabase.storage.from("toppbilete").remove([`${brukerId}/${besøkId}.jpg`]);
    await supabase.from("registreringar").update({ bilde_url: null, er_hovudbilde: false }).eq("id", besøkId);
  } catch (e) {}
}

// ─── HOVUDBILETE-VERIFISERING ─────────────────────────────────────────────
// Berre kamera-tekne bilete (jf. BildeModal sin "kjelde"-sporing) kan bli
// hovudbilete for ein topp, og berre det FØRSTE godkjende biletet gjeld —
// handheva av ein unik partial-indeks i Supabase (topp_id where er_hovudbilde).
// Sjå SQL-migrasjonen i referansedokumentet for oppsettet.
// [FJERNA] harAlleredeHovudbilde er erstatta av hentHovudbileteStatus, som
// hentar BÅDE eigar og leiar i eitt kall.

// Set eit besøk sitt bilete som hovudbilete for toppen. Returnerer false om
// nokon annan rakk først (unik-indeksen slår då til med feilkode 23505) —
// då må kallaren vise ei roleg melding, ikkje late som det gjekk bra.
async function settHovudbilde(besøkId) {
  if (!supabaseKlar() || !besøkId) return false;
  try {
    const { error } = await supabase.from("registreringar").update({ er_hovudbilde: true, har_vore_hovudbilde: true }).eq("id", besøkId);
    if (!error) return true;
    return false;
  } catch (e) { return false; }
}

// Fjernar BERRE hovudbilete-merkelappen — biletet sjølv (bilde_url) rører vi
// ikkje. Det held fram å liggje i eigaren sin logg som eit heilt vanleg
// turbilete. Bruk slettToppbilete() i staden om biletet faktisk skal vekk.
async function fjernHovudbildeStatus(besøkId) {
  if (!supabaseKlar() || !besøkId) return false;
  try {
    const { error } = await supabase.from("registreringar").update({ er_hovudbilde: false }).eq("id", besøkId);
    return !error;
  } catch (e) { return false; }
}

// Hentar kven som eig hovudbiletet og kven som ligg fremst på besøkstal for
// ein topp, i eitt kall — brukt til å vise «du leier»-hintet og til å avgjere
// om utfordre-popup skal visast etter kamera-opplasting.
// Full leiartavle for ein topp — éi rad per person, sortert flest besøk
// først. Nyttig som eit eige, valfritt "utforsk"-visning ved sida av den
// vanlege kronologiske "Siste turar"-lista.
// Global kjede-rekord — flest ULIKE toppar registrert same dag, av nokon
// nokon gong. Ingen lagra tilstand: "overtaking" skjer automatisk berre ved
// at nokon andre gjer ein lengre dag seinare.
// Full topplista over kjede-lengder — kvar person sin beste enkeltdag.
// Hentar dei faktiske toppane (som lat/lng-punkt, i rekkjefølgje) for éin
// bestemt kjede — brukt til å teikne henne på kartet.
async function hentKjedeDetaljar(brukerId, dato) {
  if (!supabaseKlar() || !brukerId || !dato) return [];
  try {
    const { data, error } = await supabase.rpc("kjede_detaljar", { p_bruker_id: brukerId, p_dato: dato });
    if (error || !Array.isArray(data)) return [];
    return data
      .map(r => ALLE_TOPPER.find(t => String(t.id) === String(r.topp_id)))
      .filter(Boolean)
      .map(t => ({ lat: t.lat, lng: t.lng, namn: t.namn }));
  } catch (e) { return []; }
}

async function hentKjedeTopplista() {
  if (!supabaseKlar()) return [];
  try {
    const { data, error } = await supabase.rpc("kjede_topplista");
    if (error || !Array.isArray(data)) return [];
    return data;
  } catch (e) { return []; }
}

// Passiv varsling ("sjå neste gong du opnar appen") for hovudbilete-teken og
// kjede-rekord-teken. IKKJE ekte push — berre henta og vist når brukaren
// uansett opnar appen.
async function hentUlesteHendingar() {
  if (!supabaseKlar()) return [];
  try {
    const { data, error } = await supabase.rpc("hent_uleste_hendingar");
    if (error || !Array.isArray(data)) return [];
    return data;
  } catch (e) { return []; }
}

async function merkHendingarSett(ids) {
  if (!supabaseKlar() || !ids?.length) return;
  try { await supabase.rpc("merk_hendingar_sett", { p_ids: ids }); } catch (e) {}
}

async function hentKjedeRekord() {
  if (!supabaseKlar()) return null;
  try {
    const { data, error } = await supabase.rpc("kjede_rekord");
    const rad = Array.isArray(data) ? data[0] : data;
    if (error || !rad) return null;
    return { brukerId: rad.bruker_id, visingsnamn: rad.visingsnamn, profilBilde: rad.profil_bilde, lengde: rad.lengde, dato: rad.dato };
  } catch (e) { return null; }
}

async function hentLeiartavle(toppId) {
  if (!supabaseKlar() || !toppId) return [];
  try {
    const { data, error } = await supabase.rpc("leiartavle_for_topp", { p_topp_id: String(toppId) });
    if (error || !Array.isArray(data)) return [];
    return data;
  } catch (e) { return []; }
}

async function hentHovudbileteStatus(toppId) {
  if (!supabaseKlar() || !toppId) return { eigar: null, leiar: null, eigarTalBesok: 0, leiarNamn: null, leiarBilde: null, leiarTalBesok: 0 };
  try {
    const { data, error } = await supabase.rpc("hovudbilde_status", { p_topp_id: String(toppId) });
    const rad = Array.isArray(data) ? data[0] : data;
    if (error || !rad) return { eigar: null, leiar: null, eigarTalBesok: 0, leiarNamn: null, leiarBilde: null, leiarTalBesok: 0 };
    return {
      eigar: rad.eigar_bruker_id || null,
      leiar: rad.leiar_bruker_id || null,
      eigarTalBesok: rad.eigar_tal_besok || 0,
      leiarNamn: rad.leiar_namn || null,
      leiarBilde: rad.leiar_bilde || null,
      leiarTalBesok: rad.leiar_tal_besok || 0,
    };
  } catch (e) { return { eigar: null, leiar: null, eigarTalBesok: 0, leiarNamn: null, leiarBilde: null, leiarTalBesok: 0 }; }
}

// Utfordrar (overtek) hovudbiletet på vegner av innlogga brukar. Leiarskap
// (flest besøk) vert VERIFISERT PÅ NYTT server-side i RPC-en — klienten sin
// eigen «erLeiarUtanEigarskap»-sjekk er berre eit hint for UI-et, ikkje ei
// tilgangsavgjerd. Returnerer false om brukaren ikkje lenger er leiar (t.d.
// nokon andre har teke igjen medan biletet vart teke).
async function utfordreHovudbilde(toppId, besøkId) {
  if (!supabaseKlar() || !toppId || !besøkId) return false;
  try {
    const { data, error } = await supabase.rpc("utfordre_hovudbilde", { p_topp_id: String(toppId), p_ny_besok_id: besøkId });
    if (error) return false;
    return !!data;
  } catch (e) { return false; }
}

// Tøm køen for gjeldande brukar. Race-vakt hindrar to samtidige tømmingar.
let _synkPågår = false;
async function synkUtKo(brukerId) {
  if (!brukerId || !supabaseKlar() || _synkPågår) return 0;
  const ko = lesSynkKo();
  if (!ko.length) return 0;
  _synkPågår = true;
  try {
    const attver = [];
    let sendt = 0;
    for (const rad of ko) {
      if (rad.bruker_id !== brukerId) { attver.push(rad); continue; } // annan brukar — vent
      const ok = await skrivRadTilSupabase(rad);
      if (ok) sendt++; else attver.push(rad);
    }
    skrivSynkKo(attver);
    return sendt;
  } finally { _synkPågår = false; }
}

// Sjekk om peaks.js har endra seg — tving reload om ny versjon
// FIX: try/catch via lsGet/lsSet — feil her gav svart skjerm før appen lasta.
// Reload skjer berre om lagringa lukkast, elles risikerer vi reload-loop.
const _CACHE_KEY = "fjell_peaks_versjon";
const _lagra = lsGet(_CACHE_KEY);
if (_PEAKS_VERSJON && _lagra !== _PEAKS_VERSJON) {
  const lagra_ok = lsSet(_CACHE_KEY, _PEAKS_VERSJON);
  if (_lagra !== null && lagra_ok) window.location.reload(); // Ikkje reload første gong
}
// Module-level ref so alle komponentar kan bruke oppdaterte peaks
let ALLE_TOPPER = _BASE_TOPPER;

// Gjer LISTER_RAW om til array med fargar
const LISTE_FARGAR = { 10: "#fbbf24", 20: "#34d399", 50: "#60a5fa", 100: "#a78bfa" };
const LISTER = Object.entries(LISTER_RAW).map(([id, label]) => ({
  id: parseInt(id),
  label,
  farge: LISTE_FARGAR[id] || "#a8d8a8",
})).sort((a, b) => a.id - b.id);

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" });
}
function formatDateTime(iso) {
  const d = new Date(iso);
  const dato = d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" });
  const tid = d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  return `${dato} kl. ${tid}`;
}

const FARGAR = { "7-fjell": "#e8a020", "Byfjell": "#3a9aee", "Høgfjell": "#cc55cc" };
const MATCH_RADIUS = 50;

// ─── BADGE-SYSTEM ────────────────────────────────────────────────────────────
const BADGES = [
  { id:"b10",    ikon:"🥉", namn:"Klatrar",        skildring:"Besøkt 10 unike toppar",             hemmeleg:false, kategori:"topper" },
  { id:"b25",    ikon:"🥈", namn:"Fjellven",        skildring:"Besøkt 25 unike toppar",             hemmeleg:false, kategori:"topper" },
  { id:"b50",    ikon:"🥇", namn:"Toppjeger",       skildring:"Besøkt 50 unike toppar",             hemmeleg:false, kategori:"topper" },
  { id:"ball",   ikon:"💎", namn:"Bergensmester",   skildring:"Alle toppar i Bergen fullført",      hemmeleg:false, kategori:"topper" },
  // t10/t20/t50/t100 er ikkje lenger statiske her — dei vert generert dynamisk
  // per kommune i genererListeBadges(), sidan trofé-tildeling skjer per kommune.
  { id:"dobbel", ikon:"⚡", namn:"Dobbelen",        skildring:"2 toppar same dag",                  hemmeleg:false, kategori:"streak" },
  { id:"trippel",ikon:"🔥", namn:"Trippelen",       skildring:"3 toppar same dag",                  hemmeleg:false, kategori:"streak" },
  { id:"femmar", ikon:"🌋", namn:"Femmaren",        skildring:"5 toppar same dag",                  hemmeleg:false, kategori:"streak", årleg:true },
  { id:"over1000-10",  ikon:"⛰️", namn:"Tusenmeteren",         skildring:"10 registreringar over 1000 moh",  hemmeleg:false, kategori:"hogde" },
  { id:"over1000-100", ikon:"🏔️", namn:"Tusenmeterveteranen",  skildring:"100 registreringar over 1000 moh", hemmeleg:false, kategori:"hogde" },
  { id:"over2000-10",  ikon:"🗻", namn:"Totusenklubben",       skildring:"10 registreringar over 2000 moh",  hemmeleg:false, kategori:"hogde" },
  { id:"over2000-50",  ikon:"🗻", namn:"Totusenrutinert",      skildring:"50 registreringar over 2000 moh",  hemmeleg:false, kategori:"hogde" },
  { id:"over2000-100", ikon:"🗻", namn:"Totusenlegenda",       skildring:"100 registreringar over 2000 moh", hemmeleg:false, kategori:"hogde" },
  { id:"tidleg", ikon:"🌅", namn:"Morgonfugl",      skildring:"Registrert ein topp før kl. 07:00",  hemmeleg:false, kategori:"tid" },
  { id:"natt",   ikon:"🌙", namn:"Nattugla",        skildring:"Registrert ein topp etter kl. 21:00",hemmeleg:false, kategori:"tid" },
  { id:"vinter", ikon:"❄️", namn:"Vinterfjell",     skildring:"Topp i desember",                    hemmeleg:false, kategori:"tid", årleg:true },
  { id:"nyttaar",ikon:"🎆", namn:"Nyttårsklatrar",  skildring:"Topp 1. januar",                     hemmeleg:false, kategori:"tid", årleg:true },
  { id:"syttande",ikon:"🇳🇴", namn:"17. mai-klatrar", skildring:"Registrert ein topp på nasjonaldagen", hemmeleg:false, kategori:"tid", årleg:true },
  { id:"baal", ikon:"🔥", namn:"Jonsokbål",        skildring:"Besøkt Gullfjellet etter kl. 18:00 på sankthans (23. juni)", hemmeleg:false, kategori:"tid", årleg:true },
  { id:"sju", ikon:"❤️", namn:"7-fjellsturen", skildring:"Alle 7 fjell i rett rekkefølge same dag", hemmeleg:false, kategori:"spesial", årleg:true },
  { id:"h1", ikon:"🌄", namn:"Soloppgang",    skildring:"Registrert ein topp mellom 05:00–06:00",  hemmeleg:true, hemSkildring:"Du veit ikkje kva du leitar etter — men du vil vite det når du finn det",  hemAvsløring:"Registrert ein topp i soloppgangstime!" },
  { id:"h2", ikon:"🎂", namn:"Bursdagsklatrar", skildring:"Registrert ein topp på bursdagen din",  hemmeleg:true, hemSkildring:"Nokon dagar er spesielle", hemAvsløring:"Gratulerer med dagen — og med toppen!" },
  { id:"h3", ikon:"💯", namn:"Hundremannen",  skildring:"100 totale registreringar",               hemmeleg:true, hemSkildring:"Tida speler ei rolle",          hemAvsløring:"100 registreringar — ekte fjellfolk!" },
  { id:"h4", ikon:"❤️‍🔥", namn:"Sjufjellaren", skildring:"Alle 7 fjell same dag",                  hemmeleg:true, hemSkildring:"Ikkje alle toppar er like",      hemAvsløring:"Alle 7 fjell på éin dag — ein legende!" },
  { id:"h5", ikon:"🔟", namn:"Stamgjest",     skildring:"Same topp 10 gonger",                     hemmeleg:true, hemSkildring:"Nokre ting krev tolmod",         hemAvsløring:"Du kjenner denne toppen betre enn nokon!" },
];

// Rangering frå sjeldnaste til vanlegaste — brukt til å velje kva troféar som
// vert vist i den kompakte rada ved sida av eit visingsnamn (dei N høgast
// rangerte). Hemmelege/årlege spesial-badges reknast som sjeldnast.
const BADGE_RANGERING = [
  "h4","h5","h3","h2","h1",
  "sju","baal","syttande","nyttaar","vinter",
  "ball","over2000-100","t100","over2000-50","over1000-100","t50","over2000-10","t20","t10",
  "femmar","trippel","dobbel",
  "over1000-10","b50","b25","b10",
  "tidleg","natt",
];
function badgeRangOrden(id) {
  const i = BADGE_RANGERING.indexOf(id);
  return i === -1 ? BADGE_RANGERING.length : i;
}
// Returnerer dei N høgast rangerte, LÅSTE OPP badge-objekta + kor mange fleire
// brukaren har utover det (til "+N"-merkelappen).
function topBadges(låsteIds, alleTopper, n = 5) {
  const alle = [...BADGES, ...genererListeBadges(alleTopper || [])];
  const låste = alle.filter(b => låsteIds.has(b.id)).sort((a, b) => badgeRangOrden(a.id) - badgeRangOrden(b.id));
  return { synlege: låste.slice(0, n), resten: Math.max(0, låste.length - n) };
}

// ── BADGE-SAMANLIKNING ("kven andre har denne") ────────────────────────────
// Fyrste versjon: berre badges som "blir sanne for alltid" — ikkje dags-tal
// (dobbel/trippel/femmar), ikkje tid-eingongsflagg (tidleg/natt), og ikkje
// årlege (baal/syttande/nyttaar/vinter, sidan "kven har denne" er tvitydig
// på tvers av år). Hemmelege badges er UTELATE inntil vidare (spoiler-fare).
const BADGE_SAMANLIKNBAR_STATISK = new Set([
  "b10","b25","b50","ball","sju",
  "over1000-10","over1000-100","over2000-10","over2000-50","over2000-100",
]);
function erBadgeSamanliknbar(id) {
  return BADGE_SAMANLIKNBAR_STATISK.has(id);
}

// Speglar dine eigne LÅSTE OPP, samanliknbare badges til brukar_badges —
// billeg "best effort"-synk (ignorerer feil, ingen brukar-synleg feilmelding).
// Køyrer kvar gong Trofear-fana opnast, IKKJE ved kvar registrering.
async function synkBadgarTilSupabase(brukerId, låsteBadges) {
  if (!supabaseKlar() || !brukerId) return;
  const relevante = [...låsteBadges].filter(erBadgeSamanliknbar);
  if (!relevante.length) return;
  try {
    await supabase.from("brukar_badges").upsert(
      relevante.map(badge_id => ({ bruker_id: brukerId, badge_id })),
      { onConflict: "bruker_id,badge_id", ignoreDuplicates: true }
    );
  } catch (e) {}
}

async function hentBadgeEigarar(badgeId) {
  if (!supabaseKlar() || !badgeId) return [];
  try {
    const { data, error } = await supabase.rpc("badge_eigarar", { p_badge_id: badgeId });
    if (error || !Array.isArray(data)) return [];
    return data;
  } catch (e) { return []; }
}

function årleBadgeÅr(besøk, sjekkFn) {
  const årliste = [...new Set(besøk.map(b => new Date(b.dato).getFullYear()))];
  return årliste.filter(y => sjekkFn(besøk.filter(b => new Date(b.dato).getFullYear() === y)));
}

// Genererer eitt Topp 10/20/50/100-trofé PER KOMMUNE (i staden for eitt globalt
// som blandar alle kommunar saman), sidan trofé-tildelinga i admin-verktøyet
// alt skjer per kommune. Kommunar med færre enn 10 toppar totalt vert utelatne
// enno (t.d. Fjaler), sidan dei ventar på fleire toppar før dette gir meining.
const KOMMUNE_BADGE_MINIMUM = 10;
// Returnerer éin badge PER NIVÅ (Topp 10/20/50/100), ikkje éin per kommune —
// unngår at lista veks uoversiktleg når fleire kommunar kjem til. Kvar badge
// ber med seg kva kommunar som er fullført, vist som små merkelappar under
// (same mønster som dei årlege "Sju Fjell 2024, 2025…"-badgane alt bruker).
// besøktIds er valfri — utan han (t.d. når ein ser på NOKON ANNAN sine trofé)
// returnerer han berre metadata, ingen nedbryting (den andre brukaren sin
// eigen synk avgjer om sjølve badgen er låst opp eller ikkje).
function genererListeBadges(alleTopper, besøktIds) {
  const kommuneTeljar = {};
  alleTopper.forEach(t => { if (t.kommune) kommuneTeljar[t.kommune] = (kommuneTeljar[t.kommune] || 0) + 1; });
  const kvalifiserte = Object.keys(kommuneTeljar).filter(k => kommuneTeljar[k] >= KOMMUNE_BADGE_MINIMUM).sort();
  return [10, 20, 50, 100].map(n => {
    const fullførte = besøktIds ? kvalifiserte.filter(kommune => {
      const topper = alleTopper.filter(t => t.kommune === kommune && t.lister.includes(n));
      return topper.length > 0 && topper.every(t => besøktIds.has(t.id));
    }) : [];
    return {
      id: `t${n}`,
      ikon: "✅",
      namnDirekte: `Topp ${n}`,
      skildringDirekte: kvalifiserte.length
        ? `${fullførte.length} av ${kvalifiserte.length} kommunar fullført`
        : `Fullført Topp ${n}-lista`,
      hemmeleg: false,
      kategori: "lister",
      kommunar: fullførte, // brukt til chip-rada, tomt for andre brukarar sitt syn
    };
  });
}

function sjekkBadges(besøk, besøktIds, alleTopper, bursdagOverride) {
  const låst = new Set();
  const idag = new Date();

  if (besøktIds.size >= 10) låst.add("b10");
  if (besøktIds.size >= 25) låst.add("b25");
  if (besøktIds.size >= 50) låst.add("b50");
  if (besøktIds.size >= alleTopper.length) låst.add("ball");

  // Éin badge per nivå — låst opp så snart MINST ÉIN kvalifisert kommune er
  // fullført (ikkje alle). Sjå genererListeBadges() over for same logikk.
  const kommuneTeljar = {};
  alleTopper.forEach(t => { if (t.kommune) kommuneTeljar[t.kommune] = (kommuneTeljar[t.kommune] || 0) + 1; });
  const kvalifiserteKommunar = Object.keys(kommuneTeljar).filter(k => kommuneTeljar[k] >= KOMMUNE_BADGE_MINIMUM);
  [10, 20, 50, 100].forEach(n => {
    const harMinstEinFullført = kvalifiserteKommunar.some(kommune => {
      const topper = alleTopper.filter(t => t.kommune === kommune && t.lister.includes(n));
      return topper.length > 0 && topper.every(t => besøktIds.has(t.id));
    });
    if (harMinstEinFullført) låst.add(`t${n}`);
  });

  const dagsMap = {};
  besøk.forEach(b => {
    const d = new Date(b.dato).toDateString();
    dagsMap[d] = (dagsMap[d] || new Set()).add(b.toppId);
  });
  const dagsMaks = Math.max(0, ...Object.values(dagsMap).map(s => s.size));
  if (dagsMaks >= 2) låst.add("dobbel");
  if (dagsMaks >= 3) låst.add("trippel");
  if (dagsMaks >= 5) låst.add("femmar");

  besøk.forEach(b => {
    const d = new Date(b.dato);
    const h = d.getHours();
    const mnd = d.getMonth();
    const dag = d.getDate();
    if (h < 7) låst.add("tidleg");
    if (h >= 21) låst.add("natt");
    if (mnd === 11) låst.add("vinter");
    if (mnd === 0 && dag === 1) låst.add("nyttaar");
  });

  if (besøk.some(b => { const h = new Date(b.dato).getHours(); return h >= 5 && h < 6; })) låst.add("h1");
  // FIX: h2 «Bursdagsklatrar» hadde inga opplåsingslogikk — død badge.
  // Les bursdag (MM-DD) frå innstillingar og sjekk mot registreringane.
  // FIX: berre les EIGE bursdags-flagg når ingen override er gjeven. Blir
  // sjekkBadges kalla for ein ANNAN brukar (BrukarProfil), må kallaren gje
  // bursdagOverride=null eksplisitt — elles ville me teste DIN bursdag mot
  // DEIRA besøk, som både er feil og eit lite personvernsdrypp.
  const bursdag = bursdagOverride !== undefined ? bursdagOverride : lsGet("fjell_bursdag");
  if (bursdag) {
    const [bMnd, bDag] = bursdag.split("-").map(Number);
    if (bMnd && bDag && besøk.some(b => {
      const d = new Date(b.dato);
      return d.getMonth() + 1 === bMnd && d.getDate() === bDag;
    })) låst.add("h2");
  }
  if (besøk.length >= 100) låst.add("h3");
  const toppCount = {};
  besøk.forEach(b => { toppCount[b.toppId] = (toppCount[b.toppId]||0)+1; });
  if (Object.values(toppCount).some(c => c >= 10)) låst.add("h5");
  if (Object.values(dagsMap).some(s => s.size >= 7)) låst.add("h4");

  const sjuIds = (() => {
    const fraGruppe = alleTopper
      .filter(t => t.grupper && t.grupper.includes("7-fjellsturen"))
      .sort((a, b) => (a.gruppeRekkefølge || 0) - (b.gruppeRekkefølge || 0));
    if (fraGruppe.length === 7) return fraGruppe.map(t => t.id);
    return ["Lyderhorn","Damsgårdsfjellet","Løvstakken","Ulriken","Fløy","Rundemanen","Sandviksfjellet"]
      .map(n => alleTopper.find(t => t.namn.toLowerCase().includes(n.toLowerCase()))?.id)
      .filter(Boolean);
  })();

  if (sjuIds.length === 7) {
    const dagBesøk = {};
    besøk.forEach(b => {
      const d = new Date(b.dato).toDateString();
      if (!dagBesøk[d]) dagBesøk[d] = [];
      dagBesøk[d].push(b);
    });
    for (const [dag, dagB] of Object.entries(dagBesøk)) {
      const sortert = [...dagB].sort((a,b) => new Date(a.dato) - new Date(b.dato));
      const dagIds = sortert.map(b => b.toppId);
      let sjuIdx = 0;
      for (const id of dagIds) {
        if (id === sjuIds[sjuIdx]) sjuIdx++;
        if (sjuIdx === 7) { låst.add("sju"); break; }
      }
      if (låst.has("sju")) break;
    }
  }

  const årlege = {};

  const femmarÅr = årleBadgeÅr(besøk, (årsB) => {
    const dagMap = {};
    årsB.forEach(b => { const d = new Date(b.dato).toDateString(); dagMap[d] = (dagMap[d]||new Set()).add(b.toppId); });
    return Object.values(dagMap).some(s => s.size >= 5);
  });
  if (femmarÅr.length) årlege["femmar"] = femmarÅr;

  const vinterÅr = årleBadgeÅr(besøk, (årsB) => årsB.some(b => new Date(b.dato).getMonth() === 11));
  if (vinterÅr.length) årlege["vinter"] = vinterÅr;

  const nyttaarÅr = årleBadgeÅr(besøk, (årsB) => årsB.some(b => { const d = new Date(b.dato); return d.getMonth()===0 && d.getDate()===1; }));
  if (nyttaarÅr.length) årlege["nyttaar"] = nyttaarÅr;

  const syttandeÅr = årleBadgeÅr(besøk, (årsB) => årsB.some(b => { const d = new Date(b.dato); return d.getMonth()===4 && d.getDate()===17; }));
  if (syttandeÅr.length) { årlege["syttande"] = syttandeÅr; låst.add("syttande"); }

  // FIX: eksakt match — «includes» trefte også Litlagullfjellet
  const GULLFJELLET_NAMN = ["gullfjellet", "gullfjelltoppen"];
  const bålÅr = årleBadgeÅr(besøk, (årsB) => årsB.some(b => {
    const d = new Date(b.dato);
    if (d.getMonth() !== 5 || d.getDate() !== 23) return false;
    if (d.getHours() < 18) return false;
    const topp = alleTopper.find(t => String(t.id) === String(b.toppId));
    return topp && GULLFJELLET_NAMN.includes(topp.namn.toLowerCase().trim());
  }));
  if (bålÅr.length) { årlege["baal"] = bålÅr; låst.add("baal"); }

  if (sjuIds.length === 7) {
    const sjuÅr = årleBadgeÅr(besøk, (årsB) => {
      const dagBesøk = {};
      årsB.forEach(b => { const d = new Date(b.dato).toDateString(); if(!dagBesøk[d]) dagBesøk[d]=[]; dagBesøk[d].push(b); });
      return Object.values(dagBesøk).some(dagB => {
        const sortert = [...dagB].sort((a,b) => new Date(a.dato)-new Date(b.dato));
        let idx = 0;
        for (const b of sortert) { if (b.toppId === sjuIds[idx]) idx++; if (idx===7) return true; }
        return false;
      });
    });
    if (sjuÅr.length) årlege["sju"] = sjuÅr;
    if (sjuÅr.length) låst.add("sju");
  }
  if (femmarÅr.length) låst.add("femmar");
  if (vinterÅr.length) låst.add("vinter");
  if (nyttaarÅr.length) låst.add("nyttaar");

  // ── HØGDE-TROFEAR — heilt uavhengige av lister/regionar ──────────────────
  // Tel KVAR registrering (ikkje unike toppar) der toppen er over grensa.
  // Ikkje årleg — kumulativt over heile historikken. Slår opp høgd via
  // alleTopper (ikkje b.høgde) så det er korrekt òg for synteti­serte besøk
  // frå ein ANNAN brukar (BrukarProfil), som ikkje har høgde cacha lokalt.
  const høgdeForBesøk = (b) => alleTopper.find(x => String(x.id) === String(b.toppId))?.høgde ?? b.høgde ?? 0;
  const tal1000 = besøk.filter(b => høgdeForBesøk(b) >= 1000).length;
  const tal2000 = besøk.filter(b => høgdeForBesøk(b) >= 2000).length;
  if (tal1000 >= 10)  låst.add("over1000-10");
  if (tal1000 >= 100) låst.add("over1000-100");
  if (tal2000 >= 10)  låst.add("over2000-10");
  if (tal2000 >= 50)  låst.add("over2000-50");
  if (tal2000 >= 100) låst.add("over2000-100");

  return { låst, årlege };
}

// ─── POENGSYSTEM ─────────────────────────────────────────────────────────────
const LISTE_POENG   = { 10: 100, 20: 80, 50: 60, 100: 40 };
const LISTE_BONUS   = { 10: 1000, 20: 2000, 50: 3000, 100: 5000 };
const BONUS_NØKKEL  = (listeId) => `bonus_liste_${listeId}`;

function poengForTopp(topp) {
  if (topp?.type === "hytte") return HYTTE_POENG;
  if (!topp?.lister?.length) return 40;
  return LISTE_POENG[Math.min(...topp.lister)] ?? 40;
}

function harFåttBonus(listeId) {
  return lsGet(BONUS_NØKKEL(listeId)) === "1";
}
function settBonus(listeId) {
  lsSet(BONUS_NØKKEL(listeId), "1");
}

function sjekkNyFullførteLister(besøktIds, tidlegareFullførte) {
  return LISTER.map(l => l.id).filter(id => {
    if (harFåttBonus(id)) return false;
    const topper = ALLE_TOPPER.filter(t => t.lister.includes(id));
    return topper.length > 0 && topper.every(t => besøktIds.has(t.id));
  });
}

function beregnTotalPoeng(besøk, alleTopper) {
  let total = besøk.reduce((sum, b) => {
    const topp = alleTopper.find(t => String(t.id) === String(b.toppId));
    return sum + (topp ? poengForTopp(topp) : 0);
  }, 0);
  LISTER.forEach(l => {
    if (harFåttBonus(l.id)) total += LISTE_BONUS[l.id] ?? 0;
  });
  return total;
}

// ─── ADMIN HELPERS ───────────────────────────────────────────────────────────
const ADMIN_PEAKS_KEY = "fjell_admin_peaks";

function lagreAdminPeaks(peaks) {
  lsSet(ADMIN_PEAKS_KEY, JSON.stringify(peaks));
}

function slugify(s) {
  return (s || "").toString().trim().toLowerCase()
    .replace(/æ/g, "ae").replace(/ø/g, "o").replace(/å/g, "a")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
function genererNyToppId(namn, eksisterande, fylke, kommune) {
  const brukte = new Set(eksisterande.map(t => String(t.id)));
  const grunnslug = [fylke, kommune, namn].filter(Boolean).map(slugify).join("-") || slugify(namn);
  let id = grunnslug, n = 2;
  while (brukte.has(id)) { id = grunnslug + "-" + n; n++; }
  return id || ("ny-topp-" + Date.now());
}

function levenshtein(a, b) {
  a = a.toLowerCase(); b = b.toLowerCase();
  const m = a.length, n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      d[i][j] = a[i-1] === b[j-1] ? d[i-1][j-1] : 1 + Math.min(d[i-1][j], d[i][j-1], d[i-1][j-1]);
    }
  }
  return d[m][n];
}
// Finn toppar med liknande namn nær eit punkt — brukt til å åtvare før ein ny topp
// vert oppretta, sidan Slettheia/Helebostkletten-hendingane viste at "same topp,
// skrivefeil retta" lett endar som to separate id-ar i staden for éin retta rad.
function finnLiknandeToppNær(namn, lat, lng, alleTopper, radiusKm = 3) {
  const namnLC = namn.trim().toLowerCase();
  return alleTopper.filter(t => {
    if (t.lat == null || t.lng == null) return false;
    const avstandKm = haversine(lat, lng, t.lat, t.lng) / 1000;
    if (avstandKm > radiusKm) return false;
    const tNamnLC = (t.namn || "").toLowerCase();
    if (tNamnLC === namnLC) return false; // eksakt likt namn er ikkje "liknande", handterast av id-generering
    return levenshtein(namnLC, tNamnLC) <= 3 || tNamnLC.includes(namnLC) || namnLC.includes(tNamnLC);
  });
}

function kanRegistrereIdag(toppId, besøk) {
  const idag = new Date().toDateString();
  return !besøk.some(b => b.toppId === toppId && new Date(b.dato).toDateString() === idag);
}

function dagstreakBonus(besøk) {
  const idag = new Date().toDateString();
  const idagCount = new Set(
    besøk.filter(b => new Date(b.dato).toDateString() === idag).map(b => b.toppId)
  ).size;
  if (idagCount === 0) return 0;
  return Math.min(idagCount * 10, 50);
}

// ─── LYD ─────────────────────────────────────────────────────────────────────
function spelaLyd(type = "registrer") {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (type === "registrer") {
      const tonar = [523.25, 659.25, 783.99];
      tonar.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "sine"; osc.frequency.value = freq;
        const start = ctx.currentTime + i * 0.12;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.3, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
        osc.start(start); osc.stop(start + 0.35);
      });
    }
    if (type === "fanfare") {
      const melodiTonar = [
        { freq: 523.25, start: 0.0,  dur: 0.15 },
        { freq: 523.25, start: 0.15, dur: 0.15 },
        { freq: 523.25, start: 0.3,  dur: 0.15 },
        { freq: 415.30, start: 0.45, dur: 0.10 },
        { freq: 523.25, start: 0.55, dur: 0.25 },
        { freq: 659.25, start: 0.8,  dur: 0.5  },
      ];
      melodiTonar.forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "triangle"; osc.frequency.value = freq;
        const t = ctx.currentTime + start;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.25, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
        osc.start(t); osc.stop(t + dur + 0.05);
      });
    }
  } catch(e) {}
}

// ─── BADGE MODAL ─────────────────────────────────────────────────────────────
function BadgeModal({ badges, aksent, darkMode, onLukk }) {
  const [idx, setIdx] = useState(0);
  const badge = badges[idx];
  const erSiste = idx === badges.length - 1;
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);

  useEffect(() => { spelaLyd("registrer"); }, [idx]);

  if (!badge) return null;

  const erHjerte = badge.id === "sju";
  const erFlagg  = badge.id === "syttande";

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.85)",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:32, fontFamily:"-apple-system,'SF Pro Display',sans-serif",
      animation:"fadeIn 0.3s ease",
    }}
    onClick={() => erSiste ? onLukk() : setIdx(i => i+1)}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:scale(0.85)}to{opacity:1;transform:scale(1)}} @keyframes badgePop{0%{transform:scale(0.5) rotate(-10deg);opacity:0}60%{transform:scale(1.15) rotate(3deg)}100%{transform:scale(1) rotate(0deg);opacity:1}}`}</style>
      {badges.length > 1 && (
        <div style={{ position:"absolute", top:24, right:24, fontSize:13, color:"rgba(255,255,255,0.5)", fontWeight:"600" }}>
          {idx+1} / {badges.length}
        </div>
      )}
      <div style={{ animation:"badgePop 0.5s cubic-bezier(0.34,1.56,0.64,1) both", marginBottom:24 }}>
        {erHjerte ? (
          <svg width="120" height="120" viewBox="0 0 100 110" fill="none">
            <path d="M50 95 L8 45 L15 20 L25 28 L28 18 L35 26 L38 14 L44 24 L47 10 L50 22 L53 10 L56 24 L62 14 L65 26 L72 18 L75 28 L85 20 L92 45 Z" fill={aksent.aksent}/>
          </svg>
        ) : erFlagg ? (
          <svg width="120" height="90" viewBox="0 0 22 16">
            <rect width="22" height="16" rx="2" fill="#EF2B2D"/>
            <rect x="6" y="0" width="3" height="16" fill="white"/>
            <rect x="0" y="6" width="22" height="3" fill="white"/>
            <rect x="7" y="0" width="1" height="16" fill="#002868"/>
            <rect x="0" y="7" width="22" height="1" fill="#002868"/>
          </svg>
        ) : (
          <div style={{ fontSize:100, lineHeight:1, filter:"drop-shadow(0 4px 16px rgba(0,0,0,0.5))" }}>{badge.ikon}</div>
        )}
      </div>
      <div style={{ textAlign:"center", marginBottom:32 }}>
        <div style={{ fontSize:13, fontWeight:"700", color:aksent.aksent, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>{tr("trofe.nytt")}</div>
        <div style={{ fontSize:32, fontWeight:"800", color:"white", marginBottom:8 }}>{badgeNamn(badge)}</div>
        <div style={{ fontSize:16, color:"rgba(255,255,255,0.65)", lineHeight:1.5, maxWidth:280 }}>{badge.hemmeleg ? (badgeHemAvsloring(badge) || badgeSkildring(badge)) : badgeSkildring(badge)}</div>
        {badge.hemmeleg && (<div style={{ marginTop:10, fontSize:12, color:aksent.aksent, fontWeight:"600" }}>{tr("trofe.hemmelegAvslort")}</div>)}
      </div>
      <button onClick={(e) => { e.stopPropagation(); erSiste ? onLukk() : setIdx(i => i+1); }}
        style={{ padding:"14px 40px", background:aksent.aksent, color:"white", border:"none", borderRadius:100, fontSize:16, fontWeight:"700", cursor:"pointer" }}>
        {erSiste ? "🎉 Supert!" : `Neste → (${idx+2}/${badges.length})`}
      </button>
    </div>
  );
}

// ─── BADGE-RAD (kompakt) ────────────────────────────────────────────────────
// Dei N høgast rangerte, låste-opp troféa til ein brukar, vist som ei lita
// ikon-rad ved sida av visingsnamnet. "+N" viser kor mange fleire dei har.
// Klikkbar (via onVisAlle) for å opne full liste.
function BadgeRad({ låsteBadges, alleTopper, onVisAlle, storleik = 22 }) {
  if (!låsteBadges || låsteBadges.size === 0) return null;
  const { synlege, resten } = topBadges(låsteBadges, alleTopper, 10);
  if (!synlege.length) return null;
  return (
    <div onClick={onVisAlle} style={{ display:"flex", alignItems:"center", gap:4, cursor: onVisAlle ? "pointer" : "default" }}>
      {synlege.map(b => (
        <span key={b.id} title={badgeNamn(b)} style={{ fontSize: storleik * 0.9, lineHeight:1 }}>{b.ikon}</span>
      ))}
      {resten > 0 && (
        <span style={{ fontSize: storleik * 0.5, fontWeight:"700", color:"currentColor", opacity:0.65, marginLeft:2 }}>+{resten}</span>
      )}
      {onVisAlle && (
        <span style={{ fontSize: storleik * 0.75, fontWeight:"700", color:"currentColor", opacity:0.85, marginLeft:2 }}>›</span>
      )}
    </div>
  );
}

// ─── TROFÉLISTE (annan brukar, lesevising) ─────────────────────────────────
// Full liste over ein annan brukar sine troféar, opna frå BrukarProfil. Same
// løyndoms-prinsipp som eigen Trofear-fane: hemmelege badges dei IKKJE har
// låst opp vert ikkje viste i det heile (verken låst eller ulåst).
function TrofeListeModal({ visingsnamn, låsteBadges, alleTopper, darkMode, aksent, onClose }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const kategoriar = [
    { id:"topper", namn:tr("kat.topper") },
    { id:"hogde",  namn:tr("kat.hogde") },
    { id:"lister", namn:tr("kat.lister") },
    { id:"streak", namn:tr("kat.streak") },
    { id:"tid",    namn:tr("kat.tid") },
    { id:"spesial",  namn:tr("kat.spesial") },
    { id:"hemmeleg", namn:tr("kat.hemmelege") },
  ];
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100003, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"24px 22px calc(env(safe-area-inset-bottom) + 24px)", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexShrink:0 }}>
          <div style={{ fontSize:17, fontWeight:"800", color:t.tekst }}>{tr("hb.trofeListeTittel", { namn: visingsnamn })}</div>
          <button onClick={onClose} aria-label="Lukk" style={{ width:34, height:34, borderRadius:17, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:17, cursor:"pointer", flexShrink:0 }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {kategoriar.map(kat => {
            const alle = kat.id === "hemmeleg" ? BADGES.filter(b => b.hemmeleg)
              : kat.id === "lister" ? genererListeBadges(alleTopper || [])
              : BADGES.filter(b => b.kategori === kat.id);
            // Hemmelege badges dei ikkje har: ikkje vis i det heile (ikkje spoile).
            const katBadges = kat.id === "hemmeleg" ? alle.filter(b => låsteBadges.has(b.id)) : alle;
            if (!katBadges.length) return null;
            return (
              <div key={kat.id} style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, fontWeight:"600", color:t.tekstDim, letterSpacing:1.5, textTransform:"uppercase", marginBottom:8 }}>{kat.namn}</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {katBadges.map(badge => {
                    const låst = låsteBadges.has(badge.id);
                    return (
                      <div key={badge.id} style={{ background: låst ? t.bgCard : "transparent", border:`1.5px solid ${låst ? t.aksentBord : t.border}`, borderRadius:12, padding:"12px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, position:"relative" }}>
                        {!låst && (<div style={{ position:"absolute", top:6, right:8, fontSize:10, opacity:0.4 }}>🔒</div>)}
                        <div style={{ fontSize:28, filter: låst ? "none" : "grayscale(1)", opacity: låst ? 1 : 0.5 }}>{badge.ikon}</div>
                        <div style={{ fontSize:11, fontWeight:"700", color: låst ? t.tekst : t.tekstMut, textAlign:"center" }}>{badgeNamn(badge)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── KJEDE-TOPPLISTE ────────────────────────────────────────────────────────
// Full topplista over kjede-lengder (kvar person sin beste enkeltdag),
// same visuelle mønster som Leiartavle-modalen i ToppProfil.
function KjedeTopplisteModal({ liste, darkMode, aksent, onOpneBrukar, onClose }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100003, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"24px 22px calc(env(safe-area-inset-bottom) + 24px)", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexShrink:0 }}>
          <div style={{ fontSize:17, fontWeight:"800", color:t.tekst }}>🔗 {tr("kjede.topplisteTittel")}</div>
          <button onClick={onClose} aria-label="Lukk" style={{ width:34, height:34, borderRadius:17, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:17, cursor:"pointer", flexShrink:0 }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {liste === null ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("hb.leiartavleLastar")}</div>
          ) : liste.length === 0 ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("kjede.ingenEnno")}</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:2, paddingBottom:8 }}>
              {liste.map((r, i) => (
                <div key={r.bruker_id || i} onClick={() => r.bruker_id && onOpneBrukar?.(r.bruker_id)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 2px", cursor: r.bruker_id ? "pointer" : "default" }}>
                  <span style={{ fontSize:12, color:t.tekstDim, fontWeight:"700", width:18, textAlign:"right", flexShrink:0 }}>{i+1}</span>
                  <div style={{ width:30, height:30, borderRadius:15, overflow:"hidden", flexShrink:0, background: darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.05)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>
                    {r.profil_bilde ? <img src={r.profil_bilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                  </div>
                  <span style={{ flex:1, minWidth:0, fontSize:14, fontWeight:"600", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.visingsnamn || "—"}</span>
                  <span style={{ fontSize:11, color:t.tekstDim, flexShrink:0 }}>{fmtDatoBerre(r.dato)}</span>
                  <span style={{ fontSize:13, color:t.tekstMut, fontWeight:"700", fontFamily:"monospace", flexShrink:0 }}>({r.lengde})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── BADGE-EIGARAR ("kven andre har denne") ────────────────────────────────
// ─── HENDINGVARSEL (hovudbilete teke frå deg) ──────────────────────────────
// Eit meir gjennomarbeidd varslingskort enn den vesle grøne meldingBanner-
// toasten (som held fram uendra for raske "✓ +10p"-stadfestingar). Denne
// nyttar ein sirkulær emoji-merkelapp, tydeleg tittel, og eit eige
// call-to-action-piller — reservert for sjeldne, litt tyngre hendingar.
function HendingVarselKort({ emoji, namn, toppNamn, darkMode, aksent, onKlikk, onLukk }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  return (
    <div
      onClick={onKlikk}
      style={{
        position:"fixed", top:"calc(env(safe-area-inset-top) + 16px)", left:16, right:16,
        zIndex:100000, cursor:"pointer",
        background: darkMode ? "#1C1C1E" : "#FFFFFF",
        borderRadius:20, padding:"16px 18px",
        boxShadow:"0 12px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.15)",
        border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`,
        display:"flex", alignItems:"center", gap:14,
        animation:"toastPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both",
      }}>
      <div style={{
          width:48, height:48, borderRadius:24, flexShrink:0,
          background: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:26,
        }}>{emoji}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:"700", color:t.tekst, lineHeight:1.3 }}>
          {tr("hending.hovudbildeTekeTittel", { namn })}
        </div>
        <div style={{ fontSize:12, color:t.tekstMut, marginTop:2 }}>
          {tr("hending.hovudbildeTekeSub", { topp: toppNamn })}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onLukk(); }}
        aria-label="Lukk"
        style={{ width:26, height:26, borderRadius:13, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:13, cursor:"pointer", flexShrink:0 }}>✕</button>
    </div>
  );
}

function BadgeEigararModal({ badge, liste, darkMode, aksent, onOpneBrukar, onClose }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100003, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"24px 22px calc(env(safe-area-inset-bottom) + 24px)", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4, flexShrink:0 }}>
          <div style={{ fontSize:17, fontWeight:"800", color:t.tekst }}>{badge?.ikon} {badge ? badgeNamn(badge) : "—"}</div>
          <button onClick={onClose} aria-label="Lukk" style={{ width:34, height:34, borderRadius:17, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:17, cursor:"pointer", flexShrink:0 }}>✕</button>
        </div>
        <div style={{ fontSize:12, color:t.tekstDim, marginBottom:16 }}>
          {liste === null ? tr("hb.leiartavleLastar") : tr("badge.talEigarar", { n: liste.length })}
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {liste === null ? null : liste.length === 0 ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("badge.ingenEigarar")}</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:2, paddingBottom:8 }}>
              {liste.map((r, i) => (
                <div key={r.bruker_id || i} onClick={() => r.bruker_id && onOpneBrukar?.(r.bruker_id)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 2px", cursor: r.bruker_id ? "pointer" : "default" }}>
                  <div style={{ width:30, height:30, borderRadius:15, overflow:"hidden", flexShrink:0, background: darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.05)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>
                    {r.profil_bilde ? <img src={r.profil_bilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                  </div>
                  <span style={{ flex:1, minWidth:0, fontSize:14, fontWeight:"600", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.visingsnamn || "—"}</span>
                  <span style={{ fontSize:11, color:t.tekstDim, flexShrink:0 }}>{fmtTurDato(r.fyrste_gong)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MINE HOVUDBILETE (gjeldande eigarskap) ────────────────────────────────
// Liste over toppar der er_hovudbilde er TRUE for deg akkurat no — kan gå
// ned igjen om nokon utfordrar deg, i motsetnad til det historiske
// «har vore hovudbilete»-talet. Reint lokal utrekning, ingen RPC.
function MineHovudbileteModal({ liste, darkMode, aksent, onOpneTopp, onClose }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100003, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"24px 22px calc(env(safe-area-inset-bottom) + 24px)", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexShrink:0 }}>
          <div style={{ fontSize:17, fontWeight:"800", color:t.tekst }}>🖼️ {tr("hb.mineHovudbileteTittel")}</div>
          <button onClick={onClose} aria-label="Lukk" style={{ width:34, height:34, borderRadius:17, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:17, cursor:"pointer", flexShrink:0 }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {liste.length === 0 ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("hb.mineHovudbileteTom")}</div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {liste.map(b => (
                <div key={b.id} onClick={() => onOpneTopp?.(b.toppId)}
                  style={{ borderRadius:14, overflow:"hidden", cursor:"pointer", background: darkMode?"#1C1C1E":"#F2F2F7", position:"relative" }}>
                  {b.bilde && <img src={b.bilde} alt="" style={{ width:"100%", height:120, objectFit:"cover", display:"block" }}/>}
                  <div style={{ padding:"8px 10px" }}>
                    <div style={{ fontSize:12, fontWeight:"700", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.namn}</div>
                    {b.høgde > 0 && <div style={{ fontSize:10, color:t.tekstDim, marginTop:1 }}>↑ {b.høgde} moh</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── FANFARE MODAL ───────────────────────────────────────────────────────────
function FanfareModal({ liste, poeng, bonus, onLukk, aksent }) {
  const [synleg, setSynleg] = useState(false);
  const [partiklar, setPartiklar] = useState([]);

  useEffect(() => {
    setSynleg(true);
    spelaLyd("fanfare");
    const p = Array.from({ length: 60 }, (_, i) => ({
      id: i, x: Math.random() * 100, delay: Math.random() * 1.5,
      dur: 1.5 + Math.random() * 2, size: 6 + Math.random() * 8,
      farge: ["#fbbf24","#34d399","#60a5fa","#a78bfa","#fb923c","#f472b6","#fff"][Math.floor(Math.random()*7)],
      drift: (Math.random() - 0.5) * 200, rotasjon: Math.random() * 720,
      form: Math.random() > 0.5 ? "50%" : "0%",
    }));
    setPartiklar(p);
    const t = setTimeout(onLukk, 6000);
    return () => clearTimeout(t);
  }, [onLukk]);

  return (
    <div style={{
      position: "fixed", top:0, left:0, right:0, bottom:0, zIndex: 99999,
      background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center",
      opacity: synleg ? 1 : 0, transition: "opacity 0.4s", WebkitTransform: "translateZ(0)",
    }} onClick={onLukk}>
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {partiklar.map(p => (
          <div key={p.id} style={{
            position: "absolute", left: p.x + "%", top: "-20px",
            width: p.size, height: p.size, borderRadius: p.form, background: p.farge,
            animation: `fall${p.id % 4} ${p.dur}s ${p.delay}s ease-in forwards`,
          }}/>
        ))}
      </div>
      <div style={{
        background: "linear-gradient(135deg, #0a1628 0%, #1a2a0f 100%)",
        border: "2px solid " + (liste?.farge || "#fbbf24"),
        borderRadius: 24, padding: "32px 28px", maxWidth: 320, width: "90%",
        textAlign: "center", position: "relative",
        boxShadow: "0 0 60px " + (liste?.farge || "#fbbf24") + "44",
        animation: "popIn 0.5s cubic-bezier(0.34,1.56,0.64,1)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 56, marginBottom: 8, animation: "bounce 0.6s ease infinite alternate" }}>🏆</div>
        <div style={{ fontSize: 13, color: liste?.farge || "#fbbf24", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8, fontFamily: "monospace" }}>{tr("fanfare.fullfort")}</div>
        <div style={{ fontSize: 28, fontWeight: "bold", color: "#e8f0e8", marginBottom: 4, fontFamily: "Georgia, serif" }}>{liste?.label}</div>
        <div style={{ fontSize: 13, color: "#7a9a7a", marginBottom: 20 }}>{tr("fanfare.alleToppar")}</div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 24 }}>
          <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "10px 16px" }}>
            <div style={{ fontSize: 11, color: "#5a8a5a", marginBottom: 2 }}>{tr("fanfare.toppPoeng")}</div>
            <div style={{ fontSize: 20, fontWeight: "bold", color: "#a8d8a8", fontFamily: "monospace" }}>+{poeng}p</div>
          </div>
          <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 12, padding: "10px 16px" }}>
            <div style={{ fontSize: 11, color: "#8a7a3a", marginBottom: 2 }}>{tr("fanfare.bonus")}</div>
            <div style={{ fontSize: 20, fontWeight: "bold", color: "#fbbf24", fontFamily: "monospace" }}>+{bonus}p</div>
          </div>
        </div>
        <button onClick={onLukk} style={{ background: liste?.farge || "#fbbf24", color: "#0a1628", border: "none", borderRadius: 10, padding: "12px 32px", fontSize: 15, fontWeight: "bold", cursor: "pointer", fontFamily: "Georgia, serif", width: "100%" }}>
          Fantastisk! 🎊
        </button>
      </div>
      <style>{`
        @keyframes popIn { from { transform: scale(0.3) rotate(-10deg); opacity:0; } to { transform: scale(1) rotate(0deg); opacity:1; } }
        @keyframes bounce { from { transform: translateY(0); } to { transform: translateY(-12px); } }
        @keyframes fall0 { to { transform: translateY(120vh) translateX(var(--dx, 80px)) rotate(720deg); opacity:0; } }
        @keyframes fall1 { to { transform: translateY(120vh) translateX(var(--dx, -60px)) rotate(-540deg); opacity:0; } }
        @keyframes fall2 { to { transform: translateY(120vh) translateX(var(--dx, 100px)) rotate(900deg); opacity:0; } }
        @keyframes fall3 { to { transform: translateY(120vh) translateX(var(--dx, -120px)) rotate(-360deg); opacity:0; } }
      `}</style>
    </div>
  );
}

// ─── PROGRESS PANEL ──────────────────────────────────────────────────────────
function ProgressPanel({ besøktIds, aktivListe, onVelListe, darkMode, tema, alleTopper }) {
  const t = tema || TEMA.dark;
  const topper = alleTopper || ALLE_TOPPER;
  return (
    <div style={{...pp.wrap, background: t.bgCard, borderBottom: `1px solid ${t.border}`, overflow:"hidden"}}>
      <div style={pp.knapper}>
        {LISTER.map(l => {
          const lTopper = topper.filter(t => t.lister.includes(l.id));
          const besøkt = lTopper.filter(t => besøktIds.has(t.id)).length;
          const pst = Math.round((besøkt / Math.max(1, lTopper.length)) * 100);
          const aktiv = aktivListe === l.id;
          return (
            <button key={l.id} style={{ ...pp.listeBtn, borderColor: aktiv ? l.farge : "rgba(255,255,255,0.08)", background: aktiv ? l.farge + "18" : "rgba(255,255,255,0.03)" }}
              onClick={() => onVelListe(l.id)}>
              <div style={{ ...pp.listeLbl, color: aktiv ? l.farge : "#5a7a6a" }}>{l.label}</div>
              <div style={{ ...pp.listeStats, color: aktiv ? l.farge : "#3a5a4a" }}>
                {besøkt}<span style={{ opacity: 0.5 }}>/{lTopper.length}</span>
              </div>
              <div style={pp.miniBarBg}>
                <div style={{ ...pp.miniBarFill, width: `${pst}%`, background: l.farge }} />
              </div>
            </button>
          );
        })}
      </div>
      {(() => {
        const l = LISTER.find(x => x.id === aktivListe) || LISTER[0];
        const lTopper = topper.filter(t => t.lister.includes(l.id));
        const besøkt = lTopper.filter(t => besøktIds.has(t.id)).length;
        const pst = Math.round((besøkt / Math.max(1, lTopper.length)) * 100);
        return (
          <div style={pp.storBar}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: l.farge, fontWeight: "bold" }}>{l.label}</span>
              <span style={{ fontSize: 11, color: "#5a8a6a", fontFamily: "monospace" }}>{besøkt} / {lTopper.length} · {pst}%</span>
            </div>
            <div style={pp.storBarBg}>
              <div style={{ ...pp.storBarFill, width: `${pst}%`, background: `linear-gradient(90deg, ${l.farge}99, ${l.farge})` }} />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const pp = {
  wrap: { padding: "10px 16px 0", flexShrink: 0 },
  knapper: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: 10 },
  listeBtn: { border: "1px solid", borderRadius: 12, padding: "8px 6px", cursor: "pointer", background: "transparent", textAlign: "center", transition: "all 0.2s" },
  listeLbl: { fontSize: 10, fontWeight: "600", letterSpacing: 0.5, marginBottom: 3 },
  listeStats: { fontSize: 17, fontWeight: "700", marginBottom: 5, lineHeight: 1 },
  miniBarBg: { height: 3, background: "rgba(128,128,128,0.15)", borderRadius: 2, overflow: "hidden" },
  miniBarFill: { height: "100%", borderRadius: 2, transition: "width 0.4s" },
  storBar: { marginBottom: 8 },
  storBarBg: { height: 4, background: "rgba(128,128,128,0.15)", borderRadius: 3, overflow: "hidden" },
  storBarFill: { height: "100%", borderRadius: 3, transition: "width 0.5s" },
};

// ─── FILTER PANEL ────────────────────────────────────────────────────────────
// Normaliserer fylke/kommune-tekst for samanlikning (trim + små bokstavar) —
// "Sunnfjord", "sunnfjord " osv. vert då rekna som same kommune, sjølv om dei
// er skrivne litt ulikt (t.d. manuelt i appen sin admin-modus vs. Kjeldefil-fana).
const normTekst = (s) => (s || "").trim().toLowerCase();
function unikeMedVising(alleTopper, felt, føresetnad) {
  const map = new Map(); // normalisert → visingstekst (fyrste sett vinn)
  alleTopper.forEach(p => {
    if (føresetnad && !føresetnad(p)) return;
    const rå = p[felt];
    if (!rå) return;
    const n = normTekst(rå);
    if (!map.has(n)) map.set(n, rå.trim());
  });
  return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "no"));
}
function FilterPanel({ filter, onFilterEndra, alleTopper, besøktIds, darkMode, aksent, onLukk }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const fylkeListe = unikeMedVising(alleTopper, "fylke");
  const kommuneListe = filter.fylke === "Alle"
    ? unikeMedVising(alleTopper, "kommune")
    : unikeMedVising(alleTopper, "kommune", p => normTekst(p.fylke) === filter.fylke);

  const knappStil = (aktiv, farge) => ({
    padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: "700",
    cursor: "pointer", border: `1px solid ${aktiv ? farge : t.border}`,
    background: aktiv ? farge + "22" : "transparent",
    color: aktiv ? farge : t.tekstMut, transition: "all 0.15s",
  });

  return (
    <>
      <div onClick={onLukk} style={{ position:"fixed", inset:0, zIndex:1998, background:"rgba(0,0,0,0.4)" }}/>
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, zIndex:1999,
        background: darkMode ? "rgba(28,28,30,0.98)" : "rgba(255,255,255,0.98)",
        borderRadius:"20px 20px 0 0", padding:"12px 18px 40px",
        boxShadow:"0 -8px 32px rgba(0,0,0,0.3)",
        fontFamily:"-apple-system,'SF Pro Display',sans-serif",
        animation:"slideOpp 0.25s cubic-bezier(0.34,1.2,0.64,1)",
      }}>
        <style>{`@keyframes slideOpp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
        <div style={{ width:36, height:4, borderRadius:2, background: darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"0 auto 16px" }}/>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div style={{ fontSize:15, fontWeight:"700", color:t.tekst }}>{tr("filter.tittel")}</div>
          <button onClick={() => onFilterEndra({ ...filter, fylke:"Alle", kommune:"Alle", visast:"alle", liste:"Alle", minHogde:0 })}
            style={{ fontSize:12, color:aksent.aksent, background:"none", border:"none", cursor:"pointer", fontWeight:"600" }}>{tr("filter.nullstill")}</button>
        </div>
        {fylkeListe.length > 1 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:8, fontWeight:"600" }}>{tr("filter.fylke")}</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <button onClick={() => onFilterEndra({ ...filter, fylke:"Alle", kommune:"Alle" })} style={knappStil(filter.fylke==="Alle", aksent.aksent)}>Alle</button>
              {fylkeListe.map(([norm, vising]) => (
                <button key={norm} onClick={() => onFilterEndra({ ...filter, fylke:norm, kommune:"Alle" })} style={knappStil(filter.fylke===norm, aksent.aksent)}>{vising}</button>
              ))}
            </div>
          </div>
        )}
        {kommuneListe.length > 1 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:8, fontWeight:"600" }}>{tr("filter.kommune")}</div>
            <div style={{ position:"relative" }}>
              <select
                value={filter.kommune}
                onChange={e => onFilterEndra({ ...filter, kommune: e.target.value })}
                style={{
                  width:"100%", padding:"10px 36px 10px 14px", borderRadius:12, fontSize:14, fontWeight:"600",
                  border:`1px solid ${filter.kommune!=="Alle" ? aksent.aksent : t.border}`,
                  background: filter.kommune!=="Alle" ? aksent.aksent + "22" : (darkMode?"#1C1C1E":"#FFFFFF"),
                  color: filter.kommune!=="Alle" ? aksent.aksent : t.tekst,
                  fontFamily:"inherit", appearance:"none", WebkitAppearance:"none",
                }}>
                <option value="Alle">Alle kommunar</option>
                {kommuneListe.map(([norm, vising]) => (
                  <option key={norm} value={norm}>{vising}</option>
                ))}
              </select>
              <div style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", color:t.tekstMut, fontSize:11 }}>▼</div>
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:8, fontWeight:"600" }}>{tr("filter.vis")}</div>
          <div style={{ display:"flex", gap:6 }}>
            {[["alle",tr("filter.alleToppar")],["ubesøkte",tr("filter.berreUbesokte")],["besøkte",tr("filter.berreBesokte")]].map(([val, label]) => (
              <button key={val} onClick={() => onFilterEndra({ ...filter, visast:val })} style={knappStil(filter.visast===val, aksent.aksent)}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:8, fontWeight:"600" }}>{tr("filter.minHogde")}</div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[[0,"Alle"],[500,"500+"],[1000,"1000+"],[1500,"1500+"],[2000,"2000+"]].map(([val, label]) => (
              <button key={val} onClick={() => onFilterEndra({ ...filter, minHogde:val })} style={knappStil((filter.minHogde||0)===val, aksent.aksent)}>{label}</button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── FULLSKJERM PANEL INNHALD ─────────────────────────────────────────────────
function FullskjermPanelInnhald({ aktiv, besøk, besøktIds, aktivListe, aktivTopper,
  posisjon, søk, setSøk, filtrerte, flyTilRef, setValtTopp, setVisFullskjermPanel,
  testModus, darkMode, aksent, setBesøk, totalPoeng, adminModus, setAdminModus,
  fullskjerm, setFullskjerm, kompakt, setKompakt, visKartVær, setVisKartVær,
  setDarkMode, farge, setFarge, setTestModus, setFanfare, s }) {

  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const listeFarge = LISTER.find(l => l.id === aktivListe)?.farge || "#a8d8a8";

  if (aktiv === "liste") return (
    <div style={{ padding:"12px 16px" }}>
      <input style={s.søkInput} placeholder={`Søk i ${LISTER.find(l=>l.id===aktivListe)?.label}…`}
        value={søk} onChange={e => setSøk(e.target.value)} />
      <div style={s.toppListe}>
        {filtrerte.map((topp) => {
          const besøkt = besøktIds.has(topp.id);
          const farge2 = aksent.aksent;
          return (
            <div key={topp.id}
              style={{ ...s.toppKort, ...(besøkt ? s.toppKortBesøkt : {}), borderLeftColor: besøkt ? "#30D158" : farge2 }}
              onClick={() => { setValtTopp(topp); setVisFullskjermPanel(false); setTimeout(() => flyTilRef.current?.(topp.lat, topp.lng, 14), 150); }}>
              <div style={s.toppVenstre}>
                <div style={{ color: besøkt ? "#22cc66" : "#333", fontSize:16, width:20, textAlign:"center" }}>{besøkt ? "✓" : "○"}</div>
                <div>
                  <div style={s.toppNamn}>
                    {topp.namn}
                    {(() => { const cnt = besøk.filter(b => String(b.toppId) === String(topp.id)).length; return cnt > 0 ? <span style={{ fontSize:10, color:"#fbbf24", fontFamily:"monospace", marginLeft:6, opacity:0.8 }}>×{cnt}</span> : null; })()}
                  </div>
                  <div style={s.toppMeta}><span style={s.høgdeBadge}>{topp.høgde} m</span></div>
                </div>
              </div>
              <div style={s.avstandCol}>
                {topp.avstand !== null
                  ? topp.avstand < 1000
                    ? <span style={{ color: topp.avstand<=50 ? "#22cc66" : "#4a6a5a" }}>{topp.avstand} m</span>
                    : <span style={{ color:"#3a5a4a" }}>{(topp.avstand/1000).toFixed(1)} km</span>
                  : <span style={{ color:"#2a3a2a", fontSize:10 }}>—</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (aktiv === "logg") return (
    <div style={{ padding:"12px 16px" }}>
      {besøk.length > 0 && (
        <div style={{ display:"flex", gap:10, marginBottom:12, flexWrap:"wrap" }}>
          <div style={{ flex:1, background:"rgba(168,216,168,0.07)", border:"1px solid rgba(168,216,168,0.15)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:22, fontWeight:"bold", color:"#a8d8a8", fontFamily:"monospace" }}>{besøktIds.size}</div>
            <div style={{ fontSize:10, color:"#5a8a5a", textTransform:"uppercase", letterSpacing:1 }}>{tr("logg.unikeToppar")}</div>
          </div>
          <div style={{ flex:1, background:"rgba(96,165,250,0.07)", border:"1px solid rgba(96,165,250,0.15)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:22, fontWeight:"bold", color:"#60a5fa", fontFamily:"monospace" }}>{besøk.length}</div>
            <div style={{ fontSize:10, color:"#3a5a8a", textTransform:"uppercase", letterSpacing:1 }}>{tr("logg.totaleTurar")}</div>
          </div>
          <div style={{ flex:1, background:"rgba(251,191,36,0.07)", border:"1px solid rgba(251,191,36,0.15)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:22, fontWeight:"bold", color:"#fbbf24", fontFamily:"monospace" }}>{totalPoeng}</div>
            <div style={{ fontSize:10, color:"#7a6a2a", textTransform:"uppercase", letterSpacing:1 }}>{tr("logg.poeng")}</div>
          </div>
        </div>
      )}
      {besøk.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"#5a7a5a" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🏔️</div>
          <div>{tr("logg.ingenBesok")}</div>
        </div>
      ) : (
        <div style={s.toppListe}>
          {besøk.map(b => (
            <div key={b.id} style={s.loggKort}>
              <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                <div style={{ fontSize:22 }}>🏔</div>
                <div>
                  <div style={{ fontSize:15, color:"#a8d8a8", fontWeight:"bold" }}>{b.namn}</div>
                  <div style={{ fontSize:12, color:"#6a9a6a", marginTop:2 }}>
                    {b.høgde} m · {new Date(b.dato).toLocaleDateString("nb-NO", { day:"2-digit", month:"short", year:"numeric" })}
                    {b.poeng && <span style={{ color:"#fbbf24", marginLeft:8, fontFamily:"monospace" }}>+{b.poeng}p</span>}
                  </div>
                </div>
              </div>
              {testModus && (<button style={s.slettBtn} onClick={() => setBesøk(prev => prev.filter(x => x.id !== b.id))}>✕</button>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (aktiv === "badge") {
    const { låst: låsteBadges, årlege: årlegeBadges } = sjekkBadges(besøk, besøktIds, ALLE_TOPPER);
    return (
      <div style={{ padding:"12px 16px" }}>
        <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, padding:"14px 16px", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:15, fontWeight:"700", color:t.tekst }}>{tr("trofe.tittel")}</div>
          <div style={{ fontSize:24, fontWeight:"700", color:aksent.aksent }}>{låsteBadges.size}<span style={{ fontSize:13, color:t.tekstDim }}>/{BADGES.length}</span></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {BADGES.filter(b => !b.hemmeleg || låsteBadges.has(b.id)).map(badge => {
            const låst = låsteBadges.has(badge.id);
            return (
              <div key={badge.id} style={{ background: låst ? (darkMode?"#2C2C2E":"#FFFFFF") : "transparent", border:`1.5px solid ${låst ? aksent.aksentBord : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, borderRadius:12, padding:"12px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ fontSize:28, filter: låst ? "none" : "grayscale(1)", opacity: låst ? 1 : 0.4 }}>{badge.ikon}</div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:12, fontWeight:"700", color: låst ? t.tekst : t.tekstMut }}>{badgeNamn(badge)}</div>
                  <div style={{ fontSize:10, color:t.tekstDim, marginTop:2, lineHeight:1.3 }}>{badgeSkildring(badge)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (aktiv === "innst") return (
    <div style={{ padding:"12px 16px" }}>
      <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:12, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
          <div>
            <div style={{ fontSize:15, fontWeight:"600", color:t.tekst }}>{tr("innst.fullskjerm")}</div>
            <div style={{ fontSize:12, color:t.tekstMut, marginTop:2 }}>{tr("innst.fullskjermAv")}</div>
          </div>
          <div onClick={() => { setFullskjerm(false); setVisFullskjermPanel(false); }} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: aksent.aksent, position:"relative" }}>
            <div style={{ position:"absolute", top:2, left:22, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
          </div>
        </div>
      </div>
      <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:12, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
          <div style={{ fontSize:15, fontWeight:"600", color:t.tekst }}>{tr("innst.morktTema")}</div>
          <div onClick={() => setDarkMode(d => !d)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: darkMode ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
            <div style={{ position:"absolute", top:2, left: darkMode ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
          </div>
        </div>
      </div>
      <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:12, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
          <div style={{ fontSize:15, fontWeight:"600", color:t.tekst }}>{tr("innst.testmodus")}</div>
          <div onClick={() => setTestModus(t2 => !t2)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: testModus ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
            <div style={{ position:"absolute", top:2, left: testModus ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
          </div>
        </div>
      </div>
    </div>
  );

  return null;
}

// ─── ADMIN TOPP-PANEL ────────────────────────────────────────────────────────
function AdminToppPanel({ topp, darkMode, aksent, flytAktiv, onFlytt, onSlett, onLagre, onLukk }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const [namn, setNamn] = useState(topp.namn);
  // Tekst, ikkje tal — så feltet kan vere reelt tomt medan ein skriv, i staden
  // for å hoppe attende til "0" (parseInt("")||0) og bli ståande som leiande siffer.
  const [høgdeTxt, setHøgdeTxt] = useState(String(topp.høgde ?? ""));
  const [fylke, setFylke] = useState(topp.fylke || "");
  const [kommune, setKommune] = useState(topp.kommune || "");
  const [lister, setLister] = useState(new Set(topp.lister || []));

  // topp.lat/lng endrar seg berre når admin faktisk har flytta punktet (sjå
  // handlePunktKlikk), og då har open-meteo-oppslaget alt lagt inn ny høgde i
  // topp.høgde. Utan denne effekten stod feltet igjen med den GAMLE høgda.
  useEffect(() => { setHøgdeTxt(String(topp.høgde ?? "")); }, [topp.lat, topp.lng]);

  const togListe = (n) => {
    const ny = new Set(lister);
    if (ny.has(n)) ny.delete(n); else ny.add(n);
    setLister(ny);
  };

  return (
    <div style={{ position:"absolute", bottom:0, left:0, right:0, zIndex:1001,
      background:darkMode?"rgba(28,28,30,0.97)":"rgba(255,255,255,0.97)",
      borderRadius:"20px 20px 0 0", padding:"12px 18px calc(70px + env(safe-area-inset-bottom))",
      boxShadow:"0 -8px 32px rgba(0,0,0,0.3)",
      fontFamily:"-apple-system,'SF Pro Display',sans-serif" }}>
      <div style={{ width:36, height:4, borderRadius:2, background:darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"0 auto 14px" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:"700", color:"#fb923c" }}>{tr("admin.rediger")}</div>
        <button onClick={onLukk} style={{ background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", border:"none", borderRadius:8, color:t.tekstMut, width:30, height:30, cursor:"pointer", fontSize:14 }}>✕</button>
      </div>
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.namn")}</div>
        <input value={namn} onChange={e => setNamn(e.target.value)}
          style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.hogde")}</div>
          {flytAktiv ? (
            <input type="text" disabled value="" placeholder="Vel nytt punkt på kartet…"
              style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekstMut, fontSize:13, fontFamily:"inherit", opacity:0.6 }}/>
          ) : (
            <input type="number" value={høgdeTxt} onChange={e => setHøgdeTxt(e.target.value)}
              style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
          )}
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.fylke")}</div>
          <input value={fylke} onChange={e => setFylke(e.target.value)} placeholder="t.d. Vestland"
            style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.kommune")}</div>
          <input value={kommune} onChange={e => setKommune(e.target.value)} placeholder="t.d. Sunnfjord"
            style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
        </div>
      </div>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{tr("admin.lister")}</div>
        <div style={{ display:"flex", gap:8 }}>
          {[10,20,50,100].map(n => (
            <button key={n} onClick={() => togListe(n)} style={{
              padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"700", cursor:"pointer",
              background: lister.has(n) ? `${["#fbbf24","#34d399","#60a5fa","#a78bfa"][([10,20,50,100].indexOf(n))]}22` : "transparent",
              border: `1px solid ${["#fbbf24","#34d399","#60a5fa","#a78bfa"][[10,20,50,100].indexOf(n)]}`,
              color: ["#fbbf24","#34d399","#60a5fa","#a78bfa"][[10,20,50,100].indexOf(n)],
              opacity: lister.has(n) ? 1 : 0.3,
            }}>T{n}</button>
          ))}
        </div>
      </div>
      <div style={{ fontSize:12, color:t.tekstMut, fontFamily:"monospace", marginBottom:14 }}>
        📍 {topp.lat?.toFixed(5)}°N, {topp.lng?.toFixed(5)}°E
        {flytAktiv && <span style={{ color:"#fb923c", marginLeft:8, fontWeight:"700" }}>{tr("admin.trykkFlytt")}</span>}
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        <button onClick={() => onFlytt(!flytAktiv)}
          style={{ padding:"12px 14px", borderRadius:12, cursor:"pointer", fontWeight:"700", fontSize:13,
            border: flytAktiv ? "none" : "1px solid rgba(251,146,60,0.4)",
            background: flytAktiv ? "#fb923c" : "rgba(251,146,60,0.08)",
            color: flytAktiv ? "white" : "#fb923c" }}>
          📍 {tr("admin.flyttKnapp")}
        </button>
        <button onClick={() => onLagre({ ...topp, namn, høgde: parseInt(høgdeTxt)||0, fylke: fylke.trim() || undefined, kommune: kommune.trim() || undefined, lister: [...lister].sort((a,b)=>a-b) })}
          style={{ flex:1, padding:"12px", borderRadius:12, border:"none", cursor:"pointer", fontWeight:"700", fontSize:13, background:"#fb923c", color:"white" }}>
          💾 Lagre
        </button>
        <button onClick={onSlett}
          style={{ padding:"12px 16px", borderRadius:12, border:"1px solid rgba(255,59,48,0.3)", cursor:"pointer", fontWeight:"700", fontSize:13, background:"rgba(255,59,48,0.08)", color:"#FF3B30" }}>
          🗑
        </button>
      </div>
    </div>
  );
}

// ─── LEGG TIL NY TOPP (modal) ────────────────────────────────────────────────
// Same felt-sett som AdminToppPanel (namn/høgde/fylke/kommune samla), i staden
// for sekvensielle window.prompt()-kall — namn og fylke/kommune fylt ut saman.
// Næraste topp med kjend posisjon — brukt til å foreslå fylke/kommune for ein
// heilt ny topp når det ikkje finst noko aktivt filter å hente det frå.
function finnNæraste(lat, lng, alleTopper, maxKm = 30) {
  let best = null, bestAvstandKm = Infinity;
  for (const t of alleTopper) {
    if (t.lat == null || t.lng == null) continue;
    const avstandKm = haversine(lat, lng, t.lat, t.lng) / 1000;
    if (avstandKm < bestAvstandKm) { bestAvstandKm = avstandKm; best = t; }
  }
  return bestAvstandKm <= maxKm ? best : null;
}

function LeggTilToppModal({ punkt, alleTopper, darkMode, aksent, filter, onLagre, onAvbryt }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const [namn, setNamn] = useState("");
  const [høgdeTxt, setHøgdeTxt] = useState(String(punkt.høgde ?? ""));
  const forslag = useMemo(() => {
    // 1) Aktivt kommune-filter — sterkast signal, brukaren har alt sagt kvar dei er
    if (filter?.kommune && filter.kommune !== "Alle") {
      const treff = alleTopper.find(t => normTekst(t.kommune) === filter.kommune);
      if (treff) return { fylke: treff.fylke?.trim() || "", kommune: treff.kommune?.trim() || "" };
    }
    // 2) Aktivt fylke-filter (kommune ukjend innanfor fylket)
    if (filter?.fylke && filter.fylke !== "Alle") {
      const treff = alleTopper.find(t => normTekst(t.fylke) === filter.fylke);
      if (treff?.fylke) return { fylke: treff.fylke.trim(), kommune: "" };
    }
    // 3) Ingen filter aktivt — gjett ut frå næraste eksisterande topp
    const næraste = finnNæraste(punkt.lat, punkt.lng, alleTopper);
    if (næraste) return { fylke: næraste.fylke?.trim() || "", kommune: næraste.kommune?.trim() || "" };
    return { fylke: "", kommune: "" };
  }, [punkt.lat, punkt.lng, filter?.fylke, filter?.kommune, alleTopper]);
  const [fylke, setFylke] = useState(forslag.fylke);
  const [kommune, setKommune] = useState(forslag.kommune);

  const lagre = () => {
    if (!namn.trim()) { window.alert(tr("admin.manglarNamn")); return; }
    const liknande = finnLiknandeToppNær(namn.trim(), punkt.lat, punkt.lng, alleTopper);
    if (liknande.length) {
      const liste = liknande.map(x => `• ${x.namn} (${Math.round(haversine(punkt.lat, punkt.lng, x.lat, x.lng))} m unna)`).join("\n");
      const haldFram = window.confirm(
        `Fann topp(ar) med liknande namn nær her:\n\n${liste}\n\n` +
        `Er dette faktisk ein NY, annan topp — eller meinte du å rette namnet på ein av desse?\n\n` +
        `Trykk OK for å likevel opprette "${namn.trim()}" som ny topp, Avbryt for å la vere.`
      );
      if (!haldFram) return;
    }
    const nyId = genererNyToppId(namn.trim(), alleTopper, fylke.trim(), kommune.trim());
    onLagre({
      id: nyId, namn: namn.trim(), høgde: parseInt(høgdeTxt)||0, lat: punkt.lat, lng: punkt.lng, lister: [],
      fylke: fylke.trim() || undefined, kommune: kommune.trim() || undefined,
      type: punkt.type === "hytte" ? "hytte" : undefined,
    });
  };

  return (
    <>
      <div onClick={onAvbryt} style={{ position:"fixed", inset:0, zIndex:1998, background:"rgba(0,0,0,0.4)" }}/>
      <div style={{ position:"absolute", bottom:0, left:0, right:0, zIndex:1999,
        background:darkMode?"rgba(28,28,30,0.97)":"rgba(255,255,255,0.97)",
        borderRadius:"20px 20px 0 0", padding:"12px 18px calc(70px + env(safe-area-inset-bottom))",
        boxShadow:"0 -8px 32px rgba(0,0,0,0.3)",
        fontFamily:"-apple-system,'SF Pro Display',sans-serif" }}>
        <div style={{ width:36, height:4, borderRadius:2, background:darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"0 auto 14px" }}/>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:"700", color:"#fb923c" }}>{punkt.type==="hytte" ? "🏠 Legg til ny hytte" : `➕ ${tr("admin.leggTilTittel")}`}</div>
          <button onClick={onAvbryt} style={{ background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", border:"none", borderRadius:8, color:t.tekstMut, width:30, height:30, cursor:"pointer", fontSize:14 }}>✕</button>
        </div>
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.namn")}</div>
          <input autoFocus value={namn} onChange={e => setNamn(e.target.value)} placeholder="t.d. Rimmafjellet"
            style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
        </div>
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.hogde")}</div>
          <input type="number" value={høgdeTxt} onChange={e => setHøgdeTxt(e.target.value)}
            style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
        </div>
        <div style={{ display:"flex", gap:8, marginBottom:6 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.fylke")}</div>
            <input value={fylke} onChange={e => setFylke(e.target.value)} placeholder="t.d. Vestland"
              style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, color:t.tekstMut, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{tr("admin.kommune")}</div>
            <input value={kommune} onChange={e => setKommune(e.target.value)} placeholder="t.d. Sunnfjord"
              style={{ width:"100%", padding:"8px 12px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1px solid ${t.border}`, borderRadius:10, color:t.tekst, fontSize:15, fontFamily:"inherit" }}/>
          </div>
        </div>
        {(forslag.fylke || forslag.kommune) && (
          <div style={{ fontSize:11, color:"#fb923c", marginBottom:8 }}>
            💡 Fylt ut automatisk — sjekk at det stemmer før du lagrar
          </div>
        )}
        <div style={{ fontSize:12, color:t.tekstMut, fontFamily:"monospace", marginBottom:14 }}>
          📍 {punkt.lat?.toFixed(5)}°N, {punkt.lng?.toFixed(5)}°E
        </div>
        <button onClick={lagre}
          style={{ width:"100%", padding:"13px", borderRadius:12, border:"none", cursor:"pointer", fontWeight:"700", fontSize:14, background:"#fb923c", color:"white" }}>
          ➕ {tr("admin.leggTilKnapp")}
        </button>
      </div>
    </>
  );
}

// ─── SLETT HOVUDBILETE (stadfesting) ────────────────────────────────────────
// Sletting av hovudbilete er permanent (fila vert fjerna frå Storage) og gir
// frå seg eigarskapen til toppen med det same. Eit vanleg window.confirm()
// var for lett å trykkje gjennom ved eit uhell — krev difor at brukaren
// eksplisitt skriv "SLETT" før knappen vert aktiv.
function SlettHovudbileteModal({ besøk, darkMode, aksent, onSlett, onAvbryt, tittel, sporsmal, knappIkon = "🗑", knappTekst, stadfestingsOrd = "SLETT" }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const [tekst, setTekst] = useState("");
  const kanSlette = tekst.trim().toUpperCase() === stadfestingsOrd;
  return (
    <>
      <div onClick={onAvbryt} style={{ position:"fixed", inset:0, zIndex:2998, background:"rgba(0,0,0,0.5)" }}/>
      <div style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:2999, width:"min(320px, 88vw)",
        background:darkMode?"#1C1C1E":"#FFFFFF", borderRadius:18, padding:20,
        boxShadow:"0 12px 40px rgba(0,0,0,0.35)", fontFamily:"-apple-system,'SF Pro Display',sans-serif" }}>
        <div style={{ fontSize:32, textAlign:"center", marginBottom:10 }}>⚠️</div>
        <div style={{ fontSize:15, fontWeight:"700", color:t.tekst, textAlign:"center", marginBottom:8 }}>
          {tittel ?? tr("hb.slettTittel")}
        </div>
        <div style={{ fontSize:13, color:t.tekstMut, textAlign:"center", marginBottom:16, lineHeight:1.4 }}>
          {sporsmal ?? tr("hb.slettSporsmal", { namn: besøk?.namn })}
        </div>
        <input autoFocus value={tekst} onChange={e => setTekst(e.target.value)}
          placeholder={tr("hb.slettSkrivHer", { ord: stadfestingsOrd })}
          style={{ width:"100%", padding:"10px 14px", background:darkMode?"#2C2C2E":"#F2F2F7", border:`1.5px solid ${kanSlette ? "#FF3B30" : t.border}`, borderRadius:12, color:t.tekst, fontSize:15, fontFamily:"inherit", textAlign:"center", marginBottom:14, boxSizing:"border-box" }}/>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onAvbryt}
            style={{ flex:1, padding:"12px", borderRadius:12, border:`1px solid ${t.border}`, background:"transparent", color:t.tekst, fontSize:14, fontWeight:"600", cursor:"pointer" }}>
            {tr("admin.avbryt")}
          </button>
          <button onClick={() => kanSlette && onSlett()} disabled={!kanSlette}
            style={{ flex:1, padding:"12px", borderRadius:12, border:"none", cursor: kanSlette ? "pointer" : "default",
              background: kanSlette ? "#FF3B30" : (darkMode?"#3A3A3C":"#E5E5EA"),
              color: kanSlette ? "white" : t.tekstMut, fontSize:14, fontWeight:"700" }}>
            {knappIkon} {knappTekst ?? tr("logg.slettBilde")}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── ADMIN-MODUS: delt innhald ──────────────────────────────────────────────
// Same knapperad/brytar vert brukt to stader: inni Innstillingar-korta (uendra)
// og i det flytande panelet over kartet (AdminModusFlytandePanel, under).
// Halde som éin komponent slik at me ikkje kan la dei to stadene skilje lag.
function AdminModusInnhald({ darkMode, adminModus, setAdminModus, adminLeggTilModus, setAdminLeggTilModus, adminLeggTilType, setAdminLeggTilType, setAdminFlyttModus, setAdminValtId, adminEndra, setAdminPeaks, setAdminEndra }) {
  return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"}` }}>
        <div>
          <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("admin.modus")}</div>
          <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("admin.modusSkildring")}</div>
        </div>
        <div onClick={() => setAdminModus(a => !a)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: adminModus ? "#fb923c" : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
          <div style={{ position:"absolute", top:2, left: adminModus ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
        </div>
      </div>
      {adminModus && (
        <div style={{ padding:"10px 16px", display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={() => {
              if (adminLeggTilModus && adminLeggTilType === "topp") { setAdminLeggTilModus(false); return; }
              setAdminLeggTilType("topp"); setAdminLeggTilModus(true); setAdminFlyttModus(false); setAdminValtId(null);
            }}
            style={{ padding:"8px 14px", background: (adminLeggTilModus && adminLeggTilType==="topp") ? "#fb923c" : "rgba(251,146,60,0.1)", border:"1px solid rgba(251,146,60,0.3)", borderRadius:10, color: (adminLeggTilModus && adminLeggTilType==="topp") ? "#FFFFFF" : "#fb923c", fontSize:12, cursor:"pointer", fontWeight:"600" }}>
            {(adminLeggTilModus && adminLeggTilType==="topp") ? "➕ Trykk på kartet…" : "➕ Legg til ny topp"}
          </button>
          <button onClick={() => {
              if (adminLeggTilModus && adminLeggTilType === "hytte") { setAdminLeggTilModus(false); return; }
              setAdminLeggTilType("hytte"); setAdminLeggTilModus(true); setAdminFlyttModus(false); setAdminValtId(null);
            }}
            style={{ padding:"8px 14px", background: (adminLeggTilModus && adminLeggTilType==="hytte") ? "#8B5E3C" : "rgba(139,94,60,0.1)", border:"1px solid rgba(139,94,60,0.35)", borderRadius:10, color: (adminLeggTilModus && adminLeggTilType==="hytte") ? "#FFFFFF" : "#8B5E3C", fontSize:12, cursor:"pointer", fontWeight:"600" }}>
            {(adminLeggTilModus && adminLeggTilType==="hytte") ? "🏠 Trykk på kartet…" : "🏠 Legg til hytte"}
          </button>
          {adminEndra && (
            <button onClick={() => {
              const manglar = ALLE_TOPPER.filter(p => p.lat==null || p.lng==null);
              const nokkel = p => `${(p.namn||"").trim().toLowerCase()}|${(p.lat??"").toFixed?.(3)}|${(p.lng??"").toFixed?.(3)}`;
              const teljar = {};
              ALLE_TOPPER.forEach(p => { const k = nokkel(p); teljar[k] = (teljar[k]||0)+1; });
              const duplikat = ALLE_TOPPER.filter(p => teljar[nokkel(p)] > 1);
              const manglarKommune = ALLE_TOPPER.filter(p => !p.fylke || !p.kommune);
              if (manglar.length || duplikat.length || manglarKommune.length > ALLE_TOPPER.length / 2) {
                const meld = [
                  manglar.length ? `${manglar.length} topp(ar) manglar koordinat: ${manglar.map(p=>p.namn).join(", ")}` : null,
                  duplikat.length ? `${duplikat.length} topp(ar) ser ut til å vere duplikat (same namn+posisjon): ${duplikat.map(p=>p.namn).join(", ")}` : null,
                  manglarKommune.length > ALLE_TOPPER.length / 2 ? `${manglarKommune.length} av ${ALLE_TOPPER.length} toppar manglar fylke/kommune — dette ser uvanleg mange ut, sjekk at du ikkje lastar ned ei fil som har mista dei` : null,
                ].filter(Boolean).join("\n\n");
                if (!window.confirm(`⚠️ Fann moglege problem:\n\n${meld}\n\nLaste ned likevel?`)) return;
              }
              const js = `// peaks.js — Tindre master\n// ${ALLE_TOPPER.length} topper · ${new Date().toLocaleDateString("nb-NO")}\n\nexport const VERSJON = "${new Date().toISOString().slice(0,10)}";\n\nexport const LISTER = {\n  10: "Topp 10",\n  20: "Topp 20",\n  50: "Topp 50",\n  100: "Topp 100",\n};\n\nexport const FJELLTOPPER = [\n${ALLE_TOPPER.map(p => `  { id: ${JSON.stringify(String(p.id))}, namn: ${JSON.stringify(p.namn)}, høgde: ${p.høgde||0}, lat: ${p.lat}, lng: ${p.lng}, lister: ${JSON.stringify(p.lister||[])}${p.fylke?`, fylke: ${JSON.stringify(p.fylke)}`:""}${p.kommune?`, kommune: ${JSON.stringify(p.kommune)}`:""}${p.type?`, type: ${JSON.stringify(p.type)}`:""} },`).join("\n")}\n];\n`;
              const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([js],{type:"text/javascript"})); a.download = "peaks.js"; a.click();
            }} style={{ padding:"8px 14px", background:"rgba(251,146,60,0.1)", border:"1px solid rgba(251,146,60,0.3)", borderRadius:10, color:"#fb923c", fontSize:12, cursor:"pointer", fontWeight:"600" }}>{tr("admin.lastNed")}</button>
          )}
          {adminEndra && (
            <button onClick={() => {
              // Same sw.js-mal som adminfjell.html sin byggSwJsTekst() — CACHE-versjonen
              // er einaste variable del, sett til dagens dato. Utan denne (bumpa saman
              // med peaks.js) held Service Workeren fram med å servere gamal, cacha
              // bundel — det er difor «Last ned peaks.js» åleine kan gje treg oppdatering.
              const versjon = new Date().toISOString().slice(0,10);
              const sw = `// Tindre — Service Worker
// Strategi:
//   • Navigasjon (HTML)  → network-first: alltid ferskaste app, fall til cache offline
//   • Statiske ressursar → cache-first: rask lasting, oppdaterer i bakgrunnen
// CACHE-versjonen vert automatisk sett til dagens dato av admin-panelet
// kvar gong du genererer denne fila saman med peaks.js — treng ikkje bumpast for hand.
const CACHE = "tindre-v${versjon}";
const PRECACHE = ["/app", "/", "/manifest.json"];

self.addEventListener("install", e => {
  e.waitUntil(
    caches.open(CACHE).then(c => c.addAll(PRECACHE)).then(() => self.skipWaiting())
  );
});

self.addEventListener("activate", e => {
  e.waitUntil(
    caches.keys().then(keys =>
      Promise.all(keys.filter(k => k !== CACHE).map(k => caches.delete(k)))
    ).then(() => self.clients.claim())
  );
});

// La sida be SW om å aktivere ny versjon utan reload-loop (valfritt å bruke frå app)
self.addEventListener("message", e => {
  if (e.data === "SKIP_WAITING") self.skipWaiting();
});

self.addEventListener("fetch", e => {
  const req = e.request;
  if (req.method !== "GET") return;

  // Hopp over eksterne API-ar — desse skal aldri cachast
  if (req.url.includes("api.open-meteo") ||
      req.url.includes("geonorge") ||
      req.url.includes("kartverket") ||
      req.url.includes("arcgisonline") ||
      req.url.includes("formspree")) return;

  // Berre handter same-origin
  if (new URL(req.url).origin !== self.location.origin) return;

  const erNavigasjon = req.mode === "navigate" ||
    (req.headers.get("accept") || "").includes("text/html");

  if (erNavigasjon) {
    // NETWORK-FIRST: ferskaste HTML, fall til cache offline
    e.respondWith(
      fetch(req).then(res => {
        const clone = res.clone();
        caches.open(CACHE).then(c => c.put(req, clone));
        return res;
      }).catch(() => caches.match(req).then(c => c || caches.match("/app")))
    );
    return;
  }

  // CACHE-FIRST for alt anna (JS/CSS/bilete/ikon)
  e.respondWith(
    caches.match(req).then(cached => {
      if (cached) return cached;
      return fetch(req).then(res => {
        if (!res || res.status !== 200 || res.type !== "basic") return res;
        const clone = res.clone();
        caches.open(CACHE).then(c => c.put(req, clone));
        return res;
      });
    })
  );
});
`;
              const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([sw],{type:"text/javascript"})); a.download = "sw.js"; a.click();
            }} style={{ padding:"8px 14px", background:"rgba(251,146,60,0.1)", border:"1px solid rgba(251,146,60,0.3)", borderRadius:10, color:"#fb923c", fontSize:12, cursor:"pointer", fontWeight:"600" }}>⬇ Last ned sw.js</button>
          )}
          <button onClick={() => { if(window.confirm(tr("admin.nullstillSporsmal"))) { lsRemove("fjell_admin_peaks"); setAdminPeaks(null); setAdminEndra(false); }}} style={{ padding:"8px 14px", background:"rgba(255,59,48,0.08)", border:"1px solid rgba(255,59,48,0.2)", borderRadius:10, color:"#FF3B30", fontSize:12, cursor:"pointer", fontWeight:"600" }}>{tr("admin.nullstill")}</button>
          {adminEndra && <span style={{ fontSize:11, color:"#fb923c", padding:"8px 0" }}>{tr("admin.endra")}</span>}
        </div>
      )}
    </>
  );
}

// Flytande versjon av same panel, synleg over kartet så snart admin-modus er
// PÅ — same posisjoneringsmønster som KartPunktPanel/ToppPanel (bottom:navHoyde,
// altså rett der ikon-raden/nav sluttar). Gøymt medan AdminToppPanel/LeggTilToppModal
// er opne (adminValtId/adminNyToppPunkt), sidan dei ligg i same område nedst.
function AdminModusFlytandePanel({ darkMode, aksent, navHoyde, ...adminProps }) {
  return (
    <div style={{
      position:"fixed", bottom: navHoyde ?? 74, left:0, right:0, maxWidth:KOMPAKT_BREDDE, margin:"0 auto",
      background: darkMode ? "rgba(28,28,30,0.97)" : "rgba(255,255,255,0.97)",
      borderRadius:"20px 20px 0 0", zIndex:899, overflow:"hidden",
      boxShadow:"0 -8px 32px rgba(0,0,0,0.3)", fontFamily:"-apple-system,'SF Pro Display',sans-serif",
      border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`, borderBottom:"none",
    }}>
      <div style={{ width:36, height:4, borderRadius:2, background: darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"10px auto 0" }}/>
      <AdminModusInnhald darkMode={darkMode} {...adminProps} />
    </div>
  );
}

// ─── KARTPUNKT-PANEL ─────────────────────────────────────────────────────────
function KartPunktPanel({ punkt, posisjon, darkMode, aksent, visVær, onLukk, navHoyde }) {
  const [høgde, setHøgde] = useState(null);
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);

  useEffect(() => {
    if (!punkt) return;
    setHøgde(null);
    fetch(`https://ws.geonorge.no/hoydedata/v1/punkt?nord=${punkt.lat.toFixed(5)}&ost=${punkt.lng.toFixed(5)}&koordsys=4326&geoid=nn2000`)
      .then(r => r.json())
      .then(d => setHøgde(Math.round(d.punkter?.[0]?.z ?? 0)))
      .catch(() => setHøgde("–"));
  }, [punkt]);

  if (!punkt) return null;

  return (
    <div style={{
      position: "fixed", bottom: navHoyde ?? 74, left: 0, right: 0, maxWidth:KOMPAKT_BREDDE, margin:"0 auto",
      background: darkMode ? "rgba(28,28,30,0.97)" : "rgba(255,255,255,0.97)",
      borderRadius: "20px 20px 0 0", padding: "12px 18px 20px", zIndex: 899,
      boxShadow: "0 -8px 32px rgba(0,0,0,0.3)", fontFamily: "-apple-system,'SF Pro Display',sans-serif",
    }}>
      <div style={{ width:36, height:4, borderRadius:2, background: darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"0 auto 14px" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:"600", color:t.tekst, marginBottom:4 }}>📍 {punkt.lat.toFixed(5)}°N, {punkt.lng.toFixed(5)}°E</div>
          <div style={{ fontSize:13, color:t.tekstMut, marginBottom: posisjon ? 4 : 0 }}>
            ⛰ {høgde === null ? tr("ver.hentarHogde") : høgde === "–" ? tr("ver.ukjendHogde") : `${høgde} m.o.h.`}
          </div>
          {posisjon && (() => {
            const dist = haversine(posisjon.lat, posisjon.lng, punkt.lat, punkt.lng);
            return (<div style={{ fontSize:13, color:t.aksent, fontWeight:"600" }}>🧭 {dist < 1000 ? `${Math.round(dist)} m` : `${(dist/1000).toFixed(1)} km`} frå deg</div>);
          })()}
        </div>
        <button onClick={() => { if(punkt.fjernMarkør) punkt.fjernMarkør(); onLukk(); }} style={{ background: darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", border:"none", borderRadius:8, color:t.tekstMut, width:30, height:30, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
      </div>
      {visVær && <VærWidget lat={punkt.lat} lng={punkt.lng} namn={tr("ver.dettePunktet")} darkMode={darkMode} t={t} />}
      {!visVær && <div style={{ fontSize:11, color:t.tekstDim, textAlign:"center", padding:"4px 0 2px" }}>{tr("ver.perPunktAv")}</div>}
    </div>
  );
}

// ─── KART (Maplibre GL) ───────────────────────────────────────────────────────
const KARTLAG = [
  { id: "topo",     label: "🗺 Topo",     url: "https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png",                attr: "© Kartverket" },
  { id: "satelitt", label: "🛰 Satelitt", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",     attr: "© Esri, Maxar, Earthstar Geographics" },
  { id: "graa",     label: "🌫 Gråtone",  url: "https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png",        attr: "© Kartverket" },
  { id: "osm",      label: "🌍 Veikart",  url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",                                                attr: "© OpenStreetMap" },
];

function lagMarkørElement(besøkt, type) {
  const mkSz = besøkt ? 50 : 45; // besøkt: 44 → 50, ubesøkt: 36 → 45 (25 % større) — held tydeleg storleiksskilnad
  // Ikkje-besøkt hytte: blå, same tone som dot-markøren ved lågt zoom (#0A84FF) —
  // konsekvent skilje mellom hytte/topp uansett zoomnivå. Ikkje-besøkt topp: grått som før.
  const mkFarge = besøkt ? "#30D158" : (type === "hytte" ? "#0A84FF" : "#6B6B6E");
  const div = document.createElement("div");
  div.style.cssText = `width:${mkSz}px;height:${Math.round(mkSz*1.2)}px;cursor:pointer`;
  // Unik id per instans — fleire markørar ligg i DOM-en samstundes, og clipPath-id-ar
  // må vere unike i heile dokumentet, elles "lek" klippinga mellom ulike markørar.
  const clipId = `pinclip-${Math.random().toString(36).slice(2)}`;
  const pinPath = "M19 44 C19 44 2 28 2 17 C2 8.7 9.7 2 19 2 C28.3 2 36 8.7 36 17 C36 28 19 44 19 44Z";
  const indre = type === "hytte"
    // Hytte, besøkt: liten "scene" klipt mot sjølve pin-konturen — blå himmel bak,
    // mørkare grønt gras nede (heilt til pin-spissen), kvit hytte-silhuett oppå.
    ? (besøkt
      ? `<g clip-path="url(#${clipId})">
           <rect x="0" y="0" width="38" height="46" fill="#7EC8F5"/>
           <rect x="0" y="24" width="38" height="22" fill="#2E7D32"/>
         </g>
         <path d="M19 9 L29 18 L27 18 L27 28 L11 28 L11 18 L9 18 Z" fill="white"/>
         <path d="M16 22 L22 22 L22 28 L16 28 Z" fill="#6B4226"/>`
      // Hytte, ikkje besøkt: enkel hytte/cabin-silhuett (som før)
      : `<path d="M19 9 L29 18 L27 18 L27 28 L11 28 L11 18 L9 18 Z" fill="white"/>
         <path d="M16 22 L22 22 L22 28 L16 28 Z" fill="#6B4226"/>`)
    // Topp: fjell-triangel (som før)
    : `<path d="M8 27 L19 9 L30 27 Z" fill="white"/>
       <path d="M19 9 L15 18 L23 18 Z" fill="${mkFarge}"/>
       <path d="M15 18 Q17 14 19 12 Q21 14 23 18 Z" fill="white" opacity="0.45"/>
       ${besøkt ? `<path d="M19 9 L30 27 L25 27 Z" fill="rgba(0,0,0,0.1)"/>` : ''}`;
  div.innerHTML = `<svg width="${mkSz}" height="${Math.round(mkSz*1.2)}" viewBox="0 0 38 46" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,0.4));display:block">
    <defs><clipPath id="${clipId}"><path d="${pinPath}"/></clipPath></defs>
    <path d="${pinPath}" fill="${mkFarge}" stroke="white" stroke-width="1.8"/>
    ${indre}
  </svg>`;
  return div;
}

function Kart({ posisjon, besøktIds, onToppKlikk, aktivListe, flyTilRef, onPunktKlikk, adminFlyttAktiv, aktivTopper, finnGPS, gpsStatus, onToggleFullskjerm, erFullskjerm, kompakt, harTilgang, søkMarkør, setSøkMarkør, punktFilter }) {
  const mapRef = useRef(null);
  const mlMapRef = useRef(null);
  const markørarRef = useRef([]);
  const userMarkørRef = useRef(null);
  const userArrowElRef = useRef(null);
  const [heading, setHeading] = useState(null);
  const kompassStartaRef = useRef(false);
  const punktMarkørRef = useRef(null);
  const aktivListeRef = useRef(aktivListe);
  const aktivTopperRef = useRef(aktivTopper);
  const besøktIdsRef = useRef(besøktIds);
  const punktFilterRef = useRef(punktFilter);
  // FIX: callbacks i refs — kart-handlers (click/zoomend) vert registrerte
  // éin gong ved init og fanga før gamle, utdaterte closures. I admin-modus
  // gjorde det at toppklikk etter zoom opna feil panel, og at flytt-modus
  // las gamle verdiar. Refs gjer at handlers alltid ser siste versjon.
  const onToppKlikkRef = useRef(onToppKlikk);
  const onPunktKlikkRef = useRef(onPunktKlikk);
  const adminFlyttAktivRef = useRef(adminFlyttAktiv);
  useEffect(() => {
    onToppKlikkRef.current = onToppKlikk;
    onPunktKlikkRef.current = onPunktKlikk;
    adminFlyttAktivRef.current = adminFlyttAktiv;
  });
  const [klar, setKlar] = useState(!!window.maplibregl);
  const [aktivtLag, setAktivtLag] = useState("topo");
  const [bearing, setBearing] = useState(0);

  useEffect(() => { aktivListeRef.current = aktivListe; }, [aktivListe]);
  useEffect(() => { aktivTopperRef.current = aktivTopper; }, [aktivTopper]);
  useEffect(() => { besøktIdsRef.current = besøktIds; }, [besøktIds]);
  useEffect(() => { punktFilterRef.current = punktFilter; }, [punktFilter]);

  const KARTLAG_ML = {
    topo:     "https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png",
    satelitt: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    graa:     "https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png",
  };

  useEffect(() => {
    if (window.maplibregl) { setKlar(true); return; }
    if (document.getElementById("maplibre-js")) return;
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.id = "maplibre-js";
    js.src = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js";
    js.onload = () => setKlar(true);
    document.head.appendChild(js);
    if (!document.getElementById("kart-css")) {
      const style = document.createElement("style");
      style.id = "kart-css";
      style.textContent = `
        @keyframes pulsRing { 0%{transform:scale(0.5);opacity:1} 100%{transform:scale(2.5);opacity:0} }
        @keyframes toastPop { 0%{opacity:0;transform:translateX(-50%) translateY(-12px)} 100%{opacity:1;transform:translateX(-50%) translateY(0)} }
        .maplibregl-ctrl-attrib { display:none !important; }
        .maplibregl-ctrl-logo { display:none !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (!klar || !mapRef.current || mlMapRef.current) return;
    const ml = window.maplibregl;
    const map = new ml.Map({
      container: mapRef.current,
      style: {
        version: 8,
        sources: { "raster-tiles": { type: "raster", tiles: [KARTLAG_ML.topo], tileSize: 256, attribution: "© Kartverket" } },
        layers: [{ id: "bakgrunn", type: "raster", source: "raster-tiles" }],
        glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
      },
      center: [5.36, 60.385], zoom: 10, maxZoom: 18, clickTolerance: 3, bearingSnap: 0,
      inertia: true, inertiaOptions: { linearity: 0.3, easing: t => t, maxSpeed: 1400, deceleration: 2500 },
    });
    mlMapRef.current = map;
    window._mlMap = map; // Global ref for Målestokk
    map.touchZoomRotate.enableRotation();

    map.on("click", async (e) => {
      const { lng, lat } = e.lngLat;
      if (adminFlyttAktivRef.current && adminFlyttAktivRef.current()) {
        if (onPunktKlikkRef.current) onPunktKlikkRef.current({ lat, lng, fjernMarkør: () => {} });
        return;
      }
      const src = map.getSource("punkt-src");
      if (src) {
        src.setData({ type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "Point", coordinates: [lng, lat] } }] });
      }
      punktMarkørRef.current = { lng, lat };
      map.panTo([lng, lat], { duration: 400 });
      if (onPunktKlikkRef.current) onPunktKlikkRef.current({
        lat, lng,
        fjernMarkør: () => {
          const s = map.getSource("punkt-src");
          if (s) s.setData({ type: "FeatureCollection", features: [] });
          punktMarkørRef.current = null;
        }
      });
    });

    if (flyTilRef) {
      flyTilRef.current = (lat, lng, zoom = 14) => map.flyTo({ center: [lng, lat], zoom, duration: 800 });
      flyTilRef.invalidate = () => map.resize();
      flyTilRef.refresh = () => oppdaterMarkørar();
      // punktListe: [{lat, lng}, ...] i rekkjefølgja dei vart registrerte.
      // Reint fugleflukt-visualisering — vi har ingen ekte gangrute-data.
      flyTilRef.visKjede = (punktListe) => {
        if (!punktListe || punktListe.length < 2) return;
        const lineSrc = map.getSource("kjede-line-src");
        const punktSrc = map.getSource("kjede-punkt-src");
        if (!lineSrc || !punktSrc) return;
        lineSrc.setData({
          type: "Feature", geometry: { type: "LineString", coordinates: punktListe.map(p => [p.lng, p.lat]) },
        });
        punktSrc.setData({
          type: "FeatureCollection",
          features: punktListe.map((p, i) => ({
            type: "Feature",
            geometry: { type: "Point", coordinates: [p.lng, p.lat] },
            properties: { rekkjefolge: String(i + 1) },
          })),
        });
        const lngs = punktListe.map(p => p.lng), lats = punktListe.map(p => p.lat);
        map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 70, duration: 800 });
      };
      flyTilRef.skjulKjede = () => {
        const lineSrc = map.getSource("kjede-line-src");
        const punktSrc = map.getSource("kjede-punkt-src");
        if (lineSrc) lineSrc.setData({ type: "Feature", geometry: { type: "LineString", coordinates: [] } });
        if (punktSrc) punktSrc.setData({ type: "FeatureCollection", features: [] });
      };
    }

    map.on("zoomend", () => oppdaterMarkørar());
    map.on("rotateend", () => oppdaterMarkørar());
    map.on("rotate", () => {
      if (!window._bearingRaf) {
        window._bearingRaf = requestAnimationFrame(() => {
          setBearing(mlMapRef.current?.getBearing() ?? 0);
          window._bearingRaf = null;
        });
      }
    });
    map.on("load", () => {
      map.addSource("punkt-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: "punkt-puls", type: "circle", source: "punkt-src", paint: { "circle-radius": 14, "circle-color": "rgba(255,59,48,0.25)", "circle-stroke-width": 0 } });
      map.addLayer({ id: "punkt-kjerne", type: "circle", source: "punkt-src", paint: { "circle-radius": 7, "circle-color": "#FF3B30", "circle-stroke-width": 2.5, "circle-stroke-color": "white" } });
      // Kjede-visualisering: rett strek (fugleflukt, IKKJE faktisk gangrute —
      // vi har ingen stidata) mellom toppane i ei kjede, pluss nummererte
      // punkt som viser rekkjefølgja dei vart registrerte i.
      map.addSource("kjede-line-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: "kjede-line", type: "line", source: "kjede-line-src",
        paint: { "line-color": "#a855f7", "line-width": 3, "line-dasharray": [2, 1.5] },
        layout: { "line-cap": "round", "line-join": "round" } });
      map.addSource("kjede-punkt-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: "kjede-punkt-sirkel", type: "circle", source: "kjede-punkt-src",
        paint: { "circle-radius": 12, "circle-color": "#a855f7", "circle-stroke-width": 2.5, "circle-stroke-color": "white" } });
      map.addLayer({ id: "kjede-punkt-tal", type: "symbol", source: "kjede-punkt-src",
        layout: { "text-field": ["get", "rekkjefolge"], "text-size": 12, "text-allow-overlap": true, "text-ignore-placement": true },
        paint: { "text-color": "#ffffff" } });
      oppdaterMarkørar();
    });
  // eslint-disable-next-line
  }, [klar]);

  const oppdaterMarkørar = useCallback(() => {
    const map = mlMapRef.current;
    if (!map || !window.maplibregl) return;
    const ml = window.maplibregl;
    markørarRef.current.forEach(({ marker }) => marker.remove());
    markørarRef.current = [];
    const zoom = map.getZoom();
    const ids = besøktIdsRef.current;
    const liste = aktivListeRef.current;
    const topper = aktivTopperRef.current;
    const pf = punktFilterRef.current || "begge";
    const synlege = (topper || ALLE_TOPPER).filter(t => {
      if (t.type === "hytte") return pf !== "toppar";            // hytter: overlag, vist unntatt «berre toppar»
      // Toppar utan trofé-tilhøyrsle skal ALLTID visast, uansett kva fane som er aktiv —
      // ei tom lister-liste skal aldri stoppe ein topp frå å bli synleg (jf. prinsippet
      // frå Kjeldefil-fana). Berre toppar som faktisk høyrer til ei anna liste vert skjult.
      return pf !== "hytter" && (t.lister.length === 0 || t.lister.includes(liste));
    });

    synlege.forEach((topp) => {
      const besøkt = ids.has(topp.id);
      let el;
      if (zoom < 9) {
        const sz = zoom < 7 ? 10 : 13;
        const farge = besøkt ? "#30D158" : (topp.type === "hytte" ? "#0A84FF" : "#8E8E93");
        el = document.createElement("div");
        el.style.cssText = `width:${sz}px;height:${sz}px;border-radius:50%;background:${farge};border:1.5px solid rgba(255,255,255,0.8);box-shadow:0 1px 3px rgba(0,0,0,0.3);cursor:pointer`;
      } else {
        el = lagMarkørElement(besøkt, topp.type);
      }
      // FIX: les frå ref så klikk-handler alltid har siste onToppKlikk
      el.addEventListener("click", (e) => { e.stopPropagation(); onToppKlikkRef.current(topp); });
      // FIX: terskelen her må matche terskelen over (zoom<9 ⇒ dott, elles pin)!
      // Var zoom<11 før — då fekk pin-forma (frå zoom 9) feil anker (senter i
      // staden for spiss) heilt til zoom 11, så spissen peikte ikkje på punktet.
      const anchor = zoom < 9 ? "center" : "bottom";
      // Sjølve SVG-en sin viewBox har spissen på y=44 av totalt 46 — eit par
      // pikslar luft under. Utan denne vesle forskyvinga landar spissen 2-3px
      // OVER koordinaten i staden for nøyaktig på han.
      const offset = anchor === "bottom" ? [0, Math.round(Math.round((besøkt?50:45)*1.2) * (2/46))] : [0, 0];
      const marker = new ml.Marker({ element: el, anchor, offset }).setLngLat([topp.lng, topp.lat]).addTo(map);
      markørarRef.current.push({ marker, toppId: String(topp.id) });
    });
  }, []); // FIX: stabil — les all data frå refs

  // FIX: bygg markørane om berre når dataa faktisk endrar seg
  // (før: full ombygging av ~100 DOM-element på kvar App-render)
  useEffect(() => {
    if (mlMapRef.current) { oppdaterMarkørar(); if (flyTilRef) flyTilRef.refresh = oppdaterMarkørar; }
  }, [oppdaterMarkørar, besøktIds, aktivListe, aktivTopper, punktFilter]);

  // FIX: resize etter at overgangen er ferdig (~0.25s animasjon), ikkje midt i
  // han. To kall: eitt rett etter animasjon, eitt litt seinare som sikring.
  useEffect(() => {
    if (!mlMapRef.current) return;
    const a = setTimeout(() => mlMapRef.current?.resize(), 400);
    const b = setTimeout(() => mlMapRef.current?.resize(), 650);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [erFullskjerm]);

  useEffect(() => {
    const map = mlMapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const url = KARTLAG_ML[aktivtLag] || KARTLAG_ML.topo;
    if (map.getSource("raster-tiles")) map.getSource("raster-tiles").setTiles([url]);
  }, [aktivtLag]);

  // Søkmarkør — raudt punkt frå stednamnsøk
  useEffect(() => {
    const map = mlMapRef.current;
    if (!map) return;
    if (!søkMarkør) {
      const src = map.getSource?.("punkt-src");
      if (src) src.setData({ type:"FeatureCollection", features:[] });
      return;
    }
    const src = map.getSource?.("punkt-src");
    if (src) {
      src.setData({ type:"FeatureCollection", features:[{ type:"Feature", geometry:{ type:"Point", coordinates:[søkMarkør.lng, søkMarkør.lat] } }] });
    }
  }, [søkMarkør]);

  // Diskré retningspil på GPS-kula — viser kva veg telefonen vender, til bruk
  // for å sikte mot eit mål. iOS krev at DeviceOrientationEvent.requestPermission()
  // vert kalla frå eit ekte brukartrykk (t.d. GPS-knappen), difor er dette ein
  // eigen funksjon i staden for automatisk oppstart.
  const startKompass = useCallback(() => {
    if (kompassStartaRef.current) return;
    const leggTilLyttar = () => {
      kompassStartaRef.current = true;
      let siste = null;
      const handler = (e) => {
        let h = null;
        if (typeof e.webkitCompassHeading === "number") {
          h = e.webkitCompassHeading; // iOS Safari: 0° = nord, aukar med klokka
        } else if (e.absolute && typeof e.alpha === "number") {
          h = (360 - e.alpha) % 360; // andre nettlesarar: alpha aukar mot klokka
        }
        if (h == null) return;
        if (siste == null || Math.abs(h - siste) > 2) { siste = h; setHeading(h); }
      };
      window.addEventListener("deviceorientation", handler, true);
    };
    if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
      DeviceOrientationEvent.requestPermission().then(res => { if (res === "granted") leggTilLyttar(); }).catch(() => {});
    } else if (typeof window.DeviceOrientationEvent !== "undefined") {
      leggTilLyttar();
    }
  }, []);

  useEffect(() => {
    if (!userArrowElRef.current) return;
    if (heading == null) { userArrowElRef.current.style.opacity = "0"; return; }
    userArrowElRef.current.style.opacity = "1";
    userArrowElRef.current.style.transform = `translateX(-50%) rotate(${heading}deg)`;
  }, [heading]);

  useEffect(() => {
    const map = mlMapRef.current;
    if (!map || !posisjon || !window.maplibregl) return;
    const ml = window.maplibregl;
    if (userMarkørRef.current) userMarkørRef.current.remove();
    const el = document.createElement("div");
    el.style.cssText = "position:relative;width:40px;height:40px;display:flex;align-items:center;justify-content:center";
    el.innerHTML = `
      <div style="position:absolute;width:40px;height:40px;border-radius:50%;background:rgba(59,143,232,0.2);"></div>
      <div class="gps-arrow" style="position:absolute;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:14px solid #3b8fe8;top:-8px;left:50%;transform:translateX(-50%) rotate(0deg);transform-origin:50% 28px;opacity:0;transition:opacity 0.3s ease;"></div>
      <div style="width:16px;height:16px;border-radius:50%;background:#3b8fe8;border:3px solid white;box-shadow:0 0 0 4px rgba(59,143,232,0.25);position:relative;z-index:1"></div>
    `;
    userArrowElRef.current = el.querySelector(".gps-arrow");
    if (userArrowElRef.current && heading != null) {
      userArrowElRef.current.style.opacity = "1";
      userArrowElRef.current.style.transform = `translateX(-50%) rotate(${heading}deg)`;
    }
    userMarkørRef.current = new ml.Marker({ element: el, anchor: "center" }).setLngLat([posisjon.lng, posisjon.lat]).addTo(map);
    if (posisjon.fly) map.flyTo({ center: [posisjon.lng, posisjon.lat], zoom: 14, duration: 800 });
  }, [posisjon]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}>
      <div ref={mapRef} style={{ flex: 1, minHeight: 0, background: "#1a1a2e" }}>
        {!klar && (<div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"#6a9a6a", fontSize:13 }}>{tr("kart.lastar")}</div>)}
      </div>
      {klar && (
        <div style={{ position:"absolute",
          // FIX: kontrollane stod på top:10 utan safe-area → låg oppå headeren
          // og under Dynamic Island. Plasser under headeren; berre øvst i fullskjerm.
          top: erFullskjerm
            ? "calc(env(safe-area-inset-top) + 12px)"
            : !harTilgang
              ? "calc(env(safe-area-inset-top) + 72px)"
              : kompakt
                ? "calc(env(safe-area-inset-top) + 112px)"
                : "calc(env(safe-area-inset-top) + 166px)",
          right:10, zIndex:500, display:"flex", flexDirection:"column", gap:8 }}>
          <button onClick={onToggleFullskjerm} style={{ width:44, height:44, border:"none", borderRadius:22, background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.25)" }} title={erFullskjerm ? tr("map.avsluttFullskjerm") : tr("map.fullskjerm")}>
            <span style={{ fontSize: erFullskjerm ? 20 : 18 }}>{erFullskjerm ? "✕" : "⛶"}</span>
          </button>
          <button onClick={() => { mlMapRef.current?.rotateTo(0, { duration: 500 }); }} style={{ width:44, height:44, border:"none", borderRadius:22, background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.25)", padding:0 }} title={tr("kart.resetNord")}>
            <svg width="30" height="30" viewBox="0 0 24 24">
              <g transform={`rotate(${bearing} 12 12)`}>
                <polygon points="12,2 15.5,12 12,10 8.5,12" fill="#FF3B30"/>
                <polygon points="12,22 15.5,12 12,14 8.5,12" fill="#8E8E93"/>
              </g>
            </svg>
          </button>
          <button onClick={() => setAktivtLag(a => a === "satelitt" ? "topo" : "satelitt")} style={{ width:44, height:44, border:"none", borderRadius:22, background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", cursor:"pointer", fontSize:22, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.25)" }}>
            {aktivtLag === "satelitt" ? "🗺" : "🛰"}
          </button>
        </div>
      )}
      {klar && (
        <button onClick={() => { startKompass(); finnGPS(); }} disabled={gpsStatus==="loading"} style={{ position:"absolute", bottom:90, right:10, zIndex:500, width:44, height:44, borderRadius:22, border:"none", cursor:"pointer", fontSize:18, fontWeight:"700", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.25)", background: gpsStatus==="ok" ? "#30D158" : "rgba(255,255,255,0.92)", color: gpsStatus==="ok" ? "white" : "#1C1C1E", backdropFilter:"blur(8px)" }}>
          {gpsStatus==="loading" ? "⟳" : "⊕"}
        </button>
      )}
    </div>
  );
}

// ─── SLIDE TO REGISTER ───────────────────────────────────────────────────────
function SlideKnapp({ onFullført, poeng, darkMode, disabled, avstand, aksent }) {
  const [pos, setPos] = useState(0);
  const [dreg, setDreg] = useState(false);
  const [suksess, setSuksess] = useState(false);
  const [partiklar, setPartiklar] = useState([]);
  const trackRef = useRef(null);
  const THUMB = 56;

  const trackBreidd = () => (trackRef.current?.offsetWidth || 300) - THUMB - 8;

  const start = useCallback((clientX) => {
    if (suksess || disabled) return;
    setDreg(true);
    const startX = clientX;
    const startPos = pos;
    let utløyst = false; // éin-skots-vakt: onMove fyrer mange gonger ved enden
    const reinsa = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
    const onMove = (e) => {
      if (utløyst) return;
      const dx = (e.touches?.[0]?.clientX ?? e.clientX) - startX;
      const ny = Math.max(0, Math.min(trackBreidd(), startPos + dx));
      setPos(ny);
      if (ny >= trackBreidd() - 5) {
        utløyst = true;       // hindrar fleire utløysingar i same drag
        reinsa();             // stopp vidare move/end-eventar straks
        setSuksess(true); setDreg(false);
        setPartiklar(Array.from({length:20},(_,i)=>({
          id:i, x:50+((Math.random()-0.5)*60),
          farge:[aksent||"#FC4C02","#FFD60A","#30D158","#60a5fa","#FF375F"][i%5],
          dur:0.6+Math.random()*0.6, dy:-(40+Math.random()*60), dx:(Math.random()-0.5)*80,
        })));
        setTimeout(()=>onFullført(), 600);
      }
    };
    const onEnd = () => {
      setDreg(false);
      setPos(prev => prev < trackBreidd() - 5 ? 0 : prev);
      reinsa();
    };
    document.addEventListener("mousemove", onMove, {passive:true});
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, {passive:true});
    document.addEventListener("touchend", onEnd);
  }, [pos, suksess, onFullført]);

  const pst = pos / Math.max(1, trackBreidd());
  const bg = disabled ? (darkMode ? "rgba(60,60,67,0.18)" : "rgba(0,0,0,0.06)") : suksess ? "#30D158" : `linear-gradient(90deg, ${aksent} ${pst*100}%, rgba(128,128,128,0.12) ${pst*100}%)`;

  return (
    <div style={{ position:"relative" }}>
      <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none", borderRadius:18 }}>
        {partiklar.map(p=>(
          <div key={p.id} style={{ position:"absolute", left:`${p.x}%`, bottom:"50%", width:7, height:7, borderRadius:"50%", background:p.farge, animation:`miniPop ${p.dur}s ease-out forwards`, "--dx":`${p.dx}px`, "--dy":`${p.dy}px` }}/>
        ))}
      </div>
      <div ref={trackRef} style={{ height: THUMB+8, borderRadius: THUMB/2+4, background: bg, border: `1px solid ${disabled ? (darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.1)") : suksess ? "rgba(48,209,88,0.4)" : "rgba(128,128,128,0.2)"}`, position:"relative", overflow:"hidden", transition: dreg ? "none" : "background 0.4s", userSelect:"none" }}>
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:"700", color: disabled ? (darkMode?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.25)") : suksess ? "#FFFFFF" : "rgba(255,255,255,0.8)", letterSpacing:0.5, pointerEvents:"none", opacity: suksess ? 1 : Math.max(0, 1 - pst*2), fontFamily:"-apple-system,sans-serif" }}>
          {disabled ? (avstand < 1000 ? `Du er ${avstand} m unna` : `Du er ${(avstand/1000).toFixed(1)} km unna`) : suksess ? `✓ Registrert! +${poeng}p` : `← Dra for å registrere · +${poeng}p`}
        </div>
        <div onMouseDown={e=>start(e.clientX)} onTouchStart={e=>start(e.touches[0].clientX)} style={{ position:"absolute", top:4, left: 4 + pos, width:THUMB, height:THUMB, borderRadius:THUMB/2, background: disabled ? (darkMode?"#48484A":"#C7C7CC") : suksess ? "#30D158" : aksent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, cursor: suksess ? "default" : "grab", boxShadow:"0 3px 12px rgba(0,0,0,0.35)", transition: dreg ? "none" : "left 0.3s, background 0.3s", userSelect:"none" }}>
          {suksess ? "✓" : "⛰"}
        </div>
      </div>
      <style>{`@keyframes miniPop { 0% { transform: translate(0,0) scale(1); opacity:1; } 100% { transform: translate(var(--dx),var(--dy)) scale(0); opacity:0; } }`}</style>
    </div>
  );
}

// ─── SKRIV I BOKA — alternativ til SlideKnapp ──────────────────────────────
// Ikkje ein slider, med vilje: eit vanleg trykk som spelar av ein liten
// "skriv i boka"-animasjon (dato + brukarnamn i løkkeskrift), og registrerer
// automatisk når animasjonen er ferdig. INGEN avbryt-mekanisme — i
// motsetnad til slideren (som hindrar UTILSIKTA trykk midt i lomma/vesken),
// er "trykk på ein synleg knapp mens du står ved toppen og har valt han i
// appen" alt eit medvite, fleire-steg val. Risikoen slideren løyser
// (utilsikta aktivering) er difor ikkje til stades her på same måte.
function SkrivIBokaKnapp({ onFullført, poeng, darkMode, disabled, brukarNamn, aksent, avstand }) {
  const [spelar, setSpelar] = useState(false);
  const [synleg, setSynleg] = useState("");
  const teksten = useMemo(() => {
    const dato = new Date().toLocaleDateString("nb-NO", { day:"numeric", month:"long", year:"numeric" });
    return `${dato}\n${brukarNamn || "Ein fjellgjengar"}`;
  }, [brukarNamn]);

  const start = () => {
    if (disabled || spelar) return;
    setSpelar(true);
    setSynleg("");
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setSynleg(teksten.slice(0, i));
      if (i >= teksten.length) {
        clearInterval(iv);
        setTimeout(() => { onFullført(); setSpelar(false); setSynleg(""); }, 700);
      }
    }, 45);
  };

  if (spelar) {
    const [datoLinje, namnLinje] = synleg.split("\n");
    return (
      <div style={{ width:"100%", background:"#FBF3E3", border:"2px solid #D9C9A3", borderRadius:16, padding:"14px 20px 18px", textAlign:"center", boxShadow:"0 6px 20px rgba(0,0,0,0.2)" }}>
        <div style={{ fontFamily:"'Caveat', cursive", fontSize:18, color:"#9C7C4A", minHeight:22 }}>{datoLinje || "\u00A0"}</div>
        <div style={{ fontFamily:"'Caveat', cursive", fontWeight:"700", fontSize:34, color:"#3D2B1A", minHeight:42, lineHeight:1.1 }}>{namnLinje}</div>
      </div>
    );
  }

  const avstandTekst = disabled && avstand != null
    ? (avstand < 1000 ? `Du er ${Math.round(avstand)} m unna` : `Du er ${(avstand/1000).toFixed(1)} km unna`)
    : null;

  return (
    <button onClick={start} disabled={disabled}
      style={{ width:"100%", padding:"16px", borderRadius:16, border:"none",
        background: disabled ? (darkMode?"#3A3A3C":"#C7C7CC") : aksent.aksent,
        color:"#fff", fontSize:16, fontWeight:"700", cursor: disabled?"default":"pointer",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        📖 {tr("reg.skrivIBoka")} · +{poeng}p
      </div>
      {avstandTekst && (
        <div style={{ fontSize:12, fontWeight:"600", opacity:0.85 }}>{avstandTekst}</div>
      )}
    </button>
  );
}

// ─── VÆR-WIDGET ──────────────────────────────────────────────────────────────
function kodeToEmojiStatic(k) {
  if (k===0) return "☀️"; if (k<=2) return "🌤"; if (k===3) return "☁️";
  if (k<=49) return "🌫"; if (k<=59) return "🌦"; if (k<=69) return "🌧";
  if (k<=79) return "🌨"; if (k<=82) return "🌦"; if (k<=86) return "🌨";
  if (k<=99) return "⛈"; return "🌤";
}

function VærWidget({ lat, lng, namn, darkMode, t }) {
  const [vær, setVær] = useState(null);
  const [lastar, setLastar] = useState(true);
  const [feil, setFeil] = useState(false);

  useEffect(() => {
    setLastar(true); setFeil(false); setVær(null);
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&current=temperature_2m,wind_speed_10m,wind_gusts_10m,weather_code,precipitation,relative_humidity_2m&hourly=temperature_2m,weather_code,wind_speed_10m,precipitation_probability&wind_speed_unit=ms&timezone=Europe%2FOslo&forecast_days=2`)
    .then(r => r.ok ? r.json() : Promise.reject())
    .then(data => {
      const c = data.current;
      const timeIdx = data.hourly.time.findIndex(t => t >= c.time);
      const neste6 = Array.from({length: 6}, (_, i) => {
        const idx = timeIdx + i;
        if (idx >= data.hourly.time.length) return null;
        const td = new Date(data.hourly.time[idx]);
        const erMorgon = td.getDate() !== new Date(c.time).getDate();
        return { kl: td.getHours(), erMorgon, temp: Math.round(data.hourly.temperature_2m[idx]), kode: data.hourly.weather_code[idx], vind: Math.round(data.hourly.wind_speed_10m[idx]), regn: data.hourly.precipitation_probability[idx] };
      }).filter(Boolean);
      const tempVerdi = Math.round(c.temperature_2m);
      if (!window._værCache) window._værCache = {};
      window._værCache[`${lat.toFixed(4)}_${lng.toFixed(4)}`] = { temp: tempVerdi, emoji: kodeToEmojiStatic(c.weather_code) };
      setVær({ temp: tempVerdi, vind: Math.round(c.wind_speed_10m), vindkast: Math.round(c.wind_gusts_10m || c.wind_speed_10m), fukt: Math.round(c.relative_humidity_2m), nedbør: c.precipitation, kode: c.weather_code, neste6 });
      setLastar(false);
    })
    .catch(() => { setFeil(true); setLastar(false); });
  }, [lat, lng]);

  const kodeToEmoji = (k) => {
    if (k === 0) return "☀️"; if (k <= 2) return "🌤"; if (k === 3) return "☁️";
    if (k <= 49) return "🌫"; if (k <= 59) return "🌦"; if (k <= 69) return "🌧";
    if (k <= 79) return "🌨"; if (k <= 82) return "🌦"; if (k <= 86) return "🌨";
    if (k <= 99) return "⛈"; return "🌤";
  };
  const kodeToTekst = (k) => {
    if (k === 0) return tr("ver.klart"); if (k <= 2) return tr("ver.lettskya"); if (k === 3) return tr("ver.overskya");
    if (k <= 49) return tr("ver.take"); if (k <= 59) return tr("ver.yr"); if (k <= 69) return tr("ver.regn");
    if (k <= 79) return tr("ver.sno"); if (k <= 82) return tr("ver.regnbyer"); if (k <= 86) return tr("ver.snobyer");
    if (k <= 99) return tr("ver.torden"); return "–";
  };

  if (lastar) return (<div style={{ background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)", border:`1px solid ${darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, borderRadius:14, padding:"14px", marginBottom:12, textAlign:"center", fontSize:12, color:t.tekstMut }}>{tr("ver.lastar")}</div>);
  if (feil) return (<div style={{ background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)", border:`1px solid ${darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, borderRadius:14, padding:"14px", marginBottom:12, textAlign:"center", fontSize:12, color:t.tekstMut }}>{tr("ver.feil")}</div>);

  return (
    <div style={{ background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)", border:`1px solid ${darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, borderRadius:14, padding:"14px", marginBottom:12 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:36 }}>{kodeToEmoji(vær.kode)}</span>
          <div>
            <div style={{ fontSize:26, fontWeight:"700", color:t.tekst, lineHeight:1 }}>{vær.temp}°C</div>
            <div style={{ fontSize:12, color:t.tekstMut, marginTop:2 }}>{kodeToTekst(vær.kode)}</div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:13, color:t.tekst, fontWeight:"600" }}>💨 {vær.vind} m/s</div>
          {vær.vindkast > vær.vind + 2 && <div style={{ fontSize:11, color:t.tekstMut }}>kast {vær.vindkast} m/s</div>}
          <div style={{ fontSize:11, color:t.tekstMut, marginTop:4 }}>💧 {vær.fukt}%</div>
        </div>
      </div>
      {vær.neste6?.length > 0 && (
        <div style={{ borderTop:`1px solid ${darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, paddingTop:10, display:"flex", justifyContent:"space-between" }}>
          {vær.neste6.map((t2, i) => (
            <div key={i} style={{ textAlign:"center", flex:1 }}>
              <div style={{ fontSize:10, color:t.tekstMut, marginBottom:3 }}>
                {t2.erMorgon && <span style={{ fontSize:8, display:"block", color:t.aksent, lineHeight:1, marginBottom:1 }}>{tr("ver.imorgon")}</span>}
                {t2.kl < 10 ? "0"+t2.kl : t2.kl}:00
              </div>
              <div style={{ fontSize:16 }}>{kodeToEmoji(t2.kode)}</div>
              <div style={{ fontSize:11, fontWeight:"600", color:t.tekst }}>{t2.temp}°</div>
              {t2.regn > 20 && <div style={{ fontSize:9, color:"#60a5fa" }}>{t2.regn}%</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TOPP-PANEL ──────────────────────────────────────────────────────────────
// Kort datoformat for turlista i ToppPanel («2. jul.»)
function fmtTurDato(d) {
  try {
    const dt = new Date(d);
    if (isNaN(dt)) return "";
    const datoStr = dt.toLocaleDateString("nb-NO", { day:"numeric", month:"short" });
    const tidStr = dt.toLocaleTimeString("nb-NO", { hour:"2-digit", minute:"2-digit" });
    return `${datoStr} · ${tidStr}`;
  } catch { return ""; }
}
// Rein dato, INGEN klokkeslett — til bruk der data faktisk berre er ein
// kalenderdag (t.d. kjede-topplista sin "beste dag"), ikkje eit ekte
// tidsstempel. Å leggje til klokkeslett der ville vist eit falskt tal
// (midnatt), sidan tidspunktet rett og slett ikkje finst i dataen.
function fmtDatoBerre(d) {
  try { const dt = new Date(d); if (isNaN(dt)) return ""; return dt.toLocaleDateString("nb-NO", { day:"numeric", month:"short" }); } catch { return ""; }
}

// ─── TOPPROFIL — toppen som eige «vesen» med eigen historikk ────────────────
// Fullskjerm-ark opna frå loggen (fase 1b v1: namn + dato, IKKJE turbilete —
// foto + rapportknapp kjem samla i v1.5, jf. moderasjonskravet i referansen).
// «Vis på kartet» tek over den gamle navigasjonsjobben til loggkortet.
function ToppProfil({ topp, darkMode, aksent, harTilgang, alleTopper, skjulUtanBilde, onVisKart, onVisBilde, onOpneBrukar, onClose, visLukkKnapp = true, brukerId, eigeLåsteBadges, eigeBesøk, visRutenettInfo, setVisRutenettInfo }) {
  const a = aksent || AKSENTAR.oransje;
  const t = darkMode ? TEMA.dark(a) : TEMA.light(a);
  const [turar, setTurar] = useState(null); // null = lastar, [] = tomt/feil
  const [visModus, setVisModus] = useState("siste"); // "siste" | "leiartavle"
  const [leiartavle, setLeiartavle] = useState(null); // null = ikkje henta enno
  useEffect(() => {
    // Hent leiartavla berre når fana faktisk vert vald (ikkje ved kvar opning
    // av toppen) — sparer eit unødvendig RPC-kall for dei fleste som berre
    // ser på siste turar.
    if (visModus !== "leiartavle" || leiartavle !== null) return;
    let avbrote = false;
    hentLeiartavle(topp.id).then(res => { if (!avbrote) setLeiartavle(res); });
    return () => { avbrote = true; };
  }, [visModus, topp.id, leiartavle]);
  useEffect(() => {
    let avbrote = false;
    setTurar(null);
    if (!supabaseKlar() || !harTilgang) { setTurar([]); return; }
    supabase.rpc("siste_turar_paa_topp", { p_topp_id: String(topp.id), p_grense: 30 })
      .then(({ data, error }) => { if (!avbrote) setTurar(error || !Array.isArray(data) ? [] : data); })
      .catch(() => { if (!avbrote) setTurar([]); });
    return () => { avbrote = true; };
  }, [topp.id, harTilgang]);

  // Leiarskap (flest besøk) — brukt til «utfordre hovudbilete»-hintet under.
  // Berre eit hint her; sjølve overtakinga vert verifisert på nytt server-side
  // (utfordre_hovudbilde-RPC) uansett kva klienten trur.
  const [hovudbileteStatus, setHovudbileteStatus] = useState(null); // { eigar, leiar, eigarTalBesok }
  useEffect(() => {
    let avbrote = false;
    setHovudbileteStatus(null);
    if (!supabaseKlar() || !harTilgang) return;
    hentHovudbileteStatus(topp.id).then(res => { if (!avbrote) setHovudbileteStatus(res); });
    return () => { avbrote = true; };
  }, [topp.id, harTilgang]);

  const erHytte = topp.type === "hytte";
  // Hero-illustrasjon: berre det EKSPLISITT godkjende hovudbiletet (sett via
  // FørsteBildePopup, kamera-verifisert). Finst ingen enno, viser vi INGEN
  // hero-bilete (fell til tekst-header-varianten under) — mykje tryggare enn
  // å vise eit tilfeldig/irrelevant bilete berre fordi det var nyast opplasta.
  // Krev at siste_turar_paa_topp-RPC-en returnerer er_hovudbilde OG tur_id (sjå SQL).
  const hovudbileteRad = Array.isArray(turar) ? turar.find(r => r.er_hovudbilde && r.tur_bilde) : null;
  const hovudbilde = hovudbileteRad?.tur_bilde || null;
  // Berre EIGAREN av hovudbiletet kan fjerne det (angra seg / ikkje nøgd) —
  // aldri andre brukarar, sjølv om dei ser det same biletet.
  const erEigHovudbilde = !!(hovudbileteRad && brukerId && hovudbileteRad.bruker_id === brukerId);
  const [visFjernStadfesting, setVisFjernStadfesting] = useState(false);
  const fjernEigHovudbilde = () => {
    if (!hovudbileteRad?.tur_id || !brukerId) return;
    setVisFjernStadfesting(true);
  };
  const utforFjernEigHovudbilde = () => {
    const turId = hovudbileteRad.tur_id;
    fjernHovudbildeStatus(turId);
    setTurar(prev => prev.map(r => r.tur_id === turId ? { ...r, er_hovudbilde: false } : r));
    setVisFjernStadfesting(false);
  };
  const erLeiarUtanEigarskap = !!(
    brukerId && hovudbileteStatus?.leiar === brukerId &&
    hovudbileteStatus?.eigar && hovudbileteStatus.eigar !== brukerId
  );

  // Fotografen (hovudbilete-eigaren) sine troféar, vist ved sida av kreditt-
  // linja på hero-biletet. Er DU fotografen, gjenbruker vi eigeLåsteBadges
  // (ingen ekstra nettverkskall). Er det NOKON ANNAN, hentar vi heile
  // turhistorikken deira (same profil_for_brukar-RPC som BrukarProfil) og
  // reknar ut badges lokalt — bursdagOverride=null av same personvernsgrunn
  // som i BrukarProfil (vi har uansett ikkje tilgang til deira bursdag).
  const [fotografBadges, setFotografBadges] = useState(null);
  const fotografBrukerId = hovudbileteRad?.bruker_id || null;
  useEffect(() => {
    let avbrote = false;
    setFotografBadges(null);
    if (!fotografBrukerId) return;
    if (fotografBrukerId === brukerId) { setFotografBadges(eigeLåsteBadges || new Set()); return; }
    if (!supabaseKlar() || !harTilgang) return;
    supabase.rpc("profil_for_brukar", { p_bruker_id: fotografBrukerId, p_grense: 60 })
      .then(({ data, error }) => {
        if (avbrote || error || !Array.isArray(data)) return;
        const turarFotograf = data.filter(r => r.tur_id);
        const synBesøk = turarFotograf.map(r => ({ toppId: r.topp_id, dato: r.dato }));
        const besøktIdsFotograf = new Set(synBesøk.map(b => b.toppId));
        const { låst } = sjekkBadges(synBesøk, besøktIdsFotograf, ALLE_TOPPER, null);
        setFotografBadges(låst);
      })
      .catch(() => {});
    return () => { avbrote = true; };
  }, [fotografBrukerId, brukerId, harTilgang]);

  // Snap-kollaps av hero-biletet: krympar til ei smal stripe når lista
  // skrollar forbi ei lita grense, ekspanderer att nær toppen av lista.
  // Ingen kontinuerleg pixel-for-pixel-følging (parallax) — berre éin enkel
  // boolsk tilstand + CSS-overgang, for å unngå jank på iOS.
  const [heroKollapsa, setHeroKollapsa] = useState(false);
  const [sisteTurarVising, setSisteTurarVising] = useState("liste"); // "liste" | "rutenett"
  const handterListeScroll = (e) => {
    const el = e.target;
    const st = el.scrollTop;
    // FIX: kollaps frigjer plass i lista (hero krympar 240–340px → 128px).
    // Har lista berre nok innhald til å så vidt trigge kollaps (t.d. 2
    // bilete), kan ho passe HEILT inn i den nyvunne plassen — nettlesaren
    // nullstiller då scrollTop til 0 automatisk, som vart tolka som "brukaren
    // scrolla attende", og ekspanderte hero att. Endelaus fram-og-tilbake.
    // Krev difor at overskytande innhald (scrollHeight - clientHeight) er
    // stort nok til at lista framleis treng skrolle SJØLV ETTER kollaps.
    const overskytande = el.scrollHeight - el.clientHeight;
    if (!heroKollapsa && overskytande > 200 && st > 30) setHeroKollapsa(true);
    else if (heroKollapsa && st < 10) setHeroKollapsa(false);
  };
  return (
    <div style={{ position:"fixed", inset:0, zIndex:99999, background:t.bg, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {hovudbilde ? (
        <div style={{ position:"relative", flexShrink:0 }}>
          <div style={{ height: heroKollapsa ? 128 : "clamp(240px, 34vh, 340px)", overflow:"hidden", transition:"height 0.28s ease", background: darkMode?"#1C1C1E":"#F2F2F7" }}>
            <div onClick={() => !heroKollapsa && onVisBilde?.(hovudbilde, hovudbileteRad?.bruker_id ? { brukerId: hovudbileteRad.bruker_id, visingsnamn: hovudbileteRad.visingsnamn, profilBilde: hovudbileteRad.profil_bilde } : null)} style={{ width:"100%", height:"clamp(240px, 34vh, 340px)", cursor: heroKollapsa ? "default" : "pointer" }}>
              <img src={hovudbilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
            </div>
            <div style={{
                position:"absolute", left:0, right:0, bottom:0, top: heroKollapsa ? 0 : "auto",
                padding: heroKollapsa ? "0 18px 8px" : "56px 18px 14px",
                display:"flex", flexDirection:"column", justifyContent: heroKollapsa ? "flex-end" : "flex-start",
                background: heroKollapsa ? "rgba(0,0,0,0.55)" : "linear-gradient(transparent, rgba(0,0,0,0.78))",
                pointerEvents:"none", transition:"padding 0.2s ease",
              }}>
              <div style={{ fontSize: heroKollapsa ? 20 : 26, fontWeight:"800", color:"#fff", textShadow:"0 1px 3px rgba(0,0,0,0.5)", transition:"font-size 0.2s ease", whiteSpace:"nowrap" }}>{erHytte ? "🏠 " : "⛰ "}{topp.namn}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.92)", marginTop:2, textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>
                {!erHytte && <span>↑ {topp.høgde} moh · </span>}+{poengForTopp(topp)}p
              </div>
              {!heroKollapsa && hovudbileteRad?.visingsnamn && (
                <div
                  onClick={() => hovudbileteRad.bruker_id && onOpneBrukar?.(hovudbileteRad.bruker_id)}
                  style={{ pointerEvents:"auto", display:"inline-flex", alignItems:"center", gap:6, marginTop:8, cursor: hovudbileteRad.bruker_id ? "pointer" : "default" }}>
                  <div style={{ width:18, height:18, borderRadius:9, overflow:"hidden", flexShrink:0, background:"rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10 }}>
                    {hovudbileteRad.profil_bilde ? <img src={hovudbileteRad.profil_bilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                  </div>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.85)", textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>
                    📸 {hovudbileteRad.visingsnamn}{hovudbileteStatus?.eigarTalBesok > 0 && ` · ${hovudbileteStatus.eigarTalBesok} ${hovudbileteStatus.eigarTalBesok === 1 ? tr("hb.turEitt") : tr("hb.turFleire")}`}
                  </span>
                  {fotografBadges && fotografBadges.size > 0 && (
                    <span style={{ color:"#fff", marginLeft:2 }}>
                      <BadgeRad låsteBadges={fotografBadges} alleTopper={alleTopper} storleik={18} />
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {erEigHovudbilde && (
            <button onClick={fjernEigHovudbilde} aria-label={tr("hb.fjernKnappLabel")} title={tr("hb.fjernKnappTitle")}
              style={{ position:"absolute", top:"calc(env(safe-area-inset-top) + 10px)", right: visLukkKnapp ? 58 : 14, width:36, height:36, borderRadius:18, border:"1px solid rgba(0,0,0,0.15)", background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", color:"#1C1C1E", fontSize:15, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.25)" }}>🗑</button>
          )}
          {visLukkKnapp && <button onClick={onClose} aria-label="Lukk" style={{ position:"absolute", top:"calc(env(safe-area-inset-top) + 10px)", right:14, width:36, height:36, borderRadius:18, border:"1px solid rgba(0,0,0,0.15)", background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", color:"#1C1C1E", fontSize:16, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.25)" }}>✕</button>}
          {visFjernStadfesting && (
            <SlettHovudbileteModal darkMode={darkMode} aksent={aksent}
              tittel={tr("hb.fjernTittel")}
              sporsmal={tr("hb.fjernSporsmal")}
              knappIkon="👋" knappTekst={tr("hb.fjernKnappTekst")}
              onAvbryt={() => setVisFjernStadfesting(false)}
              onSlett={utforFjernEigHovudbilde} />
          )}
        </div>
      ) : (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"calc(env(safe-area-inset-top) + 16px) 22px 0", flexShrink:0 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:"800", color:t.tekst }}>{erHytte ? "🏠 " : "⛰ "}{topp.namn}</div>
            <div style={{ fontSize:13, color:t.tekstMut, marginTop:3 }}>
              {!erHytte && <span>↑ {topp.høgde} moh · </span>}+{poengForTopp(topp)}p
            </div>
          </div>
          {visLukkKnapp && <button onClick={onClose} aria-label="Lukk" style={{ width:34, height:34, borderRadius:17, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:17, cursor:"pointer", flexShrink:0 }}>✕</button>}
        </div>
      )}

        <div style={{ display:"flex", flexDirection:"column", flex:1, minHeight:0, padding:"0 22px calc(env(safe-area-inset-bottom) + 24px)" }}>
        {hovudbileteStatus?.leiarNamn && hovudbileteStatus.leiarTalBesok > 0 && (
          <div
            onClick={() => hovudbileteStatus.leiar && hovudbileteStatus.leiar !== brukerId && onOpneBrukar?.(hovudbileteStatus.leiar)}
            style={{ display:"flex", alignItems:"center", gap:8, marginTop:16, padding:"10px 14px", borderRadius:12, background:t.bgCard, border:`1px solid ${t.border}`, cursor: (hovudbileteStatus.leiar && hovudbileteStatus.leiar !== brukerId) ? "pointer" : "default" }}>
            <span style={{ fontSize:18 }}>🏆</span>
            <span style={{ fontSize:13, color:t.tekst, fontWeight:"600", lineHeight:1.4 }}>
              {hovudbileteStatus.leiar && hovudbileteStatus.leiar === brukerId
                ? tr("hb.duFlestBesok", { n: hovudbileteStatus.leiarTalBesok })
                : tr("hb.flestBesok", { namn: hovudbileteStatus.leiarNamn, n: hovudbileteStatus.leiarTalBesok })}
            </span>
          </div>
        )}
        {erLeiarUtanEigarskap && (
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:16, padding:"10px 14px", borderRadius:12, background:t.aksentMut, border:`1px solid ${t.aksentBord}` }}>
            <span style={{ fontSize:18 }}>👑</span>
            <span style={{ fontSize:13, color:t.tekst, fontWeight:"600", lineHeight:1.4 }}>
              {tr("hb.leiarHint")}
            </span>
          </div>
        )}
        <div style={{ display:"flex", gap:6, margin:"16px 0 8px" }}>
          <button onClick={() => setVisModus("siste")}
            style={{ flex:1, padding:"7px 0", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:"700", letterSpacing:0.5, textTransform:"uppercase", background: visModus==="siste" ? t.aksent : "transparent", color: visModus==="siste" ? "#fff" : t.tekstMut }}>
            {tr("hb.sisteTurarFane")}
          </button>
          <button onClick={() => setVisModus("leiartavle")}
            style={{ flex:1, padding:"7px 0", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:"700", letterSpacing:0.5, textTransform:"uppercase", background: visModus==="leiartavle" ? t.aksent : "transparent", color: visModus==="leiartavle" ? "#fff" : t.tekstMut }}>
            🏆 {tr("hb.leiartavleFane")}
          </button>
          {brukerId && (
            <button onClick={() => setVisModus("mine")}
              style={{ flex:1, padding:"7px 0", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:"700", letterSpacing:0.5, textTransform:"uppercase", background: visModus==="mine" ? t.aksent : "transparent", color: visModus==="mine" ? "#fff" : t.tekstMut }}>
              {tr("hb.mineBesokFane")} ({(eigeBesøk || []).filter(b => String(b.toppId) === String(topp.id)).length})
            </button>
          )}
        </div>
        <div onScroll={handterListeScroll} style={{ flex:1, overflowY:"auto", minHeight:80 }}>
          {visModus === "mine" ? (() => {
            const mineBesøk = (eigeBesøk || [])
              .filter(b => String(b.toppId) === String(topp.id))
              .sort((a, b) => new Date(b.dato) - new Date(a.dato));
            if (!mineBesøk.length) return <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("hb.mineBesokTom")}</div>;
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:16, paddingBottom:8 }}>
                {mineBesøk.map(b => {
                  const datoObj = new Date(b.dato);
                  const datoStr = datoObj.toLocaleDateString("nb-NO", { day:"numeric", month:"short", year:"numeric" });
                  const tidStr = datoObj.toLocaleTimeString("nb-NO", { hour:"2-digit", minute:"2-digit" });
                  return (
                    <div key={b.id} style={{ background:t.bgCard, borderRadius:16, overflow:"hidden", border:`1px solid ${t.border}` }}>
                      {b.bilde && (
                        <div style={{ position:"relative" }}>
                          <img src={b.bilde} alt={tr("kart.toppbilde")} onClick={() => onVisBilde?.(b.bilde)}
                            style={{ width:"100%", height:200, objectFit:"cover", display:"block", cursor:"pointer" }}/>
                          {b.erHovudbilde && (
                            <div style={{ position:"absolute", bottom:10, left:10, background:"rgba(0,0,0,0.55)", backdropFilter:"blur(6px)", borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:"700", color:"#fff" }}>
                              {tr("hb.tag")}
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div style={{ fontSize:13, color:t.tekstMut }}>{datoStr} · {tidStr}</div>
                        {b.poeng > 0 && <div style={{ fontSize:12, color:"#fbbf24", fontFamily:"monospace", fontWeight:"700" }}>+{b.poeng}p</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })() : visModus === "leiartavle" ? (
            leiartavle === null ? (
              <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("hb.leiartavleLastar")}</div>
            ) : leiartavle.length === 0 ? (
              <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("hb.leiartavleTom")}</div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:2, paddingBottom:8 }}>
                {leiartavle.map((r, i) => (
                  <div key={r.bruker_id || i} onClick={() => r.bruker_id && onOpneBrukar?.(r.bruker_id)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 2px", cursor: r.bruker_id ? "pointer" : "default" }}>
                    <span style={{ fontSize:12, color:t.tekstDim, fontWeight:"700", width:18, textAlign:"right", flexShrink:0 }}>{i+1}</span>
                    <div style={{ width:30, height:30, borderRadius:15, overflow:"hidden", flexShrink:0, background: darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.05)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>
                      {r.profil_bilde ? <img src={r.profil_bilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                    </div>
                    <span style={{ flex:1, minWidth:0, fontSize:14, fontWeight:"600", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.visingsnamn || "—"}</span>
                    <span style={{ fontSize:13, color:t.tekstMut, fontWeight:"700", fontFamily:"monospace", flexShrink:0 }}>({r.tal_besok})</span>
                  </div>
                ))}
              </div>
            )
          ) : turar === null ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>…</div>
          ) : (() => {
            // Kronologisk (RPC sorterer alt nyaste først). FIX: ignorerer no
            // ALLTID registreringar utan bilete her — denne fana er ei rein
            // biletvising, uavhengig av den globale skjulUtanBilde-innstillinga.
            const vis = turar.filter(r => r.tur_bilde);
            if (vis.length === 0) {
              const tom = turar.length === 0 ? tr("topp.ingenTurar") : tr("topp.ingenBilete");
              return <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tom}</div>;
            }
            return (
              <>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:10 }}>
                <button onClick={() => setSisteTurarVising("liste")}
                  style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${sisteTurarVising==="liste" ? t.aksent : t.border}`, background: sisteTurarVising==="liste" ? t.aksentMut : "transparent", color: sisteTurarVising==="liste" ? t.aksent : t.tekstMut }}>
                  ☰ {tr("logg.visListe")}
                </button>
                <button onClick={() => setSisteTurarVising("rutenett")}
                  style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${sisteTurarVising==="rutenett" ? t.aksent : t.border}`, background: sisteTurarVising==="rutenett" ? t.aksentMut : "transparent", color: sisteTurarVising==="rutenett" ? t.aksent : t.tekstMut }}>
                  ▦ {tr("logg.visRutenett")}
                </button>
                {sisteTurarVising === "rutenett" && (
                  <button onClick={() => setVisRutenettInfo?.(v => !v)} title={tr("logg.rutenettInfo")}
                    style={{ padding:"5px 10px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${visRutenettInfo ? t.aksent : t.border}`, background: visRutenettInfo ? t.aksentMut : "transparent", color: visRutenettInfo ? t.aksent : t.tekstDim }}>
                    ℹ️
                  </button>
                )}
              </div>
              {sisteTurarVising === "rutenett" ? (
                <div style={{ columnCount:2, columnGap:10 }}>
                  {vis.map((r, i) => (
                    <div key={i} onClick={() => onVisBilde?.(r.tur_bilde, r.bruker_id ? { brukerId: r.bruker_id, visingsnamn: r.visingsnamn, profilBilde: r.profil_bilde } : null)}
                      style={{ breakInside:"avoid", marginBottom:10, borderRadius:14, overflow:"hidden", cursor:"pointer", background: darkMode?"#1C1C1E":"#F2F2F7", position:"relative" }}>
                      <img src={r.tur_bilde} alt="" loading="lazy" style={{ width:"100%", height:"auto", display:"block" }}/>
                      {/* FIX: fjerna eigen namneklikk her (for smått å treffe på eit
                          smalt rutenett-kort) — naviger til eigar skjer no via
                          fullskjermvisinga i staden, sjå ctx sendt til onVisBilde over. */}
                      {visRutenettInfo && (
                      <div style={{ position:"absolute", left:0, right:0, bottom:0, padding:"18px 8px 6px", background:"linear-gradient(transparent, rgba(0,0,0,0.7))", pointerEvents:"none" }}>
                        <div style={{ fontSize:11, fontWeight:"700", color:"#fff", textShadow:"0 1px 2px rgba(0,0,0,0.5)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.visingsnamn || "—"}</div>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:10, paddingBottom:8 }}>
                {vis.map((r, i) => (
                  // Stort biletkort — bilde → fullskjerm, namn-stripe → brukarprofil
                  <div key={i} style={{ borderRadius:14, overflow:"hidden", background: darkMode?"#1C1C1E":"#F2F2F7", position:"relative" }}>
                    <div onClick={() => onVisBilde?.(r.tur_bilde, r.bruker_id ? { brukerId: r.bruker_id, visingsnamn: r.visingsnamn, profilBilde: r.profil_bilde } : null)} style={{ width:"100%", height:280, overflow:"hidden", cursor:"pointer" }}>
                      <img src={r.tur_bilde} alt="" loading="lazy" style={{ width:"100%", height:"100%", display:"block", objectFit:"cover" }}/>
                    </div>
                    <div onClick={() => r.bruker_id && onOpneBrukar?.(r.bruker_id)} style={{ position:"absolute", left:0, right:0, bottom:0, padding:"24px 12px 10px", background:"linear-gradient(transparent, rgba(0,0,0,0.7))", display:"flex", alignItems:"center", gap:8, cursor: r.bruker_id ? "pointer" : "default" }}>
                      <div style={{ width:24, height:24, borderRadius:12, overflow:"hidden", flexShrink:0, background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11 }}>
                        {r.profil_bilde ? <img src={r.profil_bilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                      </div>
                      <span style={{ flex:1, minWidth:0, fontSize:13, fontWeight:"600", color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>
                        {r.visingsnamn || "—"}{r.bruker_id && <span style={{ opacity:0.75, marginLeft:3 }}>›</span>}
                      </span>
                      <span style={{ fontSize:12, color:"rgba(255,255,255,0.85)", flexShrink:0, textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>{fmtTurDato(r.dato)}</span>
                    </div>
                  </div>
                ))}
              </div>
              )}
              </>
            );
          })()}
        </div>

        <button onClick={onVisKart} style={{ width:"100%", marginTop:14, padding:"15px", background:t.aksent, color:"#fff", border:"none", borderRadius:12, fontSize:15, fontWeight:"700", cursor:"pointer", flexShrink:0 }}>
          🗺 {tr("topp.visKart")}
        </button>
        </div>
    </div>
  );
}

// Maks tal personar ein brukar kan følgje. MÅ vere likt talet i SQL-triggeren
// (folg_grense.sql) — databasen handhevar grensa, dette talet styrer berre UI
// (teljar og disabla knapp). Hald dei to i synk når grensa vert endra.
const MAKS_FØLGDE = 10;

// ─── BRUKARPROFIL — ein annan brukar som «vesen» (fase 1b) ──────────────────
// Fullskjerm-ark: offentleg profil (namn + bilete) + turhistorikk-galleri, likt
// toppprofilen. Hentar via profil_for_brukar-RPC (security definer) som returnerer
// BERRE offentlege felt — aldri e-post/bursdag/adresse. Same personvern-disiplin
// som siste_turar_paa_topp. Følg-knapp kjem i fase 3, ikkje her.
function BrukarProfil({ brukerId, darkMode, aksent, harTilgang, mittBrukerId, erAdmin, alleTopper, skjulUtanBilde, onOpneTopp, onOpneBrukar, onVisBilde, onClose }) {
  const a = aksent || AKSENTAR.oransje;
  const t = darkMode ? TEMA.dark(a) : TEMA.light(a);
  const [data, setData] = useState(null); // null = lastar, false = feil, {} = ok
  const [visTrofeListe, setVisTrofeListe] = useState(false);
  const [visFullStat, setVisFullStat] = useState(false); // ekstra statistikk, kollapsbar, standard skjult
  const erEigenProfil = mittBrukerId && brukerId && String(mittBrukerId) === String(brukerId);
  // Følg-funksjon, fase 1 (§ sjå plan i chat): ingen godkjenning enno, berre
  // direkte følging. Ope for alle brukarar.
  const [følgjer, setFølgjer] = useState(null); // null=lastar, true/false=kjent
  const [følgLastar, setFølgLastar] = useState(false);
  const [antalFølgde, setAntalFølgde] = useState(null); // null=ukjent, elles tal eg følgjer no
  const [følgFeil, setFølgFeil] = useState(""); // melding etter avvist følging
  const [visFølgjarListe, setVisFølgjarListe] = useState(false);
  useEffect(() => {
    if (erEigenProfil || !mittBrukerId || !brukerId || !supabaseKlar()) { setFølgjer(null); setAntalFølgde(null); return; }
    let avbrote = false;
    setFølgFeil("");
    supabase.from("folgjarar").select("id").eq("folgjar_id", mittBrukerId).eq("folgt_id", brukerId).maybeSingle()
      .then(({ data: d }) => { if (!avbrote) setFølgjer(!!d); })
      .catch(() => { if (!avbrote) setFølgjer(false); });
    supabase.from("folgjarar").select("id", { count: "exact", head: true }).eq("folgjar_id", mittBrukerId)
      .then(({ count, error }) => { if (!avbrote) setAntalFølgde(error || count == null ? null : count); })
      .catch(() => { if (!avbrote) setAntalFølgde(null); });
    return () => { avbrote = true; };
  }, [erEigenProfil, mittBrukerId, brukerId]);
  const togglFølg = async () => {
    if (følgLastar || følgjer === null) return;
    setFølgLastar(true);
    setFølgFeil("");
    try {
      if (følgjer) {
        const { error } = await supabase.from("folgjarar").delete().eq("folgjar_id", mittBrukerId).eq("folgt_id", brukerId);
        if (error) { setFølgFeil(tr("folg.feil")); }
        else { setFølgjer(false); setAntalFølgde(n => (n == null ? n : Math.max(0, n - 1))); }
      } else {
        const { error } = await supabase.from("folgjarar").insert({ folgjar_id: mittBrukerId, folgt_id: brukerId });
        if (error) {
          // Triggeren i databasen kastar FOLG_MAKS_NAADD når grensa er nådd.
          const nåddMaks = String(error.message || "").includes("FOLG_MAKS_NAADD");
          setFølgFeil(nåddMaks ? tr("folg.maksNaadd").replace("{n}", String(MAKS_FØLGDE)) : tr("folg.feil"));
          if (nåddMaks) setAntalFølgde(MAKS_FØLGDE);
        } else { setFølgjer(true); setAntalFølgde(n => (n == null ? n : n + 1)); }
      }
    } catch (e) { setFølgFeil(tr("folg.feil")); }
    setFølgLastar(false);
  };
  // Grensa er nådd og eg følgjer ikkje denne personen alt → knappen vert disabla.
  const maksNådd = følgjer === false && antalFølgde !== null && antalFølgde >= MAKS_FØLGDE;
  const følgMelding = følgFeil || (maksNådd ? tr("folg.maksNaadd").replace("{n}", String(MAKS_FØLGDE)) : "");
  useEffect(() => {
    let avbrote = false;
    setData(null);
    if (!supabaseKlar() || !harTilgang || !brukerId) { setData(false); return; }
    supabase.rpc("profil_for_brukar", { p_bruker_id: brukerId, p_grense: 60 })
      .then(({ data: d, error }) => { if (!avbrote) setData(error || !d ? false : d); })
      .catch(() => { if (!avbrote) setData(false); });
    return () => { avbrote = true; };
  }, [brukerId, harTilgang]);

  // profil_for_brukar returnerer rader med profil-felta gjentekne + tur-felt.
  // Første rad ber profilen; alle rader (med tur_id) er turhistorikken.
  const profil = Array.isArray(data) && data.length ? data[0] : null;
  const turar = Array.isArray(data) ? data.filter(r => r.tur_id) : [];
  const visTurar = skjulUtanBilde ? turar.filter(r => r.tur_bilde) : turar;
  const antallTurar = turar.length;
  const unikeToppar = new Set(turar.map(r => r.topp_id)).size;
  // Poengsum for DENNE brukaren — same utrekning som for deg sjølv (poengForTopp
  // per tur), men UTAN liste-fullført-bonusen, sidan den ligg lokalt på kvar
  // einskild eining og ikkje er noko me har tilgang til for andre brukarar.
  // Difor eit lite understatement for svært aktive brukarar som har fullført
  // heile lister — akseptert avveging for eit offentleg samanlikningstal.
  const poengSum = turar.reduce((sum, r) => {
    const topp = alleTopper.find(t => String(t.id) === String(r.topp_id));
    return sum + (topp ? poengForTopp(topp) : 0);
  }, 0);
  // Høgaste besøkte topp — enkel oppslag per unike topp_id, høgste vinn.
  const høgasteTopp = [...new Set(turar.map(r => r.topp_id))]
    .map(id => alleTopper.find(t => String(t.id) === String(id)))
    .filter(t => t && t.høgde != null)
    .sort((a, b) => b.høgde - a.høgde)[0];
  // MÅ deklarerast FØR fullStat-blokka under, som bruker han.
  const besøktIds = new Set(turar.map(r => r.topp_id));
  // Full statistikk (favorittopp, favoritt-tidspunkt, aktive dagar, rekorddag,
  // kommunar besøkt, topplister fullført) — same utrekning som StatistikkPanel,
  // men vist BERRE om brukaren sjølv har slått på "del full statistikk".
  const delerStatistikk = profil?.del_statistikk === true;
  let fullStat = null;
  if (delerStatistikk && turar.length) {
    const sortert = [...turar].sort((x,y) => new Date(x.dato) - new Date(y.dato));
    const dagMap = {};
    turar.forEach(r => { const d = new Date(r.dato).toDateString(); dagMap[d] = (dagMap[d]||0)+1; });
    const besteDag = Object.entries(dagMap).sort((a,b)=>b[1]-a[1])[0];
    const tidFordeling = { morgon:0, dag:0, kveld:0, natt:0 };
    turar.forEach(r => {
      const h = new Date(r.dato).getHours();
      if (h>=5 && h<10) tidFordeling.morgon++;
      else if (h>=10 && h<17) tidFordeling.dag++;
      else if (h>=17 && h<21) tidFordeling.kveld++;
      else tidFordeling.natt++;
    });
    const favoritttid = Object.entries(tidFordeling).sort((a,b)=>b[1]-a[1])[0];
    const tidnamn = { morgon:"🌅 Morgon (05-10)", dag:"☀️ Dag (10-17)", kveld:"🌇 Kveld (17-21)", natt:"🌙 Natt (21-05)" };
    const toppCount = {};
    turar.forEach(r => { if(!toppCount[r.topp_id]) toppCount[r.topp_id]={count:0,namn:r.namn}; toppCount[r.topp_id].count++; });
    const favorittTopp = Object.values(toppCount).sort((a,b)=>b.count-a.count)[0];
    const kommunar = new Set(turar.map(r => {
      const topp = alleTopper.find(t => String(t.id) === String(r.topp_id));
      return topp?.kommune ? normTekst(topp.kommune) : null;
    }).filter(Boolean)).size;
    const listerBadges = genererListeBadges(alleTopper, besøktIds);
    const listerFullført = listerBadges.reduce((sum,b) => sum + (b.kommunar?.length||0), 0);
    fullStat = {
      første: sortert[0], siste: sortert[sortert.length-1],
      aktiveDagar: Object.keys(dagMap).length, besteDag,
      favoritttid, tidnamn, favorittTopp, kommunar, listerFullført,
    };
  }
  // Badges for DENNE brukaren, ikkje for meg. bursdagOverride=null er
  // avgjerande — utan han ville sjekkBadges lese MITT eige bursdagsflagg og
  // teste det feilaktig mot DEIRA besøk (og vi har uansett ikkje deira
  // bursdag tilgjengeleg — profil_for_brukar eksponerer ho aldri).
  // MERK: p_grense:60 avgrensar turhistorikken — svært aktive brukarar (over
  // 60 registreringar totalt) kan difor få nokre høg-tal-badges (t.d.
  // Hundremannen, Stamgjest) undervurdert her.
  const synBesøk = turar.map(r => ({ toppId: r.topp_id, dato: r.dato }));
  const { låst: låsteBadges } = sjekkBadges(synBesøk, besøktIds, ALLE_TOPPER, null);

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100000, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"12px 22px calc(env(safe-area-inset-bottom) + 24px)", maxHeight:"88vh", display:"flex", flexDirection:"column", position:"relative" }}>
        <button onClick={onClose} aria-label="Lukk" style={{ position:"absolute", top:12, right:16, width:32, height:32, borderRadius:16, border:"none", background:"#FFFFFF", boxShadow:"0 1px 4px rgba(0,0,0,0.2)", color:"#1C1C1E", fontSize:15, cursor:"pointer", flexShrink:0, zIndex:1 }}>✕</button>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14, marginTop:44 }}>
          <div onClick={() => profil?.profil_bilde && onVisBilde?.(profil.profil_bilde)}
            style={{ width:52, height:52, borderRadius:26, overflow:"hidden", flexShrink:0, background:t.søkBg, border:`1px solid ${t.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, cursor: profil?.profil_bilde ? "pointer" : "default" }}>
            {profil?.profil_bilde ? <img src={profil.profil_bilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
          </div>
          <div style={{ flex:1, minWidth:0, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
            <div style={{ fontSize:20, fontWeight:"800", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{profil?.visingsnamn || (data === null ? "…" : "—")}</div>
            {!erEigenProfil && følgjer !== null && (
              <button onClick={togglFølg} disabled={følgLastar || maksNådd}
                style={{ padding:"8px 18px", borderRadius:25, fontSize:15, fontWeight:"700", cursor: (følgLastar || maksNådd) ? "default" : "pointer", whiteSpace:"nowrap", flexShrink:0,
                  border: følgjer ? `1px solid ${t.border}` : "none",
                  background: følgjer ? "transparent" : t.aksent, color: følgjer ? t.tekstMut : "#FFFFFF",
                  opacity: følgLastar ? 0.6 : (maksNådd ? 0.35 : 1) }}>
                {følgjer ? "✓ Følgjer" : "+ Følg"}
              </button>
            )}
          </div>
        </div>
        {!erEigenProfil && følgMelding && (
          <div style={{ fontSize:13, color:t.tekstMut, marginBottom:10, lineHeight:1.4 }}>{følgMelding}</div>
        )}
        <div style={{ marginBottom:14 }}>
          {profil && <div style={{ fontSize:14, fontWeight:"600", color:t.tekstMut }}>{unikeToppar} {tr("brukar.toppar")} · {antallTurar} {tr("brukar.turar")} · {poengSum}p</div>}
          {erEigenProfil && (
            <div onClick={() => setVisFølgjarListe(true)}
              style={{ marginTop:6, display:"inline-flex", alignItems:"center", gap:4, fontSize:13, fontWeight:"700", color:t.aksent, cursor:"pointer" }}>
              👥 Følgjer
            </div>
          )}
          {profil && låsteBadges.size > 0 && (
            <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:6, color:t.tekstMut }}>
              <span style={{ fontSize:13, fontWeight:"700" }}>{tr("trofe.tittel")}:</span>
              <BadgeRad låsteBadges={låsteBadges} alleTopper={alleTopper} onVisAlle={() => setVisTrofeListe(true)} />
            </div>
          )}
          {profil && (høgasteTopp || fullStat) && (
            <div style={{ marginTop:8 }}>
              <div onClick={() => setVisFullStat(v => !v)}
                style={{ display:"inline-flex", alignItems:"center", gap:3, cursor:"pointer", fontSize:13, fontWeight:"700", color:t.aksent }}>
                {visFullStat ? tr("stat.visMindre") : tr("stat.visMeir")}
                <span style={{ fontSize:12, transform: visFullStat ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
              </div>
              {visFullStat && (
                <div style={{ fontSize:14, fontWeight:"600", color:t.tekstMut, marginTop:4, lineHeight:1.6 }}>
                  {høgasteTopp && <div>🏔 {tr("brukar.hogaste")}: {høgasteTopp.namn} ({høgasteTopp.høgde} moh)</div>}
                  {fullStat && fullStat.kommunar > 0 && <div>🗺 {tr("stat.kommunarBesokt")}: {fullStat.kommunar}</div>}
                  {fullStat && fullStat.listerFullført > 0 && <div>🏆 {tr("stat.listerFullfort")}: {fullStat.listerFullført}</div>}
                  {fullStat && fullStat.favorittTopp && fullStat.favorittTopp.count > 1 && <div>❤️ {fullStat.favorittTopp.namn} ({fullStat.favorittTopp.count}×)</div>}
                  {fullStat && fullStat.besteDag && fullStat.besteDag[1] > 1 && <div>🔥 {tr("stat.rekorddag")}: {tr("stat.toppar",{n:fullStat.besteDag[1]})}</div>}
                  {fullStat && <div>⏰ {fullStat.tidnamn[fullStat.favoritttid[0]]}</div>}
                  {fullStat && <div>📆 {tr("stat.aktiveDagar")}: {fullStat.aktiveDagar}</div>}
                </div>
              )}
            </div>
          )}
        </div>

        {visTrofeListe && (
          <TrofeListeModal visingsnamn={profil?.visingsnamn || "—"} låsteBadges={låsteBadges} alleTopper={alleTopper} darkMode={darkMode} aksent={aksent} onClose={() => setVisTrofeListe(false)} />
        )}

        {visFølgjarListe && (
          <FølgjarListe mittBrukerId={mittBrukerId} alleTopper={alleTopper} darkMode={darkMode} aksent={aksent}
            onOpneProfil={(bid) => { setVisFølgjarListe(false); onOpneBrukar?.(bid); }}
            onClose={() => setVisFølgjarListe(false)} />
        )}

        <div style={{ fontSize:11, fontWeight:"700", letterSpacing:0.5, textTransform:"uppercase", color:t.tekstMut, margin:"16px 0 8px" }}>{tr("topp.sisteTurar")}</div>
        <div style={{ flex:1, overflowY:"auto", minHeight:80 }}>
          {data === null ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>…</div>
          ) : data === false ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{tr("brukar.feil")}</div>
          ) : visTurar.length === 0 ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"8px 0" }}>{antallTurar === 0 ? tr("topp.ingenTurar") : tr("topp.ingenBilete")}</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10, paddingBottom:8 }}>
              {visTurar.map((r, i) => r.tur_bilde ? (
                <div key={i} onClick={() => onVisBilde?.(r.tur_bilde)} style={{ cursor:"pointer", borderRadius:14, overflow:"hidden", background: darkMode?"#1C1C1E":"#F2F2F7", position:"relative" }}>
                  <div style={{ width:"100%", height:280, overflow:"hidden" }}>
                    <img src={r.tur_bilde} alt="" loading="lazy" style={{ width:"100%", height:"100%", display:"block", objectFit:"cover" }}/>
                  </div>
                  <div onClick={(e) => { e.stopPropagation(); onOpneTopp?.(r.topp_id); }} style={{ position:"absolute", left:0, right:0, bottom:0, padding:"24px 12px 10px", background:"linear-gradient(transparent, rgba(0,0,0,0.7))", display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                    <span style={{ flex:1, minWidth:0, fontSize:13, fontWeight:"600", color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>{(ALLE_TOPPER.find(tp => String(tp.id) === String(r.topp_id))?.namn) || "—"}</span>
                    <span style={{ fontSize:12, color:"rgba(255,255,255,0.85)", flexShrink:0, textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>{fmtTurDato(r.dato)}</span>
                  </div>
                </div>
              ) : (
                <div key={i} onClick={() => onOpneTopp?.(r.topp_id)} style={{ display:"flex", alignItems:"center", gap:10, padding:"2px 2px", cursor:"pointer" }}>
                  <span style={{ flex:1, minWidth:0, fontSize:14, fontWeight:"600", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{(ALLE_TOPPER.find(tp => String(tp.id) === String(r.topp_id))?.namn) || "—"}</span>
                  <span style={{ fontSize:12, color:t.tekstMut, flexShrink:0 }}>{fmtTurDato(r.dato)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── FØLGJARLISTE (fase 1 — ingen godkjenning, admin-gata) ──────────────────
// Hentar ALLE turane til ALLE følgde personar i ÉIN RPC (folgde_turar), ikkje
// eitt kall per person. Poeng/unike/turar reknast klient-side med same
// utrekning som BrukarProfil, sidan poengForTopp treng ALLE_TOPPER-høgder
// som ikkje finst i databasen.
function FølgjarListe({ mittBrukerId, alleTopper, darkMode, aksent, onOpneProfil, onClose }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const [rader, setRader] = useState(null); // null=lastar, false=feil, [] elles
  const [sortering, setSortering] = useState("poeng"); // "poeng" | "unike" | "turar"
  useEffect(() => {
    let avbrote = false;
    if (!supabaseKlar() || !mittBrukerId) { setRader(false); return; }
    supabase.rpc("folgde_turar", { p_grense_per_person: 200 })
      .then(({ data, error }) => { if (!avbrote) setRader(error || !data ? false : data); })
      .catch(() => { if (!avbrote) setRader(false); });
    return () => { avbrote = true; };
  }, [mittBrukerId]);

  const personar = useMemo(() => {
    if (!Array.isArray(rader)) return [];
    const per = {};
    rader.forEach(r => {
      if (!per[r.bruker_id]) per[r.bruker_id] = { brukerId: r.bruker_id, visingsnamn: r.visingsnamn, profilBilde: r.profil_bilde, turar: [] };
      if (r.topp_id) per[r.bruker_id].turar.push(r);
    });
    return Object.values(per).map(p => {
      const unikeToppar = new Set(p.turar.map(x => x.topp_id)).size;
      const poeng = p.turar.reduce((sum, x) => {
        const topp = alleTopper.find(tp => String(tp.id) === String(x.topp_id));
        return sum + (topp ? poengForTopp(topp) : 0);
      }, 0);
      let høgastTopp = null;
      p.turar.forEach(x => {
        const topp = alleTopper.find(tp => String(tp.id) === String(x.topp_id));
        if (topp && (!høgastTopp || topp.høgde > høgastTopp.høgde)) høgastTopp = topp;
      });
      // Same mønster som BrukarProfil bruker for andre sine troféar — synBesøk
      // + bursdagOverride=null (elles ville MI eiga bursdagsflagg blitt testa
      // mot DEIRA turar, både feil og eit personvernsdrypp).
      const besøktIds = new Set(p.turar.map(x => String(x.topp_id)));
      const synBesøk = p.turar.map(x => ({ toppId: x.topp_id, dato: x.dato }));
      const { låst: låsteBadges } = sjekkBadges(synBesøk, besøktIds, alleTopper, null);
      return { ...p, unikeToppar, antallTurar: p.turar.length, poeng, høgastTopp, låsteBadges };
    });
  }, [rader, alleTopper]);

  const sortert = useMemo(() => {
    const kopi = [...personar];
    if (sortering === "unike") kopi.sort((a,b) => b.unikeToppar - a.unikeToppar);
    else if (sortering === "turar") kopi.sort((a,b) => b.antallTurar - a.antallTurar);
    else kopi.sort((a,b) => b.poeng - a.poeng);
    return kopi;
  }, [personar, sortering]);

  const sortKnapp = (id, tekst) => (
    <button onClick={() => setSortering(id)} style={{
      padding:"6px 12px", borderRadius:20, fontSize:12, fontWeight:"700", cursor:"pointer",
      border: sortering===id ? "none" : `1px solid ${t.border}`,
      background: sortering===id ? t.aksent : "transparent",
      color: sortering===id ? "#FFFFFF" : t.tekstMut,
    }}>{tekst}</button>
  );

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100001, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"22px 20px calc(env(safe-area-inset-bottom) + 20px)", maxHeight:"82vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ fontSize:18, fontWeight:"800", color:t.tekst }}>👥 Følgjer ({personar.length}/{MAKS_FØLGDE})</div>
          <button onClick={onClose} aria-label="Lukk" style={{ width:32, height:32, borderRadius:16, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:16, cursor:"pointer" }}>✕</button>
        </div>
        <div style={{ display:"flex", gap:6, marginBottom:14 }}>
          {sortKnapp("poeng", "Poeng")}
          {sortKnapp("unike", "Unike toppar")}
          {sortKnapp("turar", "Tal turar")}
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {rader === null ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"12px 0" }}>…</div>
          ) : rader === false ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"12px 0" }}>Klarte ikkje å hente følgjarlista.</div>
          ) : sortert.length === 0 ? (
            <div style={{ fontSize:13, color:t.tekstDim, padding:"12px 0" }}>Du følgjer ingen enno.</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              {sortert.map(p => (
                <div key={p.brukerId} onClick={() => onOpneProfil?.(p.brukerId)}
                  style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 4px", cursor:"pointer", borderBottom:`1px solid ${t.border}` }}>
                  <div style={{ width:40, height:40, borderRadius:20, overflow:"hidden", flexShrink:0, background:t.søkBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
                    {p.profilBilde ? <img src={p.profilBilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:"700", color:t.tekst, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.visingsnamn || "—"}</div>
                    <div style={{ fontSize:12, color:t.tekstMut, marginTop:1 }}>{p.unikeToppar} toppar · {p.antallTurar} turar · {p.poeng}p</div>
                    {p.høgastTopp && <div style={{ fontSize:12, color:t.tekstMut, marginTop:1 }}>⛰️ Høgaste topp: {p.høgastTopp.namn} ({p.høgastTopp.høgde} moh)</div>}
                    {p.låsteBadges && p.låsteBadges.size > 0 && (
                      <div style={{ marginTop:4 }}>
                        <BadgeRad låsteBadges={p.låsteBadges} alleTopper={alleTopper} storleik={16} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToppPanel({ topp, posisjon, hentFriskPos, besøkt, besøkCount, onRegistrer, onLukk, testModus, darkMode, aksent, harTilgang, onKrevInnlogging, onOpneProfil, navHoyde, regStil, brukarNamn }) {

  const [henter, setHenter] = useState(false);
  const [forLangt, setForLangt] = useState(null);
  // Hent fersk posisjon når panelet opnar (eller toppen byter), så avstanden stemmer.
  useEffect(() => {
    let avbrutt = false;
    setForLangt(null);
    if (hentFriskPos) { setHenter(true); hentFriskPos().finally(() => { if (!avbrutt) setHenter(false); }); }
    return () => { avbrutt = true; };
  }, [topp.id]);
  const avstand = posisjon ? haversine(posisjon.lat, posisjon.lng, topp.lat, topp.lng) : null;
  const regRadius = topp.type === "hytte" ? HYTTE_RADIUS : MATCH_RADIUS;
  // «Skriv i boka»: ny GPS-måling rett før registrering, og sjekk avstand på nytt.
  const registrerMedFriskPos = async () => {
    setForLangt(null);
    setHenter(true);
    const p = hentFriskPos ? await hentFriskPos() : null;
    setHenter(false);
    const bruk = p || posisjon;
    const d = bruk ? haversine(bruk.lat, bruk.lng, topp.lat, topp.lng) : null;
    if (!testModus && (d === null || d > regRadius)) { setForLangt(d); return; }
    onRegistrer(topp, bruk);
  };
  const kanRegistrere = avstand !== null && avstand <= regRadius;
  const poengVerdi = poengForTopp(topp);
  const listerTekst = topp.lister.map(n => `T${n}`).join(" · ");
  const a = aksent || AKSENTAR.oransje;
  const t = darkMode ? TEMA.dark(a) : TEMA.light(a);

  return (
    <div style={{ ...tp.panel, bottom: navHoyde ?? 74, background: darkMode ? "rgba(28,28,30,0.98)" : "rgba(255,255,255,0.98)", borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)" }}>
      <div style={{ width:36, height:4, borderRadius:2, background: darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"0 auto 14px" }}/>
      <div style={tp.header}>
        <div style={{ flex:1, textAlign:"center" }}>
          <div onClick={onOpneProfil} style={{ ...tp.namn, color: t.aksent, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4 }}>
            {topp.namn}<span style={{ fontSize:15, opacity:0.7 }}>›</span>
          </div>
          <div style={{ ...tp.meta, justifyContent:"center" }}>
            <span style={{ fontSize:13, color:t.tekstMut, fontWeight:"500" }}>{topp.type === "hytte" ? tr("hytte.merkelapp") : `${topp.høgde} m.o.h.`}</span>
            <span style={{ fontSize:11, color:t.tekstDim }}>·</span>
            <span style={{ fontSize:11, color:t.tekstMut }}>{listerTekst}</span>
            {besøkCount > 0 && (<span style={{ fontSize:11, color:t.aksent, background:t.aksentMut, padding:"1px 7px", borderRadius:10 }}>×{besøkCount}</span>)}
          </div>
        </div>
        <button style={{ ...tp.lukk, background: darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color: t.tekstMut }} onClick={onLukk}>✕</button>
      </div>
      <div style={{ fontSize:12, color:t.tekstDim, fontFamily:"monospace", textAlign:"center", marginBottom:10 }}>📍 {topp.lat.toFixed(5)}°N, {topp.lng.toFixed(5)}°E</div>
      <VærWidget lat={topp.lat} lng={topp.lng} namn={topp.namn} darkMode={darkMode} t={t} />
      {!posisjon && <div style={{ ...tp.ingenGps, color:t.tekstDim }}>{tr("topp.aktiverGps")}</div>}
      {posisjon && henter && <div style={{ fontSize:12, color:t.tekstDim, textAlign:"center", marginBottom:6 }}>{tr("topp.henterPos")}</div>}
      {posisjon && !henter && posisjon.acc > 25 && <div style={{ fontSize:12, color:t.tekstDim, textAlign:"center", marginBottom:6 }}>{tr("topp.gpsNoeyaktigheit").replace("{n}", String(Math.round(posisjon.acc)))}</div>}
      {forLangt !== null && <div style={{ fontSize:13, color:"#FF453A", textAlign:"center", marginBottom:8, fontWeight:"600" }}>{tr("topp.forLangtBort").replace("{n}", String(forLangt))}</div>}
      {avstand !== null && avstand <= regRadius && (<div style={{ fontSize:13, color:"#30D158", textAlign:"center", marginBottom:8, fontWeight:"600" }}>✓ {topp.type === "hytte" ? tr("hytte.framme") : tr("topp.framme")} ({avstand} m)</div>)}
      {!harTilgang ? (
        <button onClick={onKrevInnlogging} style={{ width:"100%", padding:"14px", background:t.aksent, color:"#fff", border:"none", borderRadius:14, fontSize:15, fontWeight:"700", cursor:"pointer", marginTop:4 }}>{tr("utforsk.registrerKrev")}</button>
      ) : (!posisjon) ? null : regStil === "skriv" ? (
        <SkrivIBokaKnapp onFullført={registrerMedFriskPos} poeng={poengVerdi} darkMode={darkMode} disabled={!kanRegistrere && !testModus} brukarNamn={brukarNamn} aksent={aksent} avstand={avstand} />
      ) : (<SlideKnapp onFullført={registrerMedFriskPos} poeng={poengVerdi} darkMode={darkMode} disabled={!kanRegistrere && !testModus} avstand={avstand} aksent={t.aksent} />)}
    </div>
  );
}

const tp = {
  panel: { position:"fixed", left:0, right:0, maxWidth:KOMPAKT_BREDDE, margin:"0 auto", borderRadius:"20px 20px 0 0", padding:"12px 18px 20px", zIndex:900, boxShadow:"0 -12px 40px rgba(0,0,0,0.4)", fontFamily:"-apple-system,'SF Pro Display',sans-serif" },
  header: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 },
  namn: { fontSize:24, fontWeight:"700", marginBottom:4, textAlign:"center" },
  meta: { display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" },
  badge: { fontSize:10, padding:"3px 10px", borderRadius:20, fontWeight:"600" },
  høgde: { fontSize:13, fontWeight:"500" },
  listerBadge: { fontSize:11 },
  lukk: { border:"none", borderRadius:8, width:30, height:30, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  avstand: { fontSize:15, borderRadius:12, padding:"10px 14px", marginBottom:14, textAlign:"center", fontWeight:"600" },
  ingenGps: { fontSize:12, textAlign:"center", padding:"8px 0" },
  registrert: { textAlign:"center", fontSize:13, padding:"6px 0", fontWeight:"600" },
};

// ─── INNLOGGINGSKRAV (tom fane utan konto) ───────────────────────────────────
function InnloggingsKrav({ tekst, onLoggInn, darkMode, aksent }) {
  const a = aksent || AKSENTAR.oransje;
  const t = darkMode ? TEMA.dark(a) : TEMA.light(a);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"40px 24px", gap:18, minHeight:300 }}>
      <div style={{ fontSize:44 }}>⛰</div>
      <div style={{ fontSize:15, color:t.tekstMut, maxWidth:280, lineHeight:1.6 }}>{tekst}</div>
      <button onClick={onLoggInn} style={{ padding:"14px 24px", background:t.aksent, color:"#fff", border:"none", borderRadius:14, fontSize:15, fontWeight:"700", cursor:"pointer" }}>{tr("utforsk.loggInnKnapp")}</button>
    </div>
  );
}
// Open app: alle kan lage konto. E-poststadfesting er av, så signUp gir økt
// med ein gong. Profilen lagar seg sjølv via database-trigger (visingsnamn
// les frå metadata). Feil skal aldri krasje appen.
// Lukka beta: registrering krev ein invitasjonskode du deler ut. NB: dette er
// MJUK sperre — koden ligg i klientkoden og kan teknisk finnast av kyndige. Det
// hindrar tilfeldige, ikkje motiverte. For å opne appen for alle seinare: sett
// KREV_INVITASJON = false (då blir feltet borte og alle kan registrere seg).
const KREV_INVITASJON = true;
const INVITASJONSKODAR = ["ulvanosi"]; // legg til fleire ved behov, små bokstavar
// Lukka beta: gøym HEILE appen bak innlogging (ingen fri utforsking av kartet).
// Sett false for å opne att den utforskande modusen (kart synleg utan konto).
const BETA_LUKKA = true;
// Testknappen er gøymd til ein skriv inn denne koden (via logo-trippeltrykk).
// Berre dei du gir koden til ser testmodus. Endre fritt. Lagra per eining.
// FJERNA: TEST_KODE/einings-basert opplåsing er bytt ut med rein
// kontobasert tilgang (harTestSuffiks, sjå App()) — sjå fjerninga under.
// Admin-modus (rediger/flytt toppar i kartet) skal berre vere synleg for deg.
// MJUK sperre — same fyrehald som over: skjuler UI-et, endrar ikkje RLS. Trygt
// nok sidan admin-endringar berre skriv til lokal localStorage (peaks.js-eksport),
// aldri til Supabase. Legg til fleire e-postar her ved behov, små bokstavar.
const ADMIN_EPOSTAR = ["sfauske@gmail.com", "sfauske+test@gmail.com"];

function AuthSkjerm({ darkMode, aksent, onClose }) {
  const [modus, setModus] = useState("inn"); // "inn" | "reg" | "glemt"
  const [glemtSteg, setGlemtSteg] = useState("epost"); // "epost" | "kode"
  const [epost, setEpost] = useState("");
  const [passord, setPassord] = useState("");
  const [visingsnamn, setVisingsnamn] = useState("");
  const [invKode, setInvKode] = useState("");
  const [kode, setKode] = useState("");
  const [feil, setFeil] = useState("");
  const [lastar, setLastar] = useState(false);
  const a = aksent || AKSENTAR.oransje;
  const t = darkMode ? TEMA.dark(a) : TEMA.light(a);

  const erReg = modus === "reg";
  const erGlemt = modus === "glemt";

  const inputStil = (markerFeil) => ({ padding:"14px", background:t.søkBg, border:`1px solid ${markerFeil && feil ? t.aksent : t.border}`, borderRadius:12, color:t.tekst, fontSize:15, outline:"none" });
  const knappStil = { padding:"14px", background:lastar ? "#1A7A3A" : t.aksent, color:"#fff", border:"none", borderRadius:12, fontSize:15, fontWeight:"700", cursor:lastar?"not-allowed":"pointer", transition:"all 0.3s" };

  const submit = async () => {
    setFeil("");
    if (!supabaseKlar()) { setFeil(tr("auth.feilBackend")); return; }
    if (!epost.includes("@") || epost.length < 5) { setFeil(tr("auth.feilEpostUgyldig")); return; }
    if (passord.length < 6) { setFeil(tr("auth.feilPassordKort")); return; }
    if (erReg && !visingsnamn.trim()) { setFeil(tr("auth.feilNamnManglar")); return; }
    if (erReg && KREV_INVITASJON && !INVITASJONSKODAR.includes(invKode.trim().toLowerCase())) { setFeil(tr("auth.feilInvkode")); return; }
    setLastar(true);
    try {
      if (erReg) {
        const { error } = await supabase.auth.signUp({
          email: epost.trim(),
          password: passord,
          options: { data: { visingsnamn: visingsnamn.trim() } },
        });
        if (error) {
          const m = (error.message || "").toLowerCase();
          if (m.includes("already") || m.includes("registered")) setFeil(tr("auth.feilEpostFinst"));
          else if (m.includes("password")) setFeil(tr("auth.feilPassordKort"));
          else setFeil(tr("auth.feilGenerell"));
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: epost.trim(),
          password: passord,
        });
        if (error) setFeil(tr("auth.feilInnlogging"));
      }
    } catch (e) {
      setFeil(tr("auth.feilGenerell"));
    }
    setLastar(false);
  };

  // Steg 1: send kode. Vis ALLTID «sjekk e-posten» (ikkje røp om kontoen finst).
  const sendKode = async () => {
    setFeil("");
    if (!supabaseKlar()) { setFeil(tr("auth.feilBackend")); return; }
    if (!epost.includes("@") || epost.length < 5) { setFeil(tr("auth.feilEpostUgyldig")); return; }
    setLastar(true);
    try { await supabase.auth.resetPasswordForEmail(epost.trim()); } catch (e) {}
    setLastar(false);
    setGlemtSteg("kode"); // uansett resultat — unngå konto-kartlegging
  };

  // Steg 2: stadfest kode + sett nytt passord. verifyOtp FØR updateUser.
  const settNyttPassord = async () => {
    setFeil("");
    if (!supabaseKlar()) { setFeil(tr("auth.feilBackend")); return; }
    if (kode.trim().length < 6) { setFeil(tr("auth.feilKode")); return; }
    if (passord.length < 6) { setFeil(tr("auth.feilPassordKort")); return; }
    setLastar(true);
    try {
      const { error: vErr } = await supabase.auth.verifyOtp({
        email: epost.trim(), token: kode.trim(), type: "recovery",
      });
      if (vErr) { setFeil(tr("auth.feilKode")); setLastar(false); return; }
      // Innlogga via recovery-økt → sett nytt passord
      const { error: uErr } = await supabase.auth.updateUser({ password: passord });
      if (uErr) { setFeil(tr("auth.feilGenerell")); setLastar(false); return; }
      // Suksess: onAuthStateChange i App lukkar overlayen (brukar er innlogga)
    } catch (e) {
      setFeil(tr("auth.feilGenerell")); setLastar(false);
    }
  };

  const tittel = erGlemt ? tr("auth.glemtTittel") : erReg ? tr("auth.registrerTittel") : tr("auth.loggInnTittel");

  return (
    <div style={{ position:"fixed", inset:0, zIndex:99999, background:t.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 24px", textAlign:"center", fontFamily:"-apple-system,'SF Pro Display',sans-serif" }}>
      {onClose && (
        <button onClick={onClose} aria-label="Lukk" style={{ position:"absolute", top:"calc(env(safe-area-inset-top) + 16px)", right:20, width:40, height:40, borderRadius:20, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:20, cursor:"pointer" }}>✕</button>
      )}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:40 }}>
        <img src="/icons/icon-512.png" alt="" style={{ width:32, height:32, borderRadius:8, flexShrink:0 }} />
        <span style={{ fontFamily:"'Georgia',serif", fontSize:22, fontWeight:"bold", color:t.tekst }}>{tr("inn.tittel")}</span>
      </div>
      <div style={{ fontSize:28, fontWeight:"700", color:t.tekst, marginBottom: erGlemt ? 12 : 24 }}>{tittel}</div>

      {erGlemt && glemtSteg === "epost" && (
        <>
          <div style={{ fontSize:14, color:t.tekstMut, marginBottom:24, maxWidth:300, lineHeight:1.5 }}>{tr("auth.glemtEpostInfo")}</div>
          <div style={{ width:"100%", maxWidth:320, display:"flex", flexDirection:"column", gap:10 }}>
            <input type="email" autoComplete="email" value={epost} onChange={e => { setEpost(e.target.value); setFeil(""); }} onKeyDown={e => e.key==="Enter" && !lastar && sendKode()} placeholder={tr("auth.epostPlassh")} style={inputStil(true)}/>
            {feil && <div style={{ fontSize:13, color:t.aksent, fontWeight:"500" }}>{feil}</div>}
            <button onClick={sendKode} disabled={lastar} style={knappStil}>{lastar ? tr("auth.sender") : tr("auth.sendKode")}</button>
          </div>
        </>
      )}

      {erGlemt && glemtSteg === "kode" && (
        <>
          <div style={{ fontSize:18, fontWeight:"700", color:t.tekst, marginBottom:6 }}>{tr("auth.kodeSendtTittel")}</div>
          <div style={{ fontSize:14, color:t.tekstMut, marginBottom:24, maxWidth:300, lineHeight:1.5 }}>{tr("auth.kodeInfo")}</div>
          <div style={{ width:"100%", maxWidth:320, display:"flex", flexDirection:"column", gap:10 }}>
            {/* Synleg, skrivebeskytta e-post = username-felt. Lèt iOS-nøkkelringen pare det
                nye passordet med rett konto. Utan dette lagrar iOS det «laust» → autofyll
                på innlogging finn det ikkje. Visar dessutan kva konto ein tilbakestiller for. */}
            <input type="email" name="username" autoComplete="username" value={epost} readOnly
              style={{ ...inputStil(false), color:t.tekstMut, background:"transparent", textAlign:"center" }}/>
            <input inputMode="numeric" autoComplete="one-time-code" value={kode} onChange={e => { setKode(e.target.value.replace(/\D/g,"").slice(0,8)); setFeil(""); }} placeholder={tr("auth.kodePlassh")} maxLength={8}
              style={{ ...inputStil(true), textAlign:"center", letterSpacing:6, fontSize:20, fontFamily:"monospace" }}/>
            <input type="password" autoComplete="new-password" value={passord} onChange={e => { setPassord(e.target.value); setFeil(""); }} onKeyDown={e => e.key==="Enter" && !lastar && settNyttPassord()} placeholder={tr("auth.nyttPassord")} style={inputStil(true)}/>
            {feil && <div style={{ fontSize:13, color:t.aksent, fontWeight:"500" }}>{feil}</div>}
            <button onClick={settNyttPassord} disabled={lastar} style={knappStil}>{lastar ? tr("auth.oppdaterer") : tr("auth.settPassord")}</button>
          </div>
        </>
      )}

      {!erGlemt && (
        <div style={{ width:"100%", maxWidth:320, display:"flex", flexDirection:"column", gap:10 }}>
          {erReg && (
            <input value={visingsnamn} onChange={e => { setVisingsnamn(e.target.value); setFeil(""); }} placeholder={tr("auth.namnPlassh")} maxLength={40} style={inputStil(false)}/>
          )}
          {erReg && KREV_INVITASJON && (
            <input value={invKode} onChange={e => { setInvKode(e.target.value); setFeil(""); }} placeholder={tr("auth.invkodePlassh")} maxLength={40} style={inputStil(true)}/>
          )}
          <input type="email" autoComplete={erReg ? "email" : "username"} value={epost} onChange={e => { setEpost(e.target.value); setFeil(""); }} placeholder={tr("auth.epostPlassh")} style={inputStil(true)}/>
          <input type="password" autoComplete={erReg ? "new-password" : "current-password"} value={passord} onChange={e => { setPassord(e.target.value); setFeil(""); }} onKeyDown={e => e.key === "Enter" && !lastar && submit()} placeholder={tr("auth.passordPlassh")} style={inputStil(true)}/>
          {feil && <div style={{ fontSize:13, color:t.aksent, fontWeight:"500" }}>{feil}</div>}
          <button onClick={submit} disabled={lastar} style={knappStil}>
            {lastar ? (erReg ? tr("auth.registrerer") : tr("auth.loggerInn")) : (erReg ? tr("auth.registrerKnapp") : tr("auth.loggInnKnapp"))}
          </button>
          {!erReg && (
            <div onClick={() => { setModus("glemt"); setGlemtSteg("epost"); setFeil(""); setPassord(""); }} style={{ marginTop:4, fontSize:13, color:t.tekstMut, cursor:"pointer" }}>{tr("auth.glemtLenke")}</div>
          )}
        </div>
      )}

      {erGlemt ? (
        <div onClick={() => { setModus("inn"); setFeil(""); setKode(""); setPassord(""); }} style={{ marginTop:24, fontSize:13, color:t.tekstMut, cursor:"pointer" }}>{tr("auth.tilbakeInnlogging")}</div>
      ) : (
        <div onClick={() => { setModus(erReg ? "inn" : "reg"); setFeil(""); }} style={{ marginTop:24, fontSize:13, color:t.tekstMut, cursor:"pointer", textDecoration:"underline" }}>
          {erReg ? tr("auth.byttTilInnlogging") : tr("auth.byttTilRegistrer")}
        </div>
      )}
    </div>
  );
}

// ─── STATISTIKK PANEL ────────────────────────────────────────────────────────
function StatistikkPanel({ besøk, alleTopper, besøktIds, visStatistikk, setVisStatistikk, darkMode, aksent, onVisMineHovudbilete, delerStatistikk, onSetDelStatistikk }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const sortert = [...besøk].sort((a,b) => new Date(a.dato) - new Date(b.dato));
  const første = sortert[0];
  const siste = sortert[sortert.length - 1];
  const dagSidan = Math.floor((Date.now() - new Date(siste.dato)) / 86400000);
  const høgaste = besøk.reduce((best, b) => b.høgde > (best?.høgde ?? 0) ? b : best, null);
  const totalHøgde = besøk.reduce((sum, b) => sum + (b.høgde || 0), 0);
  const dagMap = {};
  besøk.forEach(b => { const d = new Date(b.dato).toDateString(); dagMap[d] = (dagMap[d] || 0) + 1; });
  const aktiveDagar = Object.keys(dagMap).length;
  const besteDag = Object.entries(dagMap).sort((a,b) => b[1]-a[1])[0];
  const tidFordeling = { morgon:0, dag:0, kveld:0, natt:0 };
  besøk.forEach(b => {
    const h = new Date(b.dato).getHours();
    if (h >= 5 && h < 10) tidFordeling.morgon++;
    else if (h >= 10 && h < 17) tidFordeling.dag++;
    else if (h >= 17 && h < 21) tidFordeling.kveld++;
    else tidFordeling.natt++;
  });
  const favoritttid = Object.entries(tidFordeling).sort((a,b) => b[1]-a[1])[0];
  const tidnamn = { morgon:"🌅 Morgon (05-10)", dag:"☀️ Dag (10-17)", kveld:"🌇 Kveld (17-21)", natt:"🌙 Natt (21-05)" };
  const toppCount = {};
  besøk.forEach(b => { if (!toppCount[b.toppId]) toppCount[b.toppId] = { count:0, namn:b.namn, høgde:b.høgde||0 }; toppCount[b.toppId].count++; });
  const favorittTopp = Object.values(toppCount).sort((a,b) => b.count !== a.count ? b.count-a.count : b.høgde-a.høgde)[0];
  const talToppbilde = besøk.filter(b => b.harVoreHovudbilde).length;
  const talGjeldandeHovudbilete = besøk.filter(b => b.erHovudbilde).length;
  // Tal ulike kommunar besøkt — minst éin besøkt topp i kommunen tel.
  const talKommunar = alleTopper && besøktIds
    ? new Set(alleTopper.filter(p => besøktIds.has(p.id) && p.kommune).map(p => normTekst(p.kommune))).size
    : 0;
  // Tal topplister fullført — summen av kor mange kommunar som er fullført
  // på tvers av ALLE nivå (Topp 10/20/50/100), veks etter kvart som fleire
  // vert fullført. Gjenbruker same utrekning som Trofear-fana.
  const talListerFullført = alleTopper && besøktIds
    ? genererListeBadges(alleTopper, besøktIds).reduce((sum, b) => sum + (b.kommunar?.length || 0), 0)
    : 0;

  const statRad = (ikon, label, verdi, sub, onClick) => (
    <div key={label} onClick={onClick} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${t.border}`, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:18 }}>{ikon}</span>
        <div>
          <div style={{ fontSize:13, color:t.tekst, fontWeight:"600" }}>{label}{onClick && <span style={{ marginLeft:5, color:aksent.aksent }}>→</span>}</div>
          {sub && <div style={{ fontSize:11, color:t.tekstMut, marginTop:1 }}>{sub}</div>}
        </div>
      </div>
      <div style={{ fontSize:14, fontWeight:"700", color:aksent.aksent, fontFamily:"monospace", textAlign:"right" }}>{verdi}</div>
    </div>
  );

  return (
    <div style={{ marginBottom:16 }}>
      <button onClick={() => setVisStatistikk(v => !v)}
        style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius: visStatistikk ? "12px 12px 0 0" : 12, cursor:"pointer", marginBottom: visStatistikk ? 0 : 12 }}>
        <span style={{ fontSize:14, fontWeight:"700", color:t.tekst }}>{tr("stat.tittel")}</span>
        <span style={{ fontSize:16, color:t.tekstMut, transform: visStatistikk ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
      </button>
      {visStatistikk && (
        <div style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderTop:"none", borderRadius:"0 0 12px 12px", padding:"0 16px 8px" }}>
          {statRad("📅", tr("stat.forsteTur"), new Date(første.dato).toLocaleDateString("nb-NO", { day:"2-digit", month:"short", year:"numeric" }))}
          {statRad("🕐", tr("stat.sisteTur"), dagSidan === 0 ? tr("stat.idag") : dagSidan === 1 ? tr("stat.igar") : tr("stat.dagarSidan",{n:dagSidan}), new Date(siste.dato).toLocaleDateString("nb-NO", { day:"2-digit", month:"short", year:"numeric" }))}
          {statRad("📆", tr("stat.aktiveDagar"), tr("stat.dagar",{n:aktiveDagar}))}
          {besteDag && statRad("🔥", tr("stat.rekorddag"), tr("stat.toppar",{n:besteDag[1]}), new Date(besteDag[0]).toLocaleDateString("nb-NO", { day:"2-digit", month:"short", year:"numeric" }))}
          {høgaste && statRad("⛰", tr("stat.hogasteTopp"), `${høgaste.høgde} m`, høgaste.namn)}
          {statRad("📈", tr("stat.totalHogdemeter"), `${(totalHøgde/1000).toFixed(1)} km`)}
          {talKommunar > 0 && statRad("🗺", tr("stat.kommunarBesokt"), `${talKommunar}`)}
          {talListerFullført > 0 && statRad("🏆", tr("stat.listerFullfort"), `${talListerFullført}`)}
          {favorittTopp && favorittTopp.count > 1 && statRad("❤️", tr("stat.favoritttopp"), tr("stat.favorittBesokt",{n:favorittTopp.count}), favorittTopp.namn)}
          {statRad("⏰", tr("stat.favoritttid"), tidnamn[favoritttid[0]], `${favoritttid[1]} av ${besøk.length} ${besøk.length === 1 ? tr("hb.turEitt") : tr("hb.turFleire")}`)}
          {talToppbilde > 0 && statRad("📸", tr("hb.statLabel"), `${talToppbilde}`, tr("hb.statSub"))}
          {talGjeldandeHovudbilete > 0 && statRad("🖼️", tr("hb.gjeldandeLabel"), `${talGjeldandeHovudbilete}`, tr("hb.gjeldandeSub"), onVisMineHovudbilete)}
          {onSetDelStatistikk && (
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderTop:`1px solid ${t.border}`, marginTop:6 }}>
              <div>
                <div style={{ fontSize:14, fontWeight:"600", color:t.tekst }}>🔒 {tr("innst.delStatistikk")}</div>
                <div style={{ fontSize:11, color:t.tekstMut, marginTop:1 }}>{tr("innst.delStatistikkSkildring")}</div>
              </div>
              <div onClick={() => onSetDelStatistikk(!delerStatistikk)} style={{ width:44, height:26, borderRadius:14, cursor:"pointer", background: delerStatistikk ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s", flexShrink:0, marginLeft:10 }}>
                <div style={{ position:"absolute", top:2, left: delerStatistikk ? 20 : 2, width:22, height:22, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



const MAANADER = ["januar","februar","mars","april","mai","juni","juli","august","september","oktober","november","desember"];

// Komprimerer eit bilete til eit kvadratisk JPEG (canvas), maks `storleik` px per side.
// Fungerer for HEIC òg — Safari dekodar HEIC til <img>, canvas eksporterer alltid JPEG.
function komprimerBilete(file, storleik = 400) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const kant = Math.min(img.width, img.height);
      const sx = (img.width - kant) / 2;
      const sy = (img.height - kant) / 2;
      const canvas = document.createElement("canvas");
      canvas.width = storleik;
      canvas.height = storleik;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, kant, kant, 0, 0, storleik, storleik);
      canvas.toBlob(blob => {
        if (blob) resolve(blob); else reject(new Error("Kompresjon feila"));
      }, "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Kunne ikkje lese biletet")); };
    img.src = url;
  });
}

function ProfilModal({ darkMode, aksent, brukerId, profil, onLagra, onClose, låsteBadges, alleTopper, onVisTrofear, talToppbilde, talGjeldandeHovudbilete, onVisMineHovudbilete, onOpneBrukar }) {
  const a = aksent || AKSENTAR.oransje;
  const t = darkMode ? TEMA.dark(a) : TEMA.light(a);
  const [visFølgjarListe, setVisFølgjarListe] = useState(false);
  const [visingsnamn, setVisingsnamn] = useState(profil?.visingsnamn || "");
  const [bosted, setBosted] = useState(profil?.adresse || "");
  const _b = (profil?.bursdag || "").split("-"); // lagra som "MM-DD"
  const [mnd, setMnd] = useState(_b.length === 2 ? parseInt(_b[0], 10) : 0);
  const [dag, setDag] = useState(_b.length === 2 ? parseInt(_b[1], 10) : 0);
  const [feil, setFeil] = useState("");
  const [lastar, setLastar] = useState(false);

  const [bileteUrl, setBileteUrl] = useState(profil?.bilde_url || null);
  const [lastarBilete, setLastarBilete] = useState(false);
  const [biletFeil, setBiletFeil] = useState("");
  const fileRef = useRef(null);

  const felt = { padding:"14px", background:t.søkBg, border:`1px solid ${t.border}`, borderRadius:12, color:t.tekst, fontSize:15, outline:"none", width:"100%", boxSizing:"border-box" };
  const sel = { ...felt, flex:1, width:"auto" };
  const merkelapp = { fontSize:13, fontWeight:"600", color:t.tekst, marginBottom:6, textAlign:"left" };
  const privat = { fontSize:11, color:t.tekstMut, marginTop:4, textAlign:"left" };

  const velBilete = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBiletFeil("");
    if (!supabaseKlar()) { setBiletFeil(tr("auth.feilBackend")); return; }
    setLastarBilete(true);
    try {
      const blob = await komprimerBilete(file, 400);
      const sti = `${brukerId}/avatar.jpg`;
      const { error: uploadErr } = await supabase.storage.from("profilbilete").upload(sti, blob, { upsert: true, contentType: "image/jpeg" });
      if (uploadErr) throw uploadErr;
      const { data } = supabase.storage.from("profilbilete").getPublicUrl(sti);
      const url = `${data.publicUrl}?v=${Date.now()}`;
      const { error: dbErr } = await supabase.from("profiles").update({ bilde_url: url }).eq("id", brukerId);
      if (dbErr) throw dbErr;
      setBileteUrl(url);
      onLagra({ ...(profil || {}), bilde_url: url });
    } catch (err) {
      setBiletFeil(tr("profil.biletFeil"));
    } finally {
      setLastarBilete(false);
    }
  };

  const lagra = async () => {
    setFeil("");
    if (!visingsnamn.trim()) { setFeil(tr("profil.feilNamn")); return; }
    if (!supabaseKlar()) { setFeil(tr("auth.feilBackend")); return; }
    const bursdag = (mnd > 0 && dag > 0) ? `${String(mnd).padStart(2,"0")}-${String(dag).padStart(2,"0")}` : null;
    const oppdat = { visingsnamn: visingsnamn.trim(), adresse: bosted.trim() || null, bursdag };
    setLastar(true);
    try {
      const { error } = await supabase.from("profiles").update(oppdat).eq("id", brukerId);
      if (error) { setFeil(tr("profil.feilLagra")); setLastar(false); return; }
      onLagra({ ...(profil || {}), ...oppdat });
      onClose();
    } catch (e) { setFeil(tr("profil.feilLagra")); setLastar(false); }
  };

  return (
    <>
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:99999, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:KOMPAKT_BREDDE, background:t.bg, borderRadius:"20px 20px 0 0", padding:"24px 22px calc(env(safe-area-inset-bottom) + 24px)", maxHeight:"90vh", overflowY:"auto", fontFamily:"-apple-system,'SF Pro Display',sans-serif" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontSize:20, fontWeight:"700", color:t.tekst }}>{tr("profil.tittel")}</div>
          <button onClick={onClose} aria-label="Lukk" style={{ width:34, height:34, borderRadius:17, border:"none", background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.06)", color:t.tekstMut, fontSize:17, cursor:"pointer" }}>✕</button>
        </div>

        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginBottom:20 }}>
          <div style={{ position:"relative" }}>
            <div onClick={() => !lastarBilete && fileRef.current?.click()} style={{
              width:88, height:88, borderRadius:44, overflow:"hidden", cursor:lastarBilete?"default":"pointer",
              background:t.søkBg, border:`1px solid ${t.border}`, display:"flex", alignItems:"center", justifyContent:"center",
              opacity: lastarBilete ? 0.6 : 1,
            }}>
              {bileteUrl
                ? <img src={bileteUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                : <span style={{ fontSize:32 }}>👤</span>}
            </div>
            <button onClick={() => !lastarBilete && fileRef.current?.click()} disabled={lastarBilete}
              aria-label={tr("profil.biletVel")}
              style={{ position:"absolute", bottom:-2, right:-2, width:28, height:28, borderRadius:14, border:`2px solid ${t.bg}`, background:t.aksent, color:"#fff", fontSize:13, cursor:lastarBilete?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {lastarBilete ? "⟳" : "✏️"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={velBilete} style={{ display:"none" }}/>
          </div>
          {biletFeil && <div style={{ fontSize:12, color:t.aksent, fontWeight:"500", marginTop:8, textAlign:"center" }}>{biletFeil}</div>}
          {låsteBadges && låsteBadges.size > 0 && (
            <div onClick={onVisTrofear} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, marginTop:12, cursor: onVisTrofear ? "pointer" : "default" }}>
              <div style={{ color:t.tekst }}>
                <BadgeRad låsteBadges={låsteBadges} alleTopper={alleTopper} storleik={24} />
              </div>
              {onVisTrofear && <div style={{ fontSize:11, color:t.aksent, fontWeight:"600" }}>{tr("hb.seAlleTrofeer")}</div>}
            </div>
          )}
          {talToppbilde > 0 && (
            <div style={{ marginTop:10, textAlign:"center", fontSize:12, color:t.tekstMut }}>
              {tr("hb.profilStat", { n: talToppbilde })}
            </div>
          )}
          {talGjeldandeHovudbilete > 0 && (
            <div onClick={onVisMineHovudbilete} style={{ marginTop:6, textAlign:"center", fontSize:12, color:t.aksent, fontWeight:"600", cursor: onVisMineHovudbilete ? "pointer" : "default" }}>
              🖼️ {tr("hb.gjeldandeLabel")}: {talGjeldandeHovudbilete} →
            </div>
          )}
          <div onClick={() => setVisFølgjarListe(true)} style={{ marginTop:10, textAlign:"center", fontSize:12, color:t.aksent, fontWeight:"600", cursor:"pointer" }}>
            👥 Følgjer →
          </div>
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={merkelapp}>{tr("profil.namn")}</div>
          <input value={visingsnamn} onChange={e => { setVisingsnamn(e.target.value); setFeil(""); }} placeholder={tr("auth.namnPlassh")} maxLength={40} style={felt}/>
          <div style={privat}>{tr("profil.synlegAndre")}</div>
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={merkelapp}>{tr("profil.bosted")}</div>
          <input value={bosted} onChange={e => setBosted(e.target.value)} placeholder={tr("profil.bostedPlassh")} maxLength={60} style={felt}/>
          <div style={privat}>{tr("profil.synlegAndre")}</div>
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={merkelapp}>{tr("profil.bursdag")}</div>
          <div style={{ display:"flex", gap:10 }}>
            <select value={dag} onChange={e => setDag(parseInt(e.target.value,10))} style={sel}>
              <option value={0}>{tr("profil.dag")}</option>
              {Array.from({length:31}, (_,i) => i+1).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={mnd} onChange={e => setMnd(parseInt(e.target.value,10))} style={{ ...sel, flex:2 }}>
              <option value={0}>{tr("profil.maaned")}</option>
              {MAANADER.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div style={privat}>{tr("profil.berreDu")}</div>
        </div>

        {profil?.epost && (
          <div style={{ marginBottom:18 }}>
            <div style={merkelapp}>{tr("profil.epost")}</div>
            <div style={{ ...felt, color:t.tekstMut, background:"transparent" }}>{profil.epost}</div>
            <div style={privat}>{tr("profil.berreDu")}</div>
          </div>
        )}

        {feil && <div style={{ fontSize:13, color:t.aksent, fontWeight:"500", marginBottom:12, textAlign:"center" }}>{feil}</div>}
        <button onClick={lagra} disabled={lastar} style={{ width:"100%", padding:"15px", background:lastar?"#1A7A3A":t.aksent, color:"#fff", border:"none", borderRadius:12, fontSize:15, fontWeight:"700", cursor:lastar?"not-allowed":"pointer" }}>
          {lastar ? tr("profil.lagrar") : tr("profil.lagra")}
        </button>
      </div>
    </div>
    {visFølgjarListe && (
      <FølgjarListe mittBrukerId={brukerId} alleTopper={alleTopper} darkMode={darkMode} aksent={aksent}
        onOpneProfil={(bid) => { setVisFølgjarListe(false); onOpneBrukar?.(bid); }}
        onClose={() => setVisFølgjarListe(false)} />
    )}
    </>
  );
}

// ─── MÅLESTOKK ───────────────────────────────────────────────────────────────
function Målestokk({ flyTilRef, darkMode }) {
  const [breidde, setBreidde] = useState(100);
  const [label, setLabel] = useState("100 m");

  useEffect(() => {
    // Berekn målestokk frå kartet kvar 500ms
    const oppdater = () => {
      const map = window._mlMap;
      if (!map) return;
      const canvas = map.getCanvas();
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      // To punkt 100px frå kvarandre på midten av kartet
      const p1 = map.unproject([w/2 - 50, h/2]);
      const p2 = map.unproject([w/2 + 50, h/2]);
      // Haversine-avstand
      const R = 6371000;
      const dLat = (p2.lat - p1.lat) * Math.PI / 180;
      const dLon = (p2.lng - p1.lng) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(p1.lat*Math.PI/180)*Math.cos(p2.lat*Math.PI/180)*Math.sin(dLon/2)**2;
      const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      // Rund av til næraste "fine" tal
      const fine = [5,10,20,50,100,200,500,1000,2000,5000,10000];
      const target = dist;
      const nearest = fine.reduce((a,b) => Math.abs(b-target) < Math.abs(a-target) ? b : a);
      const px = Math.min(120, Math.round(100 * nearest / dist));
      setBreidde(px);
      setLabel(nearest >= 1000 ? `${nearest/1000} km` : `${nearest} m`);
    };
    oppdater();
    const iv = setInterval(oppdater, 1000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
      <div style={{
        width: breidde, height:3,
        background: darkMode ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.65)",
        borderLeft:`2px solid ${darkMode?"rgba(0,0,0,0.65)":"rgba(0,0,0,0.65)"}`,
        borderRight:`2px solid ${darkMode?"rgba(0,0,0,0.65)":"rgba(0,0,0,0.65)"}`,
      }}/>
      <div style={{
        fontSize:11, fontWeight:"700",
        color: darkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.75)",
        marginTop:2,
        textShadow: darkMode ? "0 1px 3px rgba(0,0,0,0.8)" : "0 1px 3px rgba(255,255,255,0.9)",
      }}>
        {label}
      </div>
    </div>
  );
}


// ─── BILDEKOMPRIMERING ───────────────────────────────────────────────────────
function komprimer(fil, cb, onFeil) {
  const reader = new FileReader();
  reader.onerror = () => onFeil && onFeil();
  reader.onload = e => {
    const img = new Image();
    img.onerror = () => onFeil && onFeil(); // t.d. korrupt eller uleseleg format
    img.onload = () => {
      try {
        const maks = 900;
        let w = img.width, h = img.height;
        if (!w || !h) { onFeil && onFeil(); return; }
        if (w > maks || h > maks) {
          if (w > h) { h = Math.round(h * maks / w); w = maks; }
          else       { w = Math.round(w * maks / h); h = maks; }
        }
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        cb(c.toDataURL("image/jpeg", 0.5)); // litt høgare kvalitet enn før (0.4)
      } catch (err) { onFeil && onFeil(); }
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(fil);
}

// ─── BILDE MODAL ─────────────────────────────────────────────────────────────
function BildeModal({ toppNamn, onBilde, onHopp, darkMode, aksent }) {
  const filRef = useRef(null);
  // FIX: hovudbilete-verifisering krev å vite OM biletet vart teke med kamera
  // akkurat no, eller henta frå biblioteket. capture-attributtet på inputen
  // fortel oss det i augeblikket knappen vert trykt — men den informasjonen
  // forsvinn elles innan onChange fyrer. Ein ref overlever mellom dei to.
  const kjelde = useRef("bibliotek");
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);

  const handterFil = (e) => {
    const fil = e.target.files?.[0];
    e.target.value = ""; // nullstill så same fil kan veljast på nytt
    if (!fil) return;
    // HANDHEVING: berre bilete. accept="image/*" er berre eit hint på desktop —
    // her avviser vi alt som ikkje har image-MIME (t.d. PDF, dokument, video).
    if (!fil.type || !fil.type.startsWith("image/")) {
      alert(tr("bilde.berreBilete"));
      return;
    }
    if (fil.size > 25 * 1024 * 1024) { // ekstremt store filer (>25 MB) — avvis
      alert(tr("bilde.forStort"));
      return;
    }
    komprimer(fil, (base64) => onBilde(base64, kjelde.current), () => alert(tr("bilde.feilLes")));
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9998,
      background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
      fontFamily:"-apple-system,'SF Pro Display',sans-serif",
    }}>
      <div style={{
        background:t.bgCard, borderRadius:"20px 20px 0 0",
        padding:"20px 20px calc(32px + env(safe-area-inset-bottom))",
        width:"100%", maxWidth:KOMPAKT_BREDDE,
        boxShadow:"0 -8px 32px rgba(0,0,0,0.3)",
      }}>
        {/* Drag handle */}
        <div style={{ width:36, height:4, borderRadius:2, background:darkMode?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", margin:"0 auto 20px" }}/>

        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:36, marginBottom:8 }}>📸</div>
          <div style={{ fontSize:17, fontWeight:"700", color:t.tekst, marginBottom:4 }}>
            {tr("bilde.tittel")}
          </div>
          <div style={{ fontSize:13, color:t.tekstMut }}>
            {tr("bilde.dokumenter", { namn: toppNamn })}
          </div>
        </div>

        {/* Skjult fil-input */}
        <input ref={filRef} type="file" accept="image/png,image/jpeg,image/jpg,image/heic,image/heif,image/webp,image/*" style={{ display:"none" }} onChange={handterFil} />

        {/* Ta bilde */}
        <button
          onClick={() => { kjelde.current = "kamera"; filRef.current.setAttribute("capture","environment"); filRef.current.click(); }}
          style={{
            width:"100%", padding:"14px", marginBottom:10,
            background:aksent.aksent, color:"#fff",
            border:"none", borderRadius:14,
            fontSize:15, fontWeight:"700", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
          <span style={{ fontSize:20 }}>📷</span> {tr("bilde.taBilde")}
        </button>

        {/* Vel frå bibliotek */}
        <button
          onClick={() => { kjelde.current = "bibliotek"; filRef.current.removeAttribute("capture"); filRef.current.click(); }}
          style={{
            width:"100%", padding:"14px", marginBottom:10,
            background:darkMode?"#3A3A3C":"#F2F2F7", color:t.tekst,
            border:"none", borderRadius:14,
            fontSize:15, fontWeight:"600", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
          <span style={{ fontSize:20 }}>🖼</span> {tr("bilde.velBibliotek")}
        </button>

        {/* Hopp over */}
        <button
          onClick={onHopp}
          style={{
            width:"100%", padding:"12px",
            background:"none", color:t.tekstMut,
            border:"none", fontSize:14, cursor:"pointer",
          }}>
          Hopp over
        </button>
      </div>
    </div>
  );
}

// ─── FØRSTE-BILETE / UTFORDRE-POPUP ────────────────────────────────────────
// Vist rett etter ein vellukka kamera-opplasting, i to ulike modusar (sjekka
// via hovudbilde_status-RPC): "forste" (ingen eigar enno) eller "utfordre"
// (denne brukaren er leiar på besøkstal, men eig ikkje hovudbiletet). Reint
// opt-in — brukaren kan alltid seie nei, og kan seinare fjerne hovudbilete-
// statusen ved å slette biletet (jf. slettToppbilete, som nullstiller er_hovudbilde).
function FørsteBildePopup({ modus = "forste", toppNamn, darkMode, aksent, onJa, onNei }) {
  const t = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
  const erUtfordring = modus === "utfordre";
  return (
    <div style={{ position:"fixed", inset:0, zIndex:100002, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ background:t.bgCard, borderRadius:20, padding:"28px 24px", maxWidth:340, width:"100%", textAlign:"center", boxShadow:"0 12px 40px rgba(0,0,0,0.4)" }}>
        <div style={{ fontSize:40, marginBottom:12 }}>{erUtfordring ? "👑" : "🎉"}</div>
        <div style={{ fontSize:17, fontWeight:"800", color:t.tekst, marginBottom:8 }}>
          {erUtfordring ? tr("hb.duLeiarTittel") : tr("hb.gratulerer")}
        </div>
        <div style={{ fontSize:14, color:t.tekstMut, marginBottom:22, lineHeight:1.5 }}>
          {erUtfordring
            ? tr("hb.utfordreSporsmal", { namn: toppNamn })
            : tr("hb.forsteBildeSporsmal", { namn: toppNamn })}
        </div>
        <button onClick={onJa} style={{ width:"100%", padding:14, marginBottom:10, background:t.aksent, color:"#fff", border:"none", borderRadius:12, fontSize:15, fontWeight:"700", cursor:"pointer" }}>
          {erUtfordring ? tr("hb.jaOvertaHovudbilde") : tr("hb.jaSettHovudbilde")}
        </button>
        <button onClick={onNei} style={{ width:"100%", padding:12, background:"none", color:t.tekstMut, border:"none", fontSize:14, cursor:"pointer" }}>
          {tr("hb.neiTakk")}
        </button>
      </div>
    </div>
  );
}

// ─── HOVUDAPP ─────────────────────────────────────────────────────────────────
export default function App() {
  // Supabase auth-økt. øktLasta hindrar at AuthSkjerm blinkar før vi veit status.
  const [økt, setØkt] = useState(null);
  const [øktLasta, setØktLasta] = useState(false);
  const [visAuth, setVisAuth] = useState(false); // auth-overlay (utforskande modus)
  const [visProfil, setVisProfil] = useState(false); // profil-redigering (modal)
  useEffect(() => {
    if (!supabaseKlar()) { setØktLasta(true); return; }
    supabase.auth.getSession().then(({ data }) => {
      setØkt(data?.session ?? null);
      setØktLasta(true);
    }).catch(() => setØktLasta(true));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, ny) => {
      setØkt(ny ?? null);
      if (ny) setVisAuth(false); // logga inn → lukk overlay
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);
  const harTilgang = !!økt;
  const brukerId = økt?.user?.id ?? null;
  // Kontoar med "+test" rett før krøllalfa i e-posten (t.d. namn+test1@gmail.com)
  // får testmodus-tilgang. REIN kontobasert — ingen einings-/kode-fallback.
  const harTestSuffiks = /\+test\d*@/i.test(økt?.user?.email || "");
  const [synkVersjon, setSynkVersjon] = useState(0); // bumpast etter kø-synk → re-les

  // 5b — tøm offline-køen ved innlogging og når nett kjem tilbake.
  useEffect(() => {
    if (!brukerId) return;
    let aktiv = true;
    const tøm = async () => {
      const sendt = await synkUtKo(brukerId);
      if (aktiv && sendt > 0) setSynkVersjon(v => v + 1); // re-les så nyleg synka vises
    };
    tøm();
    window.addEventListener("online", tøm);
    return () => { aktiv = false; window.removeEventListener("online", tøm); };
  }, [brukerId]);

  // 5a — LESING: hent registreringane til innlogga brukar frå Supabase.
  // Supabase er sanninga; `besøk` er lokal spegel. Feil/offline → behald cache.
  // Logga ut → tøm. Poeng/badges reknast av besøk, så vi treng berre topp_id+dato.
  useEffect(() => {
    if (!brukerId) { setBesøk([]); return; }
    if (!supabaseKlar()) return; // ingen klient → behald evt. cache
    let avbrote = false;
    supabase
      .from("registreringar")
      .select("id, topp_id, dato, bilde_url, er_hovudbilde, har_vore_hovudbilde")
      .eq("bruker_id", brukerId)
      .order("dato", { ascending: false })
      .then(({ data, error }) => {
        if (avbrote || error || !data) return; // feil/offline → behald cache
        const mapped = data.map(r => {
          const topp = ALLE_TOPPER.find(t => String(t.id) === String(r.topp_id));
          return {
            id: r.id,
            toppId: r.topp_id,
            dato: r.dato,
            namn: topp?.namn ?? "Ukjend topp",
            høgde: topp?.høgde ?? 0,
            poeng: topp ? poengForTopp(topp) : 0,
            bilde: r.bilde_url || null,
            erHovudbilde: !!r.er_hovudbilde,
            harVoreHovudbilde: !!r.har_vore_hovudbilde,
          };
        });
        // Bevar lokale, uopplasta base64-bilete (tekne offline / opplasting pågår).
        // Utan dette kan 5a-lesinga vinne kappløpet mot opplastinga og kaste
        // det ferske biletet, slik at den gamle DB-URL-en «kjem tilbake».
        setBesøk(prev => {
          const lokale = new Map((prev || []).map(b => [b.id, b.bilde]));
          return mapped.map(m => {
            const lb = lokale.get(m.id);
            return (typeof lb === "string" && lb.startsWith("data:")) ? { ...m, bilde: lb } : m;
          });
        });
      });
    return () => { avbrote = true; };
  }, [brukerId, synkVersjon]);
  // Hent profilen til innlogga brukar (visingsnamn, bosted, bursdag). Eigen rad via RLS.
  const [profil, setProfil] = useState(null);
  useEffect(() => {
    if (!brukerId) { setProfil(null); return; }
    if (!supabaseKlar()) return;
    let avbrote = false;
    supabase.from("profiles").select("visingsnamn, adresse, bursdag, epost, bilde_url, del_statistikk").eq("id", brukerId).single()
      .then(({ data, error }) => {
        if (avbrote || error || !data) return;
        setProfil(data);
      });
    return () => { avbrote = true; };
  }, [brukerId]);
  // Spegle bursdag frå profilen til localStorage, så «Bursdagsklatrar»-badgen
  // (som les fjell_bursdag) framleis verkar etter at feltet flytta til profilen.
  useEffect(() => {
    if (profil?.bursdag) lsSet("fjell_bursdag", profil.bursdag);
    else if (profil) lsRemove("fjell_bursdag");
  }, [profil]);
  // Personvern: del full statistikk med andre. Skriv med det same (ingen eiga
  // Lagre-knapp nødvendig for ein enkel av/på-brytar).
  const setDelStatistikk = useCallback(async (verdi) => {
    setProfil(p => p ? { ...p, del_statistikk: verdi } : p);
    if (!supabaseKlar() || !brukerId) return;
    await supabase.from("profiles").update({ del_statistikk: verdi }).eq("id", brukerId);
  }, [brukerId]);
  const [besøk, setBesøk] = useState(() => {
    try { return JSON.parse(lsGet("fjell_besok") || "[]"); } catch { return []; }
  });
  const [slettHovudbileteMål, setSlettHovudbileteMål] = useState(null); // besøk-objekt | null
  // 5c — TOPPBILETE-SYNK: bilete tekne offline ligg som base64 i lokal cache.
  // Ved app-opning (og kvar besøk-endring) prøver vi å laste dei opp til Storage.
  // Vellukka opplasting byter base64 → URL, så lista tømer seg sjølv. Ref-vakta
  // hindrar parallelle køyringar; feila opplastingar blir liggjande til neste forsøk.
  // NB: MÅ liggje etter besøk-deklarasjonen — deps-arrayet les besøk under render (TDZ).
  const bildeSynkPågår = useRef(false);
  useEffect(() => {
    if (!brukerId || !supabaseKlar() || bildeSynkPågår.current) return;
    const ventande = besøk.filter(b => b.bilde && b.bilde.startsWith("data:"));
    if (!ventande.length) return;
    bildeSynkPågår.current = true;
    (async () => {
      for (const b of ventande) {
        const url = await lastOppToppbilete(brukerId, b.id, b.bilde);
        if (url) setBesøk(prev => prev.map(x => x.id === b.id ? { ...x, bilde: url } : x));
      }
      bildeSynkPågår.current = false;
    })();
  }, [brukerId, besøk]);
  const [aktivListe, setAktivListe] = useState(() => parseInt(lsGet("fjell_aktivliste") || "100", 10));
  const [posisjon, setPosisjon] = useState(null);
  const [autoKommuneFilter, setAutoKommuneFilter] = useState(() => lsGet("fjell_auto_kommune") === "1");
  useEffect(() => { lsSet("fjell_auto_kommune", autoKommuneFilter ? "1" : "0"); }, [autoKommuneFilter]);
  const [kommuneForslag, setKommuneForslag] = useState(null); // kommunenamn å foreslå, eller null
  const avvisteKommunarRef = useRef(new Set()); // hindrar gjentekne forslag same økt
  const [gpsStatus, setGpsStatus] = useState("idle");
  const [testModus, setTestModus] = useState(false);
  // FIX: testModus må ALDRI overleve eit kontobyte (logg ut → logg inn med anna
  // konto). signOut() gjer ikkje ein full sideomlasting, så vanleg React-state
  // held elles fram uendra utan dette — ein reell testmodus-lekkasje til ekte kontoar.
  useEffect(() => { setTestModus(false); }, [brukerId]);
  const [adminModus, setAdminModus] = useState(false);
  useEffect(() => { setAdminModus(false); }, [brukerId]);
  const [visKartVær, setVisKartVær] = useState(false);
  const [adminPeaks, setAdminPeaks] = useState(() => {
    try { const s = lsGet("fjell_admin_peaks"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [adminEndra, setAdminEndra] = useState(false);
  useEffect(() => {
    if (!adminEndra) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [adminEndra]);
  const [adminValtTopp, setAdminValtTopp] = useState(null);
  const [darkMode, setDarkMode] = useState(() => lsGet("fjell_dark") === "1");
  // Språk: bokmål standard. Initier modul-ordboka FØR første render.
  const [språk, setSpråkState] = useState(() => {
    // Har brukaren valt språk sjølv? Respekter det. Elles: les telefonspråket.
    const lagra = lsGet("fjell_sprak");
    const valt = (lagra === "nn" || lagra === "nb") ? lagra : oppdagSpråk();
    settAktivtSprak(valt);
    return valt;
  });
  const byttSpråk = useCallback((kode) => {
    settAktivtSprak(kode);
    lsSet("fjell_sprak", kode);
    setSpråkState(kode); // tving re-render av heile treet
  }, []);
  const [farge, setFarge] = useState(() => lsGet("fjell_farge") || "oransje");
  const [fanfare, setFanfare] = useState(null);
  const [nyeBadgeKø, setNyeBadgeKø] = useState([]);
  const [kompakt, setKompakt] = useState(() => lsGet("fjell_kompakt") === "1");
  // FIX: testmodus er no REIN kontobasert (harTestSuffiks), ikkje lenger
  // einingsbasert. Den gamle testLåstOpp/TEST_KODE-mekanismen er fjerna —
  // han var lagra i localStorage per EINING, ikkje per konto, så testmodus
  // kunne bli ståande tilgjengeleg for KVEN SOM HELST konto på ei eining som
  // ein gong hadde fått inn koden. harTestSuffiks (deklarert lenger oppe)
  // er no einaste kjelde til sanning.
  // Kart-overlag: kva punkt-typar visast. "begge" | "toppar" | "hytter"
  const [punktFilter, setPunktFilterState] = useState(() => {
    const v = lsGet("fjell_punktfilter");
    return (v === "toppar" || v === "hytter") ? v : "begge";
  });
  const setPunktFilter = useCallback((v) => {
    setPunktFilterState(v);
    lsSet("fjell_punktfilter", v);
  }, []);
  const [fullskjerm, setFullskjerm] = useState(() => lsGet("fjell_full") === "1");
  const [nyleigTrykt, setNyleigTrykt] = useState(null);
  const navRef = useRef(null);
  const [navHoyde, setNavHoyde] = useState(74);
  useEffect(() => {
    function målNav() {
      if (navRef.current) {
        const rect = navRef.current.getBoundingClientRect();
        setNavHoyde(window.innerHeight - rect.top);
      }
    }
    målNav();
    window.addEventListener("resize", målNav);
    window.visualViewport?.addEventListener("resize", målNav);
    return () => {
      window.removeEventListener("resize", målNav);
      window.visualViewport?.removeEventListener("resize", målNav);
    };
  }, []);
  const [fullskjermOvergang, setFullskjermOvergang] = useState(false);
  const flyTilRef = useRef(null);
  // Hentar kjeda sine toppar og teiknar henne på kartet — byter til Kart-fana
  // og gjev han litt tid til å montere/bli klar før sjølve teikninga.
  const visKjedePaaKart = async () => {
    if (!kjedeRekord?.brukerId || !kjedeRekord?.dato) return;
    const punkt = await hentKjedeDetaljar(kjedeRekord.brukerId, kjedeRekord.dato);
    if (punkt.length < 2) return;
    setAktiv("kart");
    setTimeout(() => flyTilRef.visKjede?.(punkt), 250);
  };
  const ventandeBildeModal = useRef(null); // Ventar på badge å lukkast
  const [kartPunkt, setKartPunkt] = useState(null);
  const [adminValtId, setAdminValtId] = useState(null);
  const [adminFlyttModus, setAdminFlyttModus] = useState(false);
  const [adminLeggTilModus, setAdminLeggTilModus] = useState(false);
  const [adminLeggTilType, setAdminLeggTilType] = useState("topp"); // "topp" | "hytte"
  const [adminNyToppPunkt, setAdminNyToppPunkt] = useState(null); // { lat, lng, høgde } | null — opnar LeggTilToppModal
  const [valtTopp, setValtTopp] = useState(null);
  const [toppProfil, setToppProfil] = useState(null); // topp vist i ToppProfil-arket (frå loggen)
  // FIX: X-knappen i ToppProfil er berre overflødig når arket vert opna FRÅ
  // kartet (der «Vis på kartet» ikkje gjer noko nytt). Opna frå Logg eller frå
  // ein annan brukar sin profil, er X den einaste vegen attende utan å hoppe
  // vekk til kart-fana — difor må vi hugse kor arket vart opna frå.
  const [toppProfilFraKart, setToppProfilFraKart] = useState(false);
  const [brukarProfil, setBrukarProfil] = useState(null); // bruker_id vist i BrukarProfil-arket
  const [aktiv, setAktiv] = useState("kart");
  const [melding, setMelding] = useState(null);
  const [meldingMål, setMeldingMål] = useState(null); // topp-objekt | null — kva melding-banneret hoppar til
  const [hendingKø, setHendingKø] = useState([]); // ulesne varsel, viste éin om gongen
  const [hendingVarsel, setHendingVarsel] = useState(null); // { emoji, namn, toppNamn, topp } | null
  const [søk, setSøk] = useState("");  // ← RETTING: søk/setSøk state lagt til
  const [listeSortModus, setListeSortModus] = useState(() => lsGet("fjell_liste_sort") || "nærmaste");
  useEffect(() => { lsSet("fjell_liste_sort", listeSortModus); }, [listeSortModus]);
  const [listeVisAntal, setListeVisAntal] = useState(30);
  const [listeHovudbileteMap, setListeHovudbileteMap] = useState({});
  const [listeBesokstalMap, setListeBesokstalMap] = useState({});
  const [filter, setFilter] = useState({ fylke:"Alle", kommune:"Alle", liste:"Alle", visast:"alle", minHogde:0 });
  const [visFilter, setVisFilter] = useState(false);
  const [visFullskjermPanel, setVisFullskjermPanel] = useState(false);
  const [visStatistikk, setVisStatistikk] = useState(false);
  const [visFølgjarListeTop, setVisFølgjarListeTop] = useState(false);
  const [visLoggFilterApen, setVisLoggFilterApen] = useState(false); // kollaps for sjølve filter-innhaldet, uavhengig av om filter-funksjonen er slått på (visLoggFilter)
  const [loggFane, setLoggFane] = useState("logg");
  // Global kjede-rekord — henta lat, berre når Trofear-fana faktisk opnast.
  // null = ikkje henta enno, false = henta men ingen rekord (ingen har ≥3 enno).
  const [kjedeRekord, setKjedeRekord] = useState(null);
  // Hentar ulesne varsel (hovudbilete-teke/kjede-teke) éin gong per innlogging
  // — merkjer dei som sett med det same (passiv "sjå neste gong"-varsling,
  // ikkje ekte push, sjå referansedokumentet).
  const hendingHentaRef = useRef(false);
  useEffect(() => {
    if (!brukerId || hendingHentaRef.current) return;
    hendingHentaRef.current = true;
    hentUlesteHendingar().then(liste => {
      if (!liste.length) return;
      setHendingKø(liste);
      merkHendingarSett(liste.map(h => h.id));
    });
  }, [brukerId]);
  // Viser éin hending om gongen frå køen, i eit eige varslingskort (ikkje den
  // vesle grøne meldingBanner-toasten, sidan dette er ei sjeldnare, litt
  // tyngre hending — sjå HendingVarselKort). FIX: kjede_teke fjerna —
  // kjede-rekorden er global og alt synleg passivt i Trofear-fana, og
  // triggeren kosta ei lita, konstant kostnad på KVAR registrering for eit
  // svært sjeldan utfall. Ikkje verdt det.
  useEffect(() => {
    if (hendingVarsel || hendingKø.length === 0) return;
    const h = hendingKø[0];
    if (h.type === "hovudbilde_teke") {
      const topp = ALLE_TOPPER.find(t => String(t.id) === String(h.topp_id));
      setHendingVarsel({ emoji: "😮", namn: h.ny_eigar_namn || "—", toppNamn: topp?.namn || "—", topp: topp || null });
    }
    setHendingKø(prev => prev.slice(1));
  }, [hendingVarsel, hendingKø]);
  useEffect(() => {
    if (!hendingVarsel) return;
    const id = setTimeout(() => setHendingVarsel(null), 5000);
    return () => clearTimeout(id);
  }, [hendingVarsel]);

  useEffect(() => {
    if (aktiv !== "badge" || kjedeRekord !== null) return;
    let avbrote = false;
    hentKjedeRekord().then(res => { if (!avbrote) setKjedeRekord(res || false); });
    return () => { avbrote = true; };
  }, [aktiv, kjedeRekord]);
  const [visKjedeTopp, setVisKjedeTopp] = useState(false);
  const [kjedeTopplista, setKjedeTopplista] = useState(null); // null = ikkje henta enno
  useEffect(() => {
    if (!visKjedeTopp || kjedeTopplista !== null) return;
    let avbrote = false;
    hentKjedeTopplista().then(res => { if (!avbrote) setKjedeTopplista(res); });
    return () => { avbrote = true; };
  }, [visKjedeTopp, kjedeTopplista]);
  const [visBadgeEigar, setVisBadgeEigar] = useState(null); // badge-objekt | null
  const [visMineHovudbilete, setVisMineHovudbilete] = useState(false);
  const [badgeEigarListe, setBadgeEigarListe] = useState(null);
  useEffect(() => {
    if (!visBadgeEigar) { setBadgeEigarListe(null); return; }
    let avbrote = false;
    hentBadgeEigarar(visBadgeEigar.id).then(res => { if (!avbrote) setBadgeEigarListe(res); });
    return () => { avbrote = true; };
  }, [visBadgeEigar]);
  const [loggFilter, setLoggFilter] = useState({ periode:"alle", liste:"alle", bilde:"alle" });
  const [visLoggFilter, setVisLoggFilter] = useState(() => lsGet("fjell_loggfilter") === "1"); // "logg" | "kalender" | "år"
  // Rutenett-visning (Pinterest-liknande waterfall) — enkel CSS columns-basert
  // masonry, alternativ 1 frå diskusjonen: enklast/billegast, testa i Logg
  // fyrst. "liste" = dagens vising, "rutenett" = ny.
  const [biletVising, setBiletVising] = useState(() => lsGet("fjell_biletvising") || "liste");
  useEffect(() => { lsSet("fjell_biletvising", biletVising); }, [biletVising]);
  // Global av/på for info-overlegget (namn/tag) i rutenett-visinga — delt
  // mellom Logg og ToppProfil sin "Siste turar", akkurat som biletVising.
  // Standard PÅ (minst overraskande). Skjuler berre overlegget — klikk på
  // biletet opnar framleis fullskjerm, som viser eigar der (uendra).
  const [visRutenettInfo, setVisRutenettInfo] = useState(() => lsGet("fjell_rutenett_info") !== "0");
  // Registreringsstil: "skyv" (eksisterande SlideKnapp, uendra) eller
  // "skriv" (nytt alternativ, "Skriv i boka"). Globalt, lagra val.
  const [regStil, setRegStil] = useState(() => lsGet("fjell_reg_stil") || "skriv");
  useEffect(() => { lsSet("fjell_reg_stil", regStil); }, [regStil]);
  useEffect(() => { lsSet("fjell_rutenett_info", visRutenettInfo ? "1" : "0"); }, [visRutenettInfo]);

  // Topper-lista sin kort-layout: "nytt" (større kort, bilete som fyller ramma)
  // eller "gammalt" (den opphavlege, kompakte rada). Globalt, lagra val.
  const [listeLayoutStil, setListeLayoutStil] = useState(() => lsGet("fjell_liste_layout") || "nytt");
  useEffect(() => { lsSet("fjell_liste_layout", listeLayoutStil); }, [listeLayoutStil]);

  // Logg-kortet sin infodel: "nytt" (topp-namn som eiga chip, linja opp med
  // Del/Slett-knappane) eller "gammalt" (rein tekst). Globalt, lagra val.
  const [loggKortStil, setLoggKortStil] = useState(() => lsGet("fjell_logg_kort") || "nytt");
  useEffect(() => { lsSet("fjell_logg_kort", loggKortStil); }, [loggKortStil]);

  const [visStedssøk, setVisStedssøk] = useState(false);
  const [bildeModal, setBildeModal] = useState(null);
  const [førsteBildePopup, setFørsteBildePopup] = useState(null); // { besøkId, toppId, toppNamn }
  const [visBilde, setVisBilde] = useState(null); // { url, brukerId?, visingsnamn? } | null — fullskjerm visning

  const [søkMarkør, setSøkMarkør] = useState(null); // { lat, lng } for raudt punkt
  const [stedssøkTekst, setStedssøkTekst] = useState("");
  const [stedssøkResultat, setStedssøkResultat] = useState([]);
  const [stedssøkLastar, setStedssøkLastar] = useState(false);
  const [visHøgde, setVisHøgde] = useState(() => lsGet("fjell_vishøgde") !== "0");
  const [visMålestokk, setVisMålestokk] = useState(() => lsGet("fjell_vismålestokk") !== "0");
  const [skjulUtanBilde, setSkjulUtanBilde] = useState(() => lsGet("fjell_skjulutanbilde") === "1");
  const [posHøgde, setPosHøgde] = useState(null);
  const listeForFullskjermRef = useRef(aktivListe);

  // FIX: Slått saman to motstridande theme-color-effektar (#2C2C2E vs #1C1C1E).
  // Body-bakgrunn og theme-color skal matche header-fargen (bgCard).
  useEffect(() => {
    const farge = darkMode ? "#2C2C2E" : "#FFFFFF";
    document.body.style.background = farge;
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", farge);
  }, [darkMode]);

  // FIX: setItem utan try/catch krasja heile appen (svart skjerm) når
  // lagringa vart full av base64-bilete (~5 MB-grense på iOS).
  useEffect(() => {
    if (!lsSet("fjell_besok", JSON.stringify(besøk))) {
      setMelding(tr("lagring.full"));
      setMeldingMål(null);
      setTimeout(() => setMelding(null), 5000);
    }
  }, [besøk]);
  useEffect(() => { lsSet("fjell_vishøgde", visHøgde ? "1" : "0"); }, [visHøgde]);
  useEffect(() => { lsSet("fjell_loggfilter", visLoggFilter ? "1" : "0"); }, [visLoggFilter]);
  useEffect(() => { lsSet("fjell_vismålestokk", visMålestokk ? "1" : "0"); }, [visMålestokk]);
  useEffect(() => { lsSet("fjell_skjulutanbilde", skjulUtanBilde ? "1" : "0"); }, [skjulUtanBilde]);
  useEffect(() => { lsSet("fjell_dark", darkMode ? "1" : "0"); }, [darkMode]);
  useEffect(() => { lsSet("fjell_farge", farge); }, [farge]);
  useEffect(() => { lsSet("fjell_kompakt", kompakt ? "1" : "0"); }, [kompakt]);
  useEffect(() => {
    // FIX: resize skjer i Kart-komponenten; dropp duplikatet her som la til churn
    lsSet("fjell_full", fullskjerm ? "1" : "0");
  }, [fullskjerm]);
  useEffect(() => { lsSet("fjell_aktivliste", String(aktivListe)); }, [aktivListe]);

  const velListe = useCallback((id) => {
    setAktivListe(id);
    setFilter(f => ({ ...f, liste: id }));
    setValtTopp(null);
  }, []);

  // Del toppbilde som Instagram Stories-kort
  const delBilde = useCallback(async (b) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1080; canvas.height = 1920;
    const ctx = canvas.getContext("2d");

    // Bakgrunn
    ctx.fillStyle = "#1A1208";
    ctx.fillRect(0, 0, 1080, 1920);

    // Last inn bilde. Storage-URL-ar krev crossOrigin, elles blir canvasen
    // «tainted» og toBlob feilar stille. onerror-vakt så promiset aldri heng.
    await new Promise(res => {
      const img = new Image();
      if (b.bilde && !b.bilde.startsWith("data:")) img.crossOrigin = "anonymous";
      img.onload = () => {
        // Dekk heile canvas, behaldt proporsjonar
        const scale = Math.max(1080/img.width, 1920/img.height);
        const w = img.width * scale, h = img.height * scale;
        const x = (1080-w)/2, y = (1920-h)/2;
        ctx.drawImage(img, x, y, w, h);
        res();
      };
      img.onerror = () => res(); // bilete feila → del kortet utan foto
      img.src = b.bilde;
    });

    // Mørk gradient nedst
    const grad = ctx.createLinearGradient(0, 1200, 0, 1920);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.85)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1080, 1920);

    // Toppnamn
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 96px -apple-system, sans-serif";
    ctx.fillText(b.namn, 80, 1580);

    // Høgde
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.font = "52px -apple-system, sans-serif";
    ctx.fillText(`↑ ${b.høgde} moh`, 80, 1660);

    // Dato
    const dato = new Date(b.dato).toLocaleDateString("nb-NO", { day:"numeric", month:"long", year:"numeric" });
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font = "42px -apple-system, sans-serif";
    ctx.fillText(dato, 80, 1730);

    // Tindre-merke
    ctx.fillStyle = "#FC4C02";
    ctx.beginPath();
    ctx.roundRect(80, 1800, 280, 70, 35);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 36px -apple-system, sans-serif";
    ctx.fillText("⛰ Tindre", 110, 1846);

    // Del
    canvas.toBlob(async (blob) => {
      const fil = new File([blob], `${b.namn}-bergentopp.jpg`, { type:"image/jpeg" });
      if (navigator.share && navigator.canShare?.({ files:[fil] })) {
        await navigator.share({ files:[fil], title:`${b.namn} — Tindre`, text:`Eg besteg ${b.namn} (${b.høgde} moh)! 🏔` });
      } else {
        // Fallback — last ned
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${b.namn}-bergentopp.jpg`;
        a.click();
      }
    }, "image/jpeg", 0.92);
  }, []);

  // Stednamnsøk via Kartverket
  const søkStednamn = useCallback(async (tekst) => {
    if (!tekst.trim()) { setStedssøkResultat([]); return; }
    setStedssøkLastar(true);
    try {
      // Wildcard (*) på søket gir treff mens du skriv, i staden for å krevje
      // at heile namnet er eksakt fullført før noko dukkar opp.
      const res = await fetch(`https://ws.geonorge.no/stedsnavn/v1/sted?sok=${encodeURIComponent(tekst.trim())}*&treffPerSide=6&side=1`);
      const data = await res.json();
      setStedssøkResultat(data.navn || []);
    } catch(e) { setStedssøkResultat([]); }
    setStedssøkLastar(false);
  }, []);

  // Hent høgde for brukarposisjon
  useEffect(() => {
    if (!posisjon || !visHøgde) { setPosHøgde(null); return; }
    fetch(`https://ws.geonorge.no/hoydedata/v1/punkt?nord=${posisjon.lat.toFixed(5)}&ost=${posisjon.lng.toFixed(5)}&koordsys=4326&geoid=nn2000`)
      .then(r => r.json())
      .then(d => setPosHøgde(Math.round(d.punkter?.[0]?.z ?? 0)))
      .catch(() => setPosHøgde(null));
  }, [posisjon, visHøgde]);

  const finnGPS = useCallback(() => {
    if (!navigator.geolocation) { setGpsStatus("error"); return; }
    setGpsStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setPosisjon({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, fly: true }); setGpsStatus("ok"); },
      () => setGpsStatus("error"),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  // Siste kjende posisjon i ein ref, slik at watch/hent-funksjonane kan
  // samanlikne utan å bli re-oppretta (og utan ekstra re-rendering).
  const posisjonRef = useRef(null);
  useEffect(() => { posisjonRef.current = posisjon; }, [posisjon]);
  const GPS_MIN_FLYTT = 10; // meter — mindre rørsle oppdaterer ikkje state/kart

  // Fersk, nøyaktig posisjon på førespurnad (opning av toppanel og «Skriv i
  // boka»). maximumAge:0 = aldri bruk mellomlagra posisjon. Returnerer
  // {lat,lng,acc} eller null ved feil/timeout.
  const hentFriskPos = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ny = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy };
        const g = posisjonRef.current;
        if (!g || haversine(g.lat, g.lng, ny.lat, ny.lng) > GPS_MIN_FLYTT) { setPosisjon(ny); setGpsStatus("ok"); }
        resolve(ny);
      },
      () => resolve(null),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 }
    );
  }), []);

  // Løpande sporing medan appen er synleg. Stoppar i bakgrunnen (iOS tillèt
  // det ikkje likevel, og det sparar batteri). Startar først når løyve er gitt
  // (gpsStatus "ok"), så vi ikkje utløyser eit ekstra løyve-popup.
  const gpsOk = gpsStatus === "ok";
  useEffect(() => {
    if (!gpsOk || !navigator.geolocation) return;
    let watchId = null;
    const start = () => {
      if (watchId !== null) return;
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const ny = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy };
          const g = posisjonRef.current;
          if (!g || haversine(g.lat, g.lng, ny.lat, ny.lng) > GPS_MIN_FLYTT) setPosisjon(ny);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
      );
    };
    const stopp = () => { if (watchId !== null) { navigator.geolocation.clearWatch(watchId); watchId = null; } };
    const onSynleg = () => { if (document.visibilityState === "visible") start(); else stopp(); };
    onSynleg();
    document.addEventListener("visibilitychange", onSynleg);
    return () => { document.removeEventListener("visibilitychange", onSynleg); stopp(); };
  }, [gpsOk]);

  // Spør om posisjon automatisk med det same appen opnar, éin gong — slepp å
  // trykkje GPS-knappen manuelt. Nettlesaren viser sitt eige
  // tillatelses-popup (kan ikkje tilpassast av oss); avslår brukaren, held
  // GPS-knappen fram å fungere som vanleg manuelt fallback etterpå.
  // Lastar Caveat (løkkeskrift) dynamisk — unngår å måtte redigere BEGGE
  // index.html-filene (src/ og public/) for éin liten funksjon.
  useEffect(() => {
    if (document.getElementById("caveat-font")) return;
    const link = document.createElement("link");
    link.id = "caveat-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap";
    document.head.appendChild(link);
  }, []);
  const gpsAutoSpurtRef = useRef(false);
  useEffect(() => {
    if (gpsAutoSpurtRef.current) return;
    gpsAutoSpurtRef.current = true;
    finnGPS();
  }, [finnGPS]);

  // Auto-kommune-filter: finn næraste topp (proxy for "kva kommune er eg i",
  // sidan me ikkje har ekte kommunegrense-polygonar), og føreslå — set ALDRI
  // filteret stille. Ingen toppar innanfor 30 km → gjer ingenting.
  useEffect(() => {
    if (!autoKommuneFilter || !posisjon) return;
    let næraste = null, kortastAvstand = Infinity;
    ALLE_TOPPER.forEach(t => {
      if (t.lat == null || t.lng == null || !t.kommune) return;
      const avstand = haversine(posisjon.lat, posisjon.lng, t.lat, t.lng);
      if (avstand < kortastAvstand) { kortastAvstand = avstand; næraste = t; }
    });
    if (!næraste || kortastAvstand > 30000) return; // over 30 km — ingen relevant kommune
    const norm = normTekst(næraste.kommune);
    if (norm === filter.kommune) return; // alt filtrert til denne
    if (avvisteKommunarRef.current.has(norm)) return; // alt avvist denne økta
    setKommuneForslag({ kommune: næraste.kommune.trim(), fylke: næraste.fylke ? næraste.fylke.trim() : null });
  }, [posisjon, autoKommuneFilter, filter.kommune]);

  // FIX: stabile callbacks til Kart — var inline-funksjonar før
  const handleToppKlikk = useCallback((t) => {
    if (adminModus) { setAdminValtId(t.id); setAdminFlyttModus(false); setValtTopp(null); setKartPunkt(null); }
    else { setValtTopp(t); setKartPunkt(null); }
  }, [adminModus]);

  const handleAdminFlyttAktiv = useCallback(() => adminModus && adminFlyttModus, [adminModus, adminFlyttModus]);

  const handlePunktKlikk = useCallback(async (p) => {
    if (adminModus && adminFlyttModus && adminValtId !== null) {
      const lat5 = parseFloat(p.lat.toFixed(5));
      const lng5 = parseFloat(p.lng.toFixed(5));
      let nyPeaks = ALLE_TOPPER.map(t => t.id !== adminValtId ? t : { ...t, lat: lat5, lng: lng5 });
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${lat5}&longitude=${lng5}`);
        const d = await r.json();
        const h = Math.round(d.elevation?.[0]);
        if (h > 0) nyPeaks = nyPeaks.map(t => t.id !== adminValtId ? t : { ...t, høgde: h });
      } catch(e) {}
      setAdminPeaks(nyPeaks); lagreAdminPeaks(nyPeaks); setAdminEndra(true); setAdminFlyttModus(false);
      setTimeout(() => flyTilRef?.refresh?.(), 100);
    } else if (adminModus && adminLeggTilModus) {
      setAdminLeggTilModus(false);
      const lat5 = parseFloat(p.lat.toFixed(5));
      const lng5 = parseFloat(p.lng.toFixed(5));
      let hogde = 0;
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${lat5}&longitude=${lng5}`);
        const d = await r.json();
        const h = Math.round(d.elevation?.[0]);
        if (h > 0) hogde = h;
      } catch(e) {}
      setAdminNyToppPunkt({ lat: lat5, lng: lng5, høgde: hogde, type: adminLeggTilType === "hytte" ? "hytte" : undefined });
    } else if (!adminModus) {
      setKartPunkt(p); setValtTopp(null);
    }
  }, [adminModus, adminFlyttModus, adminLeggTilModus, adminLeggTilType, adminValtId, ALLE_TOPPER]);

  const registrerBesøk = useCallback((topp, friskPos) => {
    const regPos = friskPos || posisjon;
    // FIX: glidebrytaren (SlideKnapp) er alt sett opp til å tillate fleire
    // registreringar i testmodus (disabled={!kanRegistrere && !testModus}),
    // men denne sjekken omgjekk ikkje testModus — så brukaren vart likevel
    // stoppa her. Held fram å gjelde vanlege kontoar utan testmodus.
    if (!testModus && !kanRegistrereIdag(topp.id, besøk)) {
      setMelding(`⚠ ${topp.namn} er allereie registrert i dag`);
      setMeldingMål(null);
      setTimeout(() => setMelding(null), 2500);
      return;
    }
    const basePoeng = poengForTopp(topp);
    const streakBonus = dagstreakBonus(besøk);
    const poeng = basePoeng + streakBonus;
    // Hent cached temp for denne toppen om tilgjengeleg
    const tempCache = window._værCache?.[`${topp.lat.toFixed(4)}_${topp.lng.toFixed(4)}`];
    const nyId = lagId(); // same id lokalt og i Supabase → idempotent
    const nyBesøk = [{ id: nyId, toppId: String(topp.id), namn: topp.namn, høgde: topp.høgde, dato: new Date().toISOString(), posisjon: regPos ? { lat: regPos.lat, lng: regPos.lng } : null, poeng, streakBonus, temp: tempCache?.temp ?? null, tempEmoji: tempCache?.emoji ?? null }, ...besøk];
    const nyBesøktIds = new Set(nyBesøk.map(b => b.toppId));
    const nyFullførte = sjekkNyFullførteLister(nyBesøktIds, null);
    nyFullførte.forEach(id => settBonus(id));
    setBesøk(nyBesøk);
    // 5b: skriv til Supabase (sanninga). Optimistisk lokalt over; kø ved offline/feil.
    if (brukerId) {
      koEllerSkriv({ id: nyId, bruker_id: brukerId, topp_id: String(topp.id), dato: nyBesøk[0].dato });
    }
    setValtTopp(null);
    const gamleBadges = sjekkBadges(besøk, besøktIds, ALLE_TOPPER);
    const nyeBadgesObj = sjekkBadges(nyBesøk, nyBesøktIds, ALLE_TOPPER);
    const nyeLåste = [...nyeBadgesObj.låst].filter(id => !gamleBadges.låst.has(id));
    const nyeÅrlege = Object.entries(nyeBadgesObj.årlege).filter(([id, år]) => {
      const gamle = gamleBadges.årlege[id] || [];
      return år.length > gamle.length;
    }).map(([id]) => id);
    const alleNye = [...new Set([...nyeLåste, ...nyeÅrlege])];
    const nyeBadgeListe = alleNye.map(id => BADGES.find(b => b.id === id)).filter(Boolean);
    spelaLyd(nyFullførte.length > 0 ? "fanfare" : "registrer");
    if (nyFullførte.length > 0) {
      const id = nyFullførte[0];
      const liste = LISTER.find(l => l.id === id);
      const bonus = LISTE_BONUS[id] ?? 0;
      setFanfare({ liste, poeng, bonus });
    } else {
      setMelding("✓ " + topp.namn + " +" + basePoeng + "p" + (streakBonus > 0 ? " +" + streakBonus + "p streak 🔥" : ""));
      setMeldingMål(null);
      setTimeout(() => setMelding(null), 2500);
    }
    if (nyeBadgeListe.length > 0) {
      setTimeout(() => setNyeBadgeKø(nyeBadgeListe), nyFullførte.length > 0 ? 4000 : 3000);
    }

    // Opne bildemodal — vent om badge er på veg
    const nyBesøkId = nyBesøk[0].id;
    const bildeInfo = { toppNamn: topp.namn, besøkId: nyBesøkId, toppId: String(topp.id) };
    if (nyeBadgeListe.length > 0 || nyFullførte.length > 0) {
      ventandeBildeModal.current = bildeInfo; // Badge kjem — vent
    } else {
      setTimeout(() => setBildeModal(bildeInfo), 800);
    }
  }, [posisjon, besøk, brukerId, testModus]);

  const aksent = AKSENTAR[farge] || AKSENTAR.oransje;
  const s = lagStil(darkMode ? TEMA.dark(aksent) : TEMA.light(aksent));
  ALLE_TOPPER = adminPeaks || _BASE_TOPPER;
  // FIX: useMemo gir stabile referansar. Før vart besøktIds (ny Set) og
  // aktivTopper (nytt array) laga på kvar render → Kart bygde om alle
  // markørane ved kvart einaste tastetrykk og kvar toast.
  const besøktIds = useMemo(() => new Set(harTilgang ? besøk.map(b => b.toppId) : []), [besøk, harTilgang]);
  // Synk eigne samanliknbare badges til Supabase kvar gong Trofear-fana
  // opnast (billeg, best-effort — sjå synkBadgarTilSupabase).
  // FIX: må stå ETTER besøktIds er deklarert — stod han før, gav det ein
  // "Cannot access 'besøktIds' before initialization"-krasj ved KVAR opning
  // av appen (uansett fane), sidan useEffect sin dependency-array vert
  // evaluert med det same komponenten renderer, ikkje berre når effekten
  // faktisk køyrer.
  const badgeSynkRef = useRef(false);
  useEffect(() => {
    if (aktiv !== "badge" || !brukerId || badgeSynkRef.current) return;
    badgeSynkRef.current = true;
    const { låst } = sjekkBadges(besøk, besøktIds, ALLE_TOPPER);
    synkBadgarTilSupabase(brukerId, låst);
  }, [aktiv, brukerId, besøk, besøktIds]);
  const totalPoeng = beregnTotalPoeng(besøk, ALLE_TOPPER);

  const harFilter = filter.fylke !== "Alle" || filter.kommune !== "Alle" || filter.visast !== "alle" || (filter.minHogde||0) > 0;
  const aktivTopper = useMemo(() => (adminPeaks || _BASE_TOPPER).filter(t => {
    if (filter.fylke !== "Alle" && normTekst(t.fylke) !== filter.fylke) return false;
    if (filter.kommune !== "Alle" && normTekst(t.kommune) !== filter.kommune) return false;
    if (filter.liste !== "Alle" && !t.lister.includes(filter.liste)) return false;
    if (filter.visast === "ubesøkte" && besøktIds.has(t.id)) return false;
    if (filter.visast === "besøkte" && !besøktIds.has(t.id)) return false;
    if (filter.minHogde > 0 && (t.høgde||0) < filter.minHogde) return false;
    return true;
  }), [adminPeaks, filter, besøktIds]);

  // Same filter som aktivTopper, MEN utan filter.liste — ProgressPanel treng den
  // fulle (fylke/kommune-filtrerte) mengda for å rekne ut korrekt nemnar per kort
  // (10/20/50/100). Brukte me aktivTopper her, ville alle fire korta vist same
  // tal så snart eit listekort var valt, sidan aktivTopper då alt er snevra inn
  // til berre den valde lista (§ feilrapport frå Steingrim, "0/10 på alle korta").
  const progressTopper = useMemo(() => (adminPeaks || _BASE_TOPPER).filter(t => {
    if (filter.fylke !== "Alle" && normTekst(t.fylke) !== filter.fylke) return false;
    if (filter.kommune !== "Alle" && normTekst(t.kommune) !== filter.kommune) return false;
    if (filter.visast === "ubesøkte" && besøktIds.has(t.id)) return false;
    if (filter.visast === "besøkte" && !besøktIds.has(t.id)) return false;
    if (filter.minHogde > 0 && (t.høgde||0) < filter.minHogde) return false;
    return true;
  }), [adminPeaks, filter.fylke, filter.kommune, filter.visast, filter.minHogde, besøktIds]);

  const listeTopper = aktivTopper.filter(t => t.lister.includes(aktivListe));
  const listeFarge = LISTER.find(l => l.id === aktivListe)?.farge || "#a8d8a8";

  // Hent gjeldande hovudbilete for alle toppar i éin spørjing (ikkje éin og éin) —
  // og besøkstal per topp, viss RPC-en finst (sjå eigen SQL levert til Steingrim).
  useEffect(() => {
    if (aktiv !== "liste" || !supabaseKlar) return;
    supabase.rpc("alle_hovudbilete").then(({ data, error }) => {
      if (error || !data) return; // RPC finst ikkje enno hos alle — feilar stille
      const m = {}; data.forEach(r => { m[String(r.topp_id)] = { url: r.bilde_url, eigd: brukerId && r.bruker_id === brukerId }; });
      setListeHovudbileteMap(m);
    });
    supabase.rpc("besok_per_topp").then(({ data, error }) => {
      if (error || !data) return; // RPC finst ikkje enno hos alle — feilar stille
      const m = {}; data.forEach(r => { m[String(r.topp_id)] = r.tal_besok; });
      setListeBesokstalMap(m);
    });
  }, [aktiv]);

  useEffect(() => { setListeVisAntal(30); }, [aktivListe, søk, listeSortModus]);

  const filtrerte = listeTopper
    .filter(t => t.namn.toLowerCase().includes(søk.toLowerCase()))
    .map(t => ({ ...t, avstand: posisjon ? haversine(posisjon.lat, posisjon.lng, t.lat, t.lng) : null, besokstal: listeBesokstalMap[String(t.id)] ?? null }))
    .sort((a, b) => {
      if (listeSortModus === "høgaste") return (b.høgde||0) - (a.høgde||0);
      if (listeSortModus === "mestBesøkt") return (b.besokstal??-1) - (a.besokstal??-1);
      return (a.avstand ?? Infinity) - (b.avstand ?? Infinity);
    });
  const filtrerteViste = filtrerte.slice(0, listeVisAntal);

  // FIX: desse useCallback-HOOKS må stå FØR dei tidlege return-ane under.
  // Ligg dei etter, endrar hook-talet seg når auth går utlogga→innlogga
  // («rendered more hooks than previous render») → blank skjerm.
  const gåFullskjerm = useCallback(() => {
    listeForFullskjermRef.current = aktivListe;
    setAktiv("kart");
    setFullskjerm(true);
    setTimeout(() => velListe(100), 60);
  }, [aktivListe]);
  const avsluttFullskjerm = useCallback(() => {
    setFullskjerm(false); // header/framdrift animerer inn med ein gong
    const forrige = listeForFullskjermRef.current;
    setTimeout(() => velListe(forrige), 300); // markør-ombygging etter animasjon
  }, []);

  if (!øktLasta) {
    return <div style={{ position:"fixed", inset:0, background: darkMode ? "#2C2C2E" : "#FFFFFF" }} />;
  }
  // Lukka beta: utan konto ser ein berre innloggingsskjermen (ingen ✕, ingen kart).
  // Utan BETA_LUKKA fell ein tilbake til utforskande modus (overlay-auth via visAuth).
  if (BETA_LUKKA && !harTilgang) {
    return <AuthSkjerm darkMode={darkMode} aksent={aksent} />;
  }

  // panel-toppluft må følgje kva header som faktisk vert vist (ikkje hook — trygt etter return)
  // Fullskjerm skal berre påverke sjølve LAYOUT-et (skjule header, fjerne
  // panel-toppluft) medan du faktisk ER på Kart-fana. Sjølve fullskjerm-
  // TILSTANDEN (fullskjerm-variabelen) held fram uendra på tvers av faner —
  // det er det som gjer at han «ventar» og er på att når du kjem attende til
  // Kart. Andre faner skal alltid ha ein naturleg header/kompakt-visning.
  const fullskjermKart = fullskjerm && aktiv === "kart";
  const panelTopPad = fullskjermKart
    ? 0 // ekte fullskjerm: kartet går under statuslinja, kontrollane har eigne safe-area-offset
    : !harTilgang
      ? "calc(env(safe-area-inset-top) + 72px)"
      : kompakt
        ? "calc(env(safe-area-inset-top) + 116px)"
        : "calc(env(safe-area-inset-top) + 171px)";
  const panelStil = { ...s.panel, paddingTop: panelTopPad, paddingBottom: "calc(88px + env(safe-area-inset-bottom))" };

  return (
    <div style={s.app}>
      {adminModus && (
        <div style={{
          position:"fixed", top:0, left:0, right:0, zIndex:3000,
          background:"#fb923c", color:"white",
          padding:"calc(4px + env(safe-area-inset-top)) 14px 5px",
          fontSize:11, fontWeight:"700", textAlign:"center",
          fontFamily:"-apple-system,'SF Pro Display',sans-serif",
          letterSpacing:0.3,
        }}>
          ✏️ {tr("admin.bannerPaaSlaatt")}
        </div>
      )}
      {/* FIX: ingen fade — header vises/skjules med ein gong (mindre hakking
          i fullskjerm-overgangen, framer-motion gjorde arbeid samtidig). */}
      {!fullskjermKart && (
        <header
          style={{ ...s.header, position:"absolute", top:0, left:0, right:0, zIndex:200 }}
        >
        {adminModus && adminFlyttModus && (
          <div style={{ position:"absolute", bottom:-28, left:0, right:0, textAlign:"center", fontSize:11, color:"#fb923c", fontWeight:"600", background:darkMode?"rgba(28,28,30,0.95)":"rgba(255,255,255,0.95)", padding:"6px 0", zIndex:10 }}>
            🗺 Klikk på kartet for å flytte toppen
          </div>
        )}
        {adminModus && adminLeggTilModus && (
          <div style={{ position:"absolute", bottom:-28, left:0, right:0, textAlign:"center", fontSize:11, color:"#fb923c", fontWeight:"600", background:darkMode?"rgba(28,28,30,0.95)":"rgba(255,255,255,0.95)", padding:"6px 0", zIndex:10 }}>
            ➕ Klikk på kartet der den nye toppen skal ligge
          </div>
        )}
        <div style={s.logo} onClick={() => {
          const now = Date.now();
          if (!window._tapT) window._tapT = [];
          window._tapT.push(now);
          window._tapT = window._tapT.filter(t => now - t < 1500);
          if (window._tapT.length >= 3) {
            window._tapT = [];
            if (harTestSuffiks) {
              setTestModus(prev => { const ny = !prev; alert(ny ? tr("innst.testPaa") : tr("innst.testAv")); return ny; });
            }
          }
        }}>
          <span style={{ fontSize: 22 }}>⛰</span>
          <div>
            <div style={{...s.logoTitle, color: darkMode ? "#FFFFFF" : "#1C1C1E"}}>{tr("merke.tittel")}</div>
            {(() => {
              // Vis aktivt kommune/fylke- og/eller høgdefilter som undertekst i staden
              // for det statiske "Fjellregistrering" — tomt (skjult) når ingen filter er sett.
              const aktivStad = filter.kommune !== "Alle"
                ? ALLE_TOPPER.find(t => normTekst(t.kommune) === filter.kommune)?.kommune?.trim()
                : filter.fylke !== "Alle"
                  ? ALLE_TOPPER.find(t => normTekst(t.fylke) === filter.fylke)?.fylke?.trim()
                  : null;
              const aktivHogde = filter.minHogde > 0 ? `${filter.minHogde}+ moh` : null;
              const delar = [aktivStad, aktivHogde].filter(Boolean);
              return <div style={s.logoSub}>{delar.length ? delar.join(" · ") : "\u00A0"}</div>;
            })()}
          </div>
        </div>
        <div style={s.headerRett}>
          {harTilgang ? (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:18, fontWeight:"bold", color:"#fbbf24", fontFamily:"monospace", lineHeight:1 }}>{totalPoeng}</div>
              <div style={{ fontSize:9, color:"#6a7a4a", textTransform:"uppercase", letterSpacing:1 }}>{tr("felles.poengSmaa")}</div>
            </div>
          ) : (
            <button onClick={() => setVisAuth(true)} style={{ padding:"8px 16px", background:aksent.aksent, color:"#fff", border:"none", borderRadius:20, fontSize:13, fontWeight:"700", cursor:"pointer" }}>{tr("utforsk.loggInn")}</button>
          )}
        </div>
        </header>
      )}

      {!fullskjermKart && !kompakt && harTilgang && (
        <div
          style={{ position:"absolute", top:`calc(59px + env(safe-area-inset-top))`, left:0, right:0, zIndex:199 }}
        >
        {<ProgressPanel besøktIds={besøktIds} aktivListe={aktivListe} onVelListe={velListe} darkMode={darkMode} tema={darkMode ? TEMA.dark(aksent) : TEMA.light(aksent)} alleTopper={progressTopper} />}
        </div>
      )}
      {!fullskjermKart && kompakt && harTilgang && (
        <div style={{ position:"absolute", top:`calc(59px + env(safe-area-inset-top))`, left:0, right:0, zIndex:199, display:"flex", alignItems:"center", justifyContent:"center", gap:12, padding:"6px 16px", background: darkMode?"#2C2C2E":"#FFFFFF", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`, flexWrap:"wrap" }}>
          {LISTER.map(l => {
            const topper = progressTopper.filter(t => t.lister.includes(l.id));
            const besøkt = topper.filter(t => besøktIds.has(t.id)).length;
            const aktiv2 = l.id === aktivListe;
            return (
              <button key={l.id} onClick={() => velListe(l.id)} style={{ background: aktiv2 ? l.farge+"18" : "transparent", border:"none", cursor:"pointer", padding:"2px 8px", borderRadius:20 }}>
                <span style={{ fontSize:12, fontWeight:"700", color: aktiv2 ? l.farge : darkMode?"#48484A":"#C7C7CC" }}>
                  {besøkt}<span style={{ color: darkMode?"#48484A":"#C7C7CC", fontWeight:"400" }}>/{topper.length}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {visKjedeTopp && (
        <KjedeTopplisteModal liste={kjedeTopplista} darkMode={darkMode} aksent={aksent}
          onOpneBrukar={(bid) => { setVisKjedeTopp(false); setBrukarProfil(bid); }}
          onClose={() => setVisKjedeTopp(false)} />
      )}
      {visBadgeEigar && (
        <BadgeEigararModal badge={visBadgeEigar} liste={badgeEigarListe} darkMode={darkMode} aksent={aksent}
          onOpneBrukar={(bid) => { setVisBadgeEigar(null); setBrukarProfil(bid); }}
          onClose={() => setVisBadgeEigar(null)} />
      )}
      {slettHovudbileteMål && (
        <SlettHovudbileteModal besøk={slettHovudbileteMål} darkMode={darkMode} aksent={aksent}
          onAvbryt={() => setSlettHovudbileteMål(null)}
          onSlett={() => {
            const b = slettHovudbileteMål;
            slettToppbilete(brukerId, b.id);
            setBesøk(prev => prev.map(x => x.id===b.id ? {...x, bilde:null, erHovudbilde:false} : x));
            setSlettHovudbileteMål(null);
          }} />
      )}
      {visMineHovudbilete && (
        <MineHovudbileteModal liste={besøk.filter(b => b.erHovudbilde)} darkMode={darkMode} aksent={aksent}
          onOpneTopp={(toppId) => {
            const topp = ALLE_TOPPER.find(t => String(t.id) === String(toppId));
            if (!topp) return;
            setVisMineHovudbilete(false);
            setVisProfil(false);
            setToppProfilFraKart(false);
            setToppProfil(topp);
          }}
          onClose={() => setVisMineHovudbilete(false)} />
      )}

      {/* FULLSKJERM BILDE */}
      {visBilde && (
        <div
          onClick={() => setVisBilde(null)}
          style={{
            position:"fixed", inset:0, zIndex:100001,
            background:"rgba(0,0,0,0.95)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:16,
          }}>
          {/* FIX: wrapperen krympar til biletet sin faktiske storleik
              (objectFit:contain gjer at biletet sjeldan fyller heile
              skjermen) — X-knappen vert då bunden til BILETET sitt hjørne,
              ikkje skjermen sitt, uansett biletforhold. Ingen stopPropagation
              her — klikk på sjølve biletet skal framleis lukke, som før. */}
          <div style={{ position:"relative", display:"inline-block", maxWidth:"100%", maxHeight:"100%" }}>
            <img
              src={visBilde.url}
              alt={tr("kart.toppbilde")}
              style={{ display:"block", maxWidth:"100%", maxHeight:"calc(100vh - 32px)", borderRadius:12, objectFit:"contain" }}
            />
            {visBilde.brukerId && (
              <div
                onClick={(e) => { e.stopPropagation(); const bid = visBilde.brukerId; setVisBilde(null); setBrukarProfil(bid); }}
                style={{ position:"absolute", left:0, right:0, bottom:0, padding:"20px 14px 12px", background:"linear-gradient(transparent, rgba(0,0,0,0.75))", borderRadius:"0 0 12px 12px", display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <div style={{ width:22, height:22, borderRadius:11, overflow:"hidden", flexShrink:0, background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12 }}>
                  {visBilde.profilBilde ? <img src={visBilde.profilBilde} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <span>👤</span>}
                </div>
                <span style={{ fontSize:14, fontWeight:"700", color:"#fff", textShadow:"0 1px 2px rgba(0,0,0,0.5)" }}>
                  {visBilde.visingsnamn || "—"} →
                </span>
              </div>
            )}
            <button
              onClick={() => setVisBilde(null)}
              style={{
                position:"absolute", top:8, right:8,
                background:"rgba(255,255,255,0.15)", border:"none",
                borderRadius:"50%", width:36, height:36,
                color:"white", fontSize:18, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>✕</button>
          </div>
        </div>
      )}

      {/* BILDE MODAL */}
      {bildeModal && (
        <BildeModal
          toppNamn={bildeModal.toppNamn}
          darkMode={darkMode}
          aksent={aksent}
          onBilde={(base64, kjelde) => {
            const besøkId = bildeModal.besøkId;
            const toppId = bildeModal.toppId;
            const toppNamn = bildeModal.toppNamn;
            // Optimistisk: base64 lokalt først (verkar offline). Ved vellukka
            // opplasting byter vi til Storage-URL; feilar det, tek 5c-synken over.
            setBesøk(prev => prev.map(b =>
              b.id === besøkId ? { ...b, bilde: base64 } : b
            ));
            setBildeModal(null);
            lastOppToppbilete(brukerId, besøkId, base64).then(url => {
              if (url) setBesøk(prev => prev.map(b => b.id === besøkId ? { ...b, bilde: url } : b));
              // Hovudbilete-tilbod: berre kamera-tekne bilete.
              // - Ingen eigar enno → «første bilete»-popup.
              // - Finst ein eigar, men DENNE brukaren er leiar (flest besøk)
              //   og eig IKKJE hovudbiletet frå før → «utfordre»-popup.
              if (url && kjelde === "kamera" && toppId) {
                hentHovudbileteStatus(toppId).then(({ eigar, leiar }) => {
                  if (!eigar) {
                    setFørsteBildePopup({ modus: "forste", besøkId, toppId, toppNamn });
                  } else if (eigar !== brukerId && leiar === brukerId) {
                    setFørsteBildePopup({ modus: "utfordre", besøkId, toppId, toppNamn });
                  }
                });
              }
            });
          }}
          onHopp={() => setBildeModal(null)}
        />
      )}

      {/* FØRSTE-BILETE / UTFORDRE-POPUP */}
      {førsteBildePopup && (
        <FørsteBildePopup
          modus={førsteBildePopup.modus}
          toppNamn={førsteBildePopup.toppNamn}
          darkMode={darkMode} aksent={aksent}
          onJa={() => {
            const { besøkId, toppId, toppNamn, modus } = førsteBildePopup;
            setFørsteBildePopup(null);
            const handling = modus === "utfordre" ? utfordreHovudbilde(toppId, besøkId) : settHovudbilde(besøkId);
            handling.then(ok => {
              if (!ok) {
                setMelding(modus === "utfordre"
                  ? tr("hb.utfordreFeila")
                  : tr("hb.andreSette"));
                setMeldingMål(null);
              } else {
                // FIX: ingen tilbakemelding vart gjeven ved suksess tidlegare.
                // Ikkje tving vidare til toppen (kan vere midt i ein tur med
                // fleire registreringar på gang) — men gjer meldinga klikkbar
                // slik at ein KAN hoppe dit om ein vil sjå resultatet.
                setMelding(tr("hb.settSuksess", { namn: toppNamn }));
                const målTopp = ALLE_TOPPER.find(t => String(t.id) === String(toppId));
                setMeldingMål(målTopp || null);
                setBesøk(prev => prev.map(b => b.id === besøkId ? { ...b, erHovudbilde: true, harVoreHovudbilde: true } : b));
              }
              setTimeout(() => { setMelding(null); setMeldingMål(null); }, 4000);
            });
          }}
          onNei={() => setFørsteBildePopup(null)}
        />
      )}

      {fanfare && (<FanfareModal liste={fanfare.liste} poeng={fanfare.poeng} bonus={fanfare.bonus} onLukk={() => setFanfare(null)} aksent={aksent.aksent} />)}
      {nyeBadgeKø.length > 0 && !fanfare && (
        <BadgeModal badges={nyeBadgeKø} aksent={aksent} darkMode={darkMode}
          onLukk={() => {
            setNyeBadgeKø([]);
            // Vis bildemodal etter at badge er lukka
            if (ventandeBildeModal.current) {
              setBildeModal(ventandeBildeModal.current);
              ventandeBildeModal.current = null;
            }
          }} />
      )}
      {melding && (
        <div
          style={{ ...s.meldingBanner, cursor: meldingMål ? "pointer" : "default" }}
          onClick={() => {
            if (!meldingMål) return;
            setToppProfilFraKart(false);
            setToppProfil(meldingMål);
            setMelding(null); setMeldingMål(null);
          }}
        >{melding}</div>
      )}
      {hendingVarsel && (
        <HendingVarselKort
          emoji={hendingVarsel.emoji} namn={hendingVarsel.namn} toppNamn={hendingVarsel.toppNamn}
          darkMode={darkMode} aksent={aksent}
          onKlikk={() => {
            if (hendingVarsel.topp) { setToppProfilFraKart(false); setToppProfil(hendingVarsel.topp); }
            setHendingVarsel(null);
          }}
          onLukk={() => setHendingVarsel(null)}
        />
      )}

      {kommuneForslag && (
        <div style={{
          position:"fixed", left:14, right:14, bottom:"calc(76px + env(safe-area-inset-bottom))", zIndex:1500,
          background:darkMode?"rgba(28,28,30,0.97)":"rgba(255,255,255,0.97)", borderRadius:16,
          padding:"14px 16px", boxShadow:"0 6px 24px rgba(0,0,0,0.25)",
          fontFamily:"-apple-system,'SF Pro Display',sans-serif",
        }}>
          <div style={{ fontSize:14, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", marginBottom:10 }}>
            📍 {tr("filter.foreslattKommune", { kommune: kommuneForslag.kommune })}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => {
                avvisteKommunarRef.current.add(normTekst(kommuneForslag.kommune));
                // Nei til kommune-filter → fell tilbake til fylke-filter i staden
                // for ingenting, om me faktisk veit kva fylke kommunen høyrer til.
                if (kommuneForslag.fylke) {
                  setFilter(f => ({ ...f, fylke: normTekst(kommuneForslag.fylke), kommune:"Alle" }));
                }
                setKommuneForslag(null);
              }}
              style={{ flex:1, padding:"9px", borderRadius:10, border:`1px solid ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}`, background:"transparent", color:darkMode?"#FFFFFF":"#1C1C1E", fontSize:13, fontWeight:"600", cursor:"pointer" }}>
              {tr("filter.nei")}
            </button>
            <button onClick={() => {
                setFilter(f => ({ ...f, fylke:"Alle", kommune: normTekst(kommuneForslag.kommune) }));
                setKommuneForslag(null);
              }}
              style={{ flex:1, padding:"9px", borderRadius:10, border:"none", background:aksent.aksent, color:"white", fontSize:13, fontWeight:"700", cursor:"pointer" }}>
              {tr("filter.ja")}
            </button>
          </div>
        </div>
      )}

      {/* ── STEDNAMNSØK — fullskjerm-side ───────────────────────── */}
      {visStedssøk && (
        <div style={{
          position:"fixed", inset:0, zIndex:9000,
          background:darkMode?"#1C1C1E":"#F2F2F7",
          display:"flex", flexDirection:"column",
          fontFamily:"-apple-system,'SF Pro Display',sans-serif",
        }}>
          {/* Søkefelt-header */}
          <div style={{
            display:"flex", alignItems:"center", gap:10,
            padding:"12px 16px",
            background:darkMode?"#2C2C2E":"#FFFFFF",
            borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
            paddingTop:"calc(12px + env(safe-area-inset-top))",
          }}>
            <div style={{
              flex:1, display:"flex", alignItems:"center", gap:8,
              background:darkMode?"#3A3A3C":"#F2F2F7",
              borderRadius:12, padding:"8px 12px",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={darkMode?"#8E8E93":"#8E8E93"} strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/>
              </svg>
              <input
                autoFocus
                value={stedssøkTekst}
                onChange={e => { setStedssøkTekst(e.target.value); søkStednamn(e.target.value); }}
                placeholder={tr("kart.sokStadnamn")}
                style={{ flex:1, border:"none", outline:"none", fontSize:16, background:"transparent", color:darkMode?"#FFFFFF":"#1C1C1E" }}
              />
              {stedssøkTekst && (
                <button onClick={() => { setStedssøkTekst(""); setStedssøkResultat([]); }}
                  style={{ background:"rgba(142,142,147,0.3)", border:"none", borderRadius:"50%", width:18, height:18, cursor:"pointer", fontSize:11, color:darkMode?"#FFFFFF":"#3A3A3C", display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}>✕</button>
              )}
            </div>
            <button onClick={() => { setVisStedssøk(false); setStedssøkTekst(""); setStedssøkResultat([]); }}
              style={{ background:"none", border:"none", cursor:"pointer", fontSize:15, color:aksent.aksent, fontWeight:"600", whiteSpace:"nowrap", padding:"4px 0" }}>
              Avbryt
            </button>
          </div>

          {/* Resultat-liste */}
          <div style={{ flex:1, overflowY:"auto" }}>
            {stedssøkLastar && (
              <div style={{ padding:"20px 16px", textAlign:"center", color:"#8E8E93", fontSize:14 }}>{tr("kart.sokjer")}</div>
            )}
            {!stedssøkLastar && stedssøkTekst.length > 1 && stedssøkResultat.length === 0 && (
              <div style={{ padding:"20px 16px", textAlign:"center", color:"#8E8E93", fontSize:14 }}>Ingen treff for «{stedssøkTekst}»</div>
            )}
            {!stedssøkLastar && stedssøkTekst.length === 0 && (
              <div style={{ padding:"40px 16px", textAlign:"center", color:"#8E8E93", fontSize:14 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
                Skriv eit stadnamn for å søkje
              </div>
            )}
            {stedssøkResultat.map((s, i) => {
              const lat = s.representasjonspunkt?.nord;
              const lng = s.representasjonspunkt?.øst;
              const namn = s.stedsnavn?.[0]?.skrivemåte || s.skrivemåte || "–";
              const type = s.navneobjekttype || "";
              const kommune = s.kommuner?.[0]?.kommunenavn || "";
              return (
                <div key={i}
                  onClick={() => {
                    if (lat && lng) {
                      flyTilRef.current?.(lat, lng, 14);
                      setSøkMarkør({ lat, lng });
                      setVisStedssøk(false);
                      setStedssøkTekst("");
                      setStedssøkResultat([]);
                    }
                  }}
                  style={{
                    padding:"13px 16px", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:12,
                    borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}`,
                    background:darkMode?"#2C2C2E":"#FFFFFF",
                    marginBottom: i === 0 ? 0 : 0,
                  }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:darkMode?"#3A3A3C":"#F2F2F7", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={aksent.aksent} strokeWidth="2" strokeLinecap="round">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                      <circle cx="12" cy="9" r="2.5" fill={aksent.aksent} stroke="none"/>
                    </svg>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{namn}</div>
                    <div style={{ fontSize:12, color:"#8E8E93", marginTop:1 }}>{[type, kommune].filter(Boolean).join(" · ")}</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={s.innhald}>

        {/* KART */}
        <div style={{ position:"absolute", inset:0, display: aktiv==="kart" ? "flex" : "none", flexDirection:"column" }}>

          {fullskjerm && (
            <>
              <button onClick={() => { const lister = LISTER.map(l => l.id); const idx = lister.indexOf(aktivListe); velListe(lister[(idx + 1) % lister.length]); }}
                style={{ position:"absolute", top:"calc(14px + env(safe-area-inset-top))", left:10, zIndex:500, background: darkMode?"rgba(0,0,0,0.7)":"rgba(255,255,255,0.95)", backdropFilter:"blur(10px)", borderRadius:20, padding:"6px 14px", border:`1.5px solid ${LISTER.find(l=>l.id===aktivListe)?.farge || "#a8d8a8"}`, cursor:"pointer", display:"flex", alignItems:"center", gap:6, boxShadow:"0 2px 8px rgba(0,0,0,0.2)", whiteSpace:"nowrap" }}>
                <span style={{ fontSize:12, color: darkMode?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.25)" }}>‹</span>
                <span style={{ fontSize:13, fontWeight:"800", color: LISTER.find(l=>l.id===aktivListe)?.farge || "#a8d8a8" }}>{LISTER.find(l=>l.id===aktivListe)?.label}</span>
                <span style={{ fontSize:12, color: darkMode?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.25)" }}>›</span>
              </button>
              <div style={{ position:"absolute", top:"calc(14px + env(safe-area-inset-top))", left:"50%", transform:"translateX(-50%)", zIndex:500, background: darkMode?"rgba(0,0,0,0.6)":"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", borderRadius:20, padding:"6px 14px", display:"flex", alignItems:"baseline", gap:3, boxShadow:"0 2px 8px rgba(0,0,0,0.18)" }}>
                <span style={{ fontSize:15, fontWeight:"700", color:aksent.aksent, fontFamily:"monospace" }}>{totalPoeng}</span>
                <span style={{ fontSize:9, color: darkMode?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.3)", letterSpacing:1, textTransform:"uppercase" }}>p</span>
              </div>
            </>
          )}

          {testModus && (
            <div
              onClick={() => setTestModus(false)}
              style={{
                position:"absolute",
                top:"calc(162px + env(safe-area-inset-top))",
                left:"50%", transform:"translateX(-50%)",
                zIndex:600,
                fontSize:13, fontWeight:"700",
                color:aksent.aksent,
                padding:"8px 20px",
                background:aksent.aksentMut,
                border:`1.5px solid ${aksent.aksentBord}`,
                borderRadius:24,
                boxShadow:"0 2px 12px rgba(0,0,0,0.25)",
                cursor:"pointer",
                whiteSpace:"nowrap",
                backdropFilter:"blur(8px)",
              }}>
              🧪 TESTMODUS — trykk for å slå av
            </div>
          )}

          <Kart posisjon={posisjon} besøktIds={besøktIds} søkMarkør={søkMarkør} setSøkMarkør={setSøkMarkør}
            finnGPS={finnGPS} gpsStatus={gpsStatus}
            punktFilter={punktFilter}
            onToggleFullskjerm={() => { if(!fullskjerm){ gåFullskjerm(); } else { avsluttFullskjerm(); } }}
            erFullskjerm={fullskjerm}
            kompakt={kompakt}
            harTilgang={harTilgang}
            aktivTopper={aktivTopper}
            adminFlyttAktiv={handleAdminFlyttAktiv}
            onToppKlikk={handleToppKlikk}
            aktivListe={aktivListe} flyTilRef={flyTilRef}
            onPunktKlikk={handlePunktKlikk} />

          {/* moh + målestokk — stabla, sentrert mellom midten og GPS */}
          {(visHøgde || visMålestokk) && (
            <div style={{
              position:"absolute", bottom:86, left:"50%", right:68,
              zIndex:500, pointerEvents:"none",
              display:"flex", flexDirection:"column", alignItems:"center", gap:1,
            }}>
              {visHøgde && posHøgde !== null && (
                <div style={{
                  fontSize:12, fontWeight:"700", color:"#C93800",
                  textShadow:"0 1px 3px rgba(255,255,255,0.85)",
                  whiteSpace:"nowrap",
                }}>
                  {posHøgde} moh
                </div>
              )}
              {visMålestokk && <Målestokk flyTilRef={flyTilRef} darkMode={darkMode} />}
            </div>
          )}

          {/* Forstørrelseglas — over filter-knappen */}
          <button
            onClick={() => { setVisStedssøk(true); setStedssøkTekst(""); setStedssøkResultat([]); }}
            style={{
              position:"absolute", bottom:146, left:16, zIndex:500,
              width:44, height:44, borderRadius:22, border:"none",
              background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)",
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:"0 2px 8px rgba(0,0,0,0.25)",
            }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1C1C1E" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="11" cy="11" r="7"/>
              <line x1="16.5" y1="16.5" x2="22" y2="22"/>
            </svg>
          </button>

          {/* Filter-knapp */}
          <div style={{ position:"absolute", bottom:90, left:16, zIndex:500 }}>
            <button onClick={() => setVisFilter(v => !v)} style={{ width:44, height:44, borderRadius:22, background: harFilter ? aksent.aksent : darkMode?"rgba(0,0,0,0.65)":"rgba(255,255,255,0.92)", border:"none", cursor:"pointer", fontSize:20, boxShadow:"0 2px 8px rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
              ≡
              {harFilter && (<div style={{ position:"absolute", top:2, right:2, width:10, height:10, borderRadius:5, background:"#FF3B30", border:"2px solid white" }}/>)}
            </button>
          </div>

          {visFilter && (
            <FilterPanel filter={filter}
              onFilterEndra={(nyFilter) => { setFilter(nyFilter); if (nyFilter.liste !== "Alle") setAktivListe(nyFilter.liste); setVisFilter(false); }}
              alleTopper={ALLE_TOPPER} besøktIds={besøktIds} darkMode={darkMode} aksent={aksent} onLukk={() => setVisFilter(false)} />
          )}

          {valtTopp && (
            <ToppPanel topp={valtTopp} posisjon={posisjon} hentFriskPos={hentFriskPos}
              besøkt={besøktIds.has(valtTopp.id)} besøkCount={besøk.filter(b => b.toppId === valtTopp.id).length}
              onRegistrer={registrerBesøk} onLukk={() => setValtTopp(null)}
              harTilgang={harTilgang} onKrevInnlogging={() => setVisAuth(true)}
              onOpneProfil={() => { setToppProfilFraKart(true); setToppProfil(valtTopp); }}
              testModus={testModus} darkMode={darkMode} aksent={aksent} navHoyde={navHoyde}
              regStil={regStil} brukarNamn={profil?.visingsnamn} />
          )}
          {kartPunkt && !valtTopp && !adminModus && (
            <KartPunktPanel punkt={kartPunkt} posisjon={posisjon} darkMode={darkMode} aksent={aksent} visVær={visKartVær} onLukk={() => setKartPunkt(null)} navHoyde={navHoyde} />
          )}

          {adminModus && ADMIN_EPOSTAR.includes((profil?.epost || "").toLowerCase()) && adminValtId===null && !adminNyToppPunkt && (
            <AdminModusFlytandePanel darkMode={darkMode} aksent={aksent} navHoyde={navHoyde}
              adminModus={adminModus} setAdminModus={setAdminModus}
              adminLeggTilModus={adminLeggTilModus} setAdminLeggTilModus={setAdminLeggTilModus}
              adminLeggTilType={adminLeggTilType} setAdminLeggTilType={setAdminLeggTilType}
              setAdminFlyttModus={setAdminFlyttModus} setAdminValtId={setAdminValtId}
              adminEndra={adminEndra} setAdminPeaks={setAdminPeaks} setAdminEndra={setAdminEndra} />
          )}

          {adminModus && adminValtId !== null && (() => {
            const topp = ALLE_TOPPER.find(t => t.id === adminValtId);
            if (!topp) return null;
            return (
              <AdminToppPanel topp={topp} darkMode={darkMode} aksent={aksent}
                flytAktiv={adminFlyttModus}
                onFlytt={(aktiv) => setAdminFlyttModus(aktiv)}
                onLagre={(oppdatert) => { const nyPeaks = ALLE_TOPPER.map(t => t.id === oppdatert.id ? oppdatert : t); setAdminPeaks(nyPeaks); lagreAdminPeaks(nyPeaks); setAdminEndra(true); setAdminValtId(null); }}
                onSlett={() => { if (!window.confirm(`Slett "${topp.namn}"?`)) return; const nyPeaks = ALLE_TOPPER.filter(t => t.id !== adminValtId); setAdminPeaks(nyPeaks); lagreAdminPeaks(nyPeaks); setAdminEndra(true); setAdminValtId(null); }}
                onLukk={() => { setAdminValtId(null); setAdminFlyttModus(false); }} />
            );
          })()}

          {adminModus && adminNyToppPunkt && (
            <LeggTilToppModal punkt={adminNyToppPunkt} alleTopper={ALLE_TOPPER} darkMode={darkMode} aksent={aksent} filter={filter}
              onLagre={(nyTopp) => {
                const nyPeaks = [...ALLE_TOPPER, nyTopp];
                setAdminPeaks(nyPeaks); lagreAdminPeaks(nyPeaks); setAdminEndra(true);
                setAdminNyToppPunkt(null);
                setAdminValtId(nyTopp.id);
                setTimeout(() => flyTilRef?.refresh?.(), 100);
              }}
              onAvbryt={() => setAdminNyToppPunkt(null)} />
          )}
        </div>

        {/* LISTE */}
        {aktiv==="liste" && (
          <div style={panelStil}>
            <input style={s.søkInput} placeholder={`Søk i ${LISTER.find(l=>l.id===aktivListe)?.label}…`} value={søk} onChange={e => setSøk(e.target.value)} />
            <div style={{ display:"flex", gap:6, marginBottom:12, overflowX:"auto" }}>
              {[["nærmaste","📍 Nærmaste"],["mestBesøkt","👥 Mest besøkt"],["høgaste","⛰ Høgaste"]].map(([id, label]) => (
                <button key={id} onClick={() => setListeSortModus(id)}
                  style={{ flexShrink:0, padding:"6px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer",
                    border:`1.5px solid ${listeSortModus===id ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`,
                    background: listeSortModus===id ? aksent.aksentMut : "transparent",
                    color: listeSortModus===id ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>{label}</button>
              ))}
            </div>
            <div style={s.toppListe}>
              {filtrerteViste.map((topp, idx) => {
                const besøkt = besøktIds.has(topp.id);
                const farge = aksent.aksent;
                const hovudbilete = listeHovudbileteMap[String(topp.id)];
                const tema = darkMode ? TEMA.dark(aksent) : TEMA.light(aksent);
                const cntTal = besøk.filter(b => String(b.toppId) === String(topp.id)).length;
                const avstandVising = topp.avstand !== null
                  ? topp.avstand < 1000
                    ? <span style={{ color: topp.avstand<=MATCH_RADIUS ? "#22cc66" : topp.avstand<300 ? "#e8c860" : "#4a6a5a" }}>{topp.avstand} m</span>
                    : <span style={{ color:"#3a5a4a" }}>{(topp.avstand/1000).toFixed(1)} km</span>
                  : <span style={{ color:"#2a3a2a", fontSize:10 }}>—</span>;

                if (listeLayoutStil === "nytt") {
                  return (
                    <div key={topp.id}
                      onClick={() => { setToppProfilFraKart(false); setToppProfil(topp); }}
                      style={{
                        display:"flex", alignItems:"stretch", height:100, borderRadius:14, overflow:"hidden", cursor:"pointer",
                        marginBottom:10,
                        background: besøkt ? tema.grønMut : tema.kortBg,
                        border:`1px solid ${besøkt ? tema.grønBord : tema.border}`,
                        boxShadow: tema.kortBg==="#FFFFFF" ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                      }}>
                      <div style={{ width:"30%", minWidth:88, maxWidth:130, flexShrink:0, position:"relative", background:darkMode?"#2C2C2E":"#F2F2F7" }}>
                        {hovudbilete ? (
                          <img src={hovudbilete.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
                        ) : (
                          <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, opacity:0.3 }}>🏔️</div>
                        )}
                        {hovudbilete?.eigd && (
                          <div style={{ position:"absolute", bottom:6, right:6, background:"#FFD60A", borderRadius:8, width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, boxShadow:"0 1px 3px rgba(0,0,0,0.3)" }}>👑</div>
                        )}
                      </div>
                      <div style={{ flex:1, minWidth:0, padding:"10px 14px", display:"flex", flexDirection:"column", justifyContent:"center", textAlign:"left" }}>
                        <div style={{ fontSize:16, fontWeight:"700", color:tema.tekst, display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ color: besøkt ? "#22cc66" : (darkMode?"#666":"#999"), flexShrink:0 }}>{besøkt ? "✓" : "○"}</span>
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{topp.namn}</span>
                        </div>
                        <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginTop:6 }}>
                          <span style={s.høgdeBadge}>{topp.høgde} m</span>
                          {cntTal > 0 && <span style={{ fontSize:10, fontWeight:"700", color:darkMode?"#1C1C1E":"#78350F", background:darkMode?"#fbbf24":"#FDE68A", padding:"2px 7px", borderRadius:8, fontFamily:"monospace", flexShrink:0 }}>×{cntTal}</span>}
                          {topp.kommune && <span style={{ fontSize:10, color: farge }}>{topp.kommune}</span>}
                          {topp.besokstal != null && <span style={{ fontSize:10, color:darkMode?"#8E8E93":"#6C6C70" }}>👥 {topp.besokstal}</span>}
                          {topp.gruppe && <span style={{ fontSize:10, color: farge }}>{topp.gruppe}</span>}
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", padding:"0 14px", flexShrink:0, fontSize:13, fontWeight:"700" }}>
                        {avstandVising}
                      </div>
                    </div>
                  );
                }

                // ── GAMMAL LAYOUT (uendra, valfri via innstillingane) ──────────────
                return (
                  <div key={topp.id} style={{ ...s.toppKort, ...(besøkt ? s.toppKortBesøkt : {}), borderLeftColor: besøkt ? "#30D158" : farge, gap:10 }}
                    onClick={() => { setToppProfilFraKart(false); setToppProfil(topp); }}>
                    {hovudbilete && (
                      <div style={{ position:"relative", width:52, height:52, flexShrink:0 }}>
                        <div style={{ width:52, height:52, borderRadius:10, overflow:"hidden", background:darkMode?"#2C2C2E":"#F2F2F7", border: hovudbilete.eigd ? "2px solid #FFD60A" : "none" }}>
                          <img src={hovudbilete.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
                        </div>
                        {hovudbilete.eigd && (
                          <div style={{ position:"absolute", bottom:-4, right:-4, background:"#FFD60A", borderRadius:8, width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, boxShadow:"0 1px 3px rgba(0,0,0,0.3)" }}>👑</div>
                        )}
                      </div>
                    )}
                    <div style={s.toppVenstre}>
                      {!hovudbilete && <div style={{ color: besøkt ? "#22cc66" : "#333", fontSize: 16, width: 20, textAlign: "center" }}>{besøkt ? "✓" : "○"}</div>}
                      <div>
                        <div style={s.toppNamn}>
                          {hovudbilete && <span style={{ color: besøkt ? "#22cc66" : "#333", marginRight:6 }}>{besøkt ? "✓" : "○"}</span>}
                          {topp.namn}
                        </div>
                        <div style={s.toppMeta}>
                          <span style={s.høgdeBadge}>{topp.høgde} m</span>
                          {cntTal > 0 && <span style={{ fontSize:10, fontWeight:"700", color:darkMode?"#1C1C1E":"#78350F", background:darkMode?"#fbbf24":"#FDE68A", padding:"2px 7px", borderRadius:8, fontFamily:"monospace" }}>×{cntTal}</span>}
                          {topp.kommune && <span style={{ fontSize:10, color: farge }}>{topp.kommune}</span>}
                          {topp.besokstal != null && <span style={{ fontSize:10, color:darkMode?"#8E8E93":"#6C6C70" }}>👥 {topp.besokstal}</span>}
                          {topp.gruppe && <span style={{ fontSize:10, color: farge }}>{topp.gruppe}</span>}
                        </div>
                      </div>
                    </div>
                    <div style={s.avstandCol}>
                      {avstandVising}
                    </div>
                  </div>
                );
              })}
              {filtrerteViste.length < filtrerte.length && (
                <div ref={el => {
                  if (!el || typeof IntersectionObserver === "undefined") return;
                  const obs = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting) setListeVisAntal(n => n + 30);
                  }, { rootMargin: "200px" });
                  obs.observe(el);
                  el._obs = obs;
                }} style={{ height:1 }}/>
              )}
            </div>
          </div>
        )}

        {/* LOGG */}
        {aktiv==="logg" && !harTilgang && (
          <div style={panelStil}>
            <InnloggingsKrav tekst={tr("utforsk.loggKrev")} onLoggInn={() => setVisAuth(true)} darkMode={darkMode} aksent={aksent} />
          </div>
        )}
        {aktiv==="logg" && harTilgang && (
          <div style={panelStil}>

            {/* Fane-veljar */}
            {besøk.length > 0 && (
              <div style={{ display:"flex", gap:6, marginBottom:16, background:darkMode?"#2C2C2E":"#F2F2F7", borderRadius:12, padding:4 }}>
                {[["logg","📋 Logg"],["kalender","📅 Kalender"],["år","📊 År"]].map(([id, label]) => (
                  <button key={id} onClick={() => setLoggFane(id)}
                    style={{ flex:1, padding:"8px 4px", border:"none", borderRadius:9, cursor:"pointer", fontSize:12, fontWeight:"700", transition:"all 0.2s",
                      background: loggFane===id ? (darkMode?"#48484A":"#FFFFFF") : "transparent",
                      color: loggFane===id ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70",
                      boxShadow: loggFane===id ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    }}>{label}</button>
                ))}
              </div>
            )}

            {besøk.length === 0 ? (
              <div style={s.tomLogg}>
                <div style={{ fontSize:48, marginBottom:16 }}>🏔️</div>
                <div style={{ color:"#5a7a5a" }}>{tr("logg.ingenBesok")}</div>
                <div style={{ fontSize:12, color:"#3a5a3a", marginTop:6, fontStyle:"italic" }}>{tr("topp.gaTilKart")}</div>
              </div>
            ) : (
              <>
                <div style={{ display:"flex", gap:10, marginBottom:12, flexWrap:"wrap" }}>
                  <div style={{ flex:1, background:"rgba(168,216,168,0.07)", border:"1px solid rgba(168,216,168,0.15)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:22, fontWeight:"bold", color:"#a8d8a8", fontFamily:"monospace" }}>{besøktIds.size}</div>
                    <div style={{ fontSize:10, color:"#5a8a5a", textTransform:"uppercase", letterSpacing:1 }}>{tr("logg.unikeToppar")}</div>
                  </div>
                  <div style={{ flex:1, background:"rgba(96,165,250,0.07)", border:"1px solid rgba(96,165,250,0.15)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:22, fontWeight:"bold", color:"#60a5fa", fontFamily:"monospace" }}>{besøk.length}</div>
                    <div style={{ fontSize:10, color:"#3a5a8a", textTransform:"uppercase", letterSpacing:1 }}>{tr("logg.totaleTurar")}</div>
                  </div>
                  <div style={{ flex:1, background:"rgba(251,191,36,0.07)", border:"1px solid rgba(251,191,36,0.15)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:22, fontWeight:"bold", color:"#fbbf24", fontFamily:"monospace" }}>{totalPoeng}</div>
                    <div style={{ fontSize:10, color:"#7a6a2a", textTransform:"uppercase", letterSpacing:1 }}>{tr("logg.poeng")}</div>
                  </div>
                </div>
                {loggFane === "logg" && <>
                <StatistikkPanel besøk={besøk} alleTopper={ALLE_TOPPER} besøktIds={besøktIds} visStatistikk={visStatistikk} setVisStatistikk={setVisStatistikk} darkMode={darkMode} aksent={aksent} onVisMineHovudbilete={() => setVisMineHovudbilete(true)}
                  delerStatistikk={profil?.del_statistikk === true} onSetDelStatistikk={setDelStatistikk} />

                <button onClick={() => setVisFølgjarListeTop(true)}
                  style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:darkMode?"#2C2C2E":"#FFFFFF", border:`1px solid ${darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.08)"}`, borderRadius:12, cursor:"pointer", marginBottom:12 }}>
                  <span style={{ fontSize:14, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E", display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:16 }}>👥</span> Følgjer
                  </span>
                  <span style={{ fontSize:16, color:aksent.aksent }}>→</span>
                </button>

                {/* LOGG FILTER */}
                {visLoggFilter && <>
                <button onClick={() => setVisLoggFilterApen(v => !v)}
                  style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:darkMode?"#2C2C2E":"#FFFFFF", border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`, borderRadius: visLoggFilterApen ? "12px 12px 0 0" : 12, cursor:"pointer", marginBottom: visLoggFilterApen ? 0 : 12 }}>
                  <span style={{ fontSize:14, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("loggf.tittel")}</span>
                  <span style={{ fontSize:16, color:darkMode?"#8E8E93":"#6C6C70", transform: visLoggFilterApen ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
                </button>
                {visLoggFilterApen && (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12, background:darkMode?"#2C2C2E":"#FFFFFF", border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`, borderTop:"none", borderRadius:"0 0 12px 12px", padding:"10px 16px 14px" }}>
                  {/* Periode */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {[["alle",tr("loggf.alle")],["veke",tr("loggf.veke")],["mnd",tr("loggf.mnd")],["år",tr("loggf.aar")]].map(([id,label]) => (
                      <button key={id} onClick={() => setLoggFilter(f => ({...f, periode:id}))}
                        style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${loggFilter.periode===id ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: loggFilter.periode===id ? aksent.aksent+"22" : "transparent", color: loggFilter.periode===id ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* Liste */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {[["alle",tr("loggf.alleLister")],["10","Topp 10"],["20","Topp 20"],["50","Topp 50"],["100","Topp 100"]].map(([id,label]) => (
                      <button key={id} onClick={() => setLoggFilter(f => ({...f, liste:id}))}
                        style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${loggFilter.liste===id ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: loggFilter.liste===id ? aksent.aksent+"22" : "transparent", color: loggFilter.liste===id ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* Bilde */}
                  <div style={{ display:"flex", gap:6 }}>
                    {[["alle",tr("loggf.alle")],["med",tr("loggf.medBilde")],["utan",tr("loggf.utanBilde")]].map(([id,label]) => (
                      <button key={id} onClick={() => setLoggFilter(f => ({...f, bilde:id}))}
                        style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${loggFilter.bilde===id ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: loggFilter.bilde===id ? aksent.aksent+"22" : "transparent", color: loggFilter.bilde===id ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                )}
                </>}
                {/* FILTRERT LOGG */}
                {(() => {
                  const no = new Date();
                  const filtrertBesøk = !visLoggFilter ? besøk : besøk.filter(b => {
                    // Periode
                    if (loggFilter.periode !== "alle") {
                      const d = new Date(b.dato);
                      if (loggFilter.periode === "veke") {
                        const dagSidan = (no - d) / 86400000;
                        if (dagSidan > 7) return false;
                      } else if (loggFilter.periode === "mnd") {
                        if (d.getMonth() !== no.getMonth() || d.getFullYear() !== no.getFullYear()) return false;
                      } else if (loggFilter.periode === "år") {
                        if (d.getFullYear() !== no.getFullYear()) return false;
                      }
                    }
                    // Liste
                    if (loggFilter.liste !== "alle") {
                      const topp = ALLE_TOPPER.find(t => String(t.id) === String(b.toppId));
                      if (!topp?.lister?.includes(parseInt(loggFilter.liste))) return false;
                    }
                    // Bilde
                    if (loggFilter.bilde === "med" && !b.bilde) return false;
                    if (loggFilter.bilde === "utan" && b.bilde) return false;
                    return true;
                  });

                  if (filtrertBesøk.length === 0) return (
                    <div style={{ textAlign:"center", padding:"32px 16px", color:darkMode?"#8E8E93":"#6C6C70", fontSize:14 }}>
                      <div style={{ fontSize:32, marginBottom:8 }}>🔍</div>
                      Ingen turar passar filteret
                    </div>
                  );

                  return (
                <>
                <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:12 }}>
                  <button onClick={() => setBiletVising("liste")}
                    style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${biletVising==="liste" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: biletVising==="liste" ? aksent.aksent+"22" : "transparent", color: biletVising==="liste" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    ☰ {tr("logg.visListe")}
                  </button>
                  <button onClick={() => setBiletVising("rutenett")}
                    style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${biletVising==="rutenett" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: biletVising==="rutenett" ? aksent.aksent+"22" : "transparent", color: biletVising==="rutenett" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    ▦ {tr("logg.visRutenett")}
                  </button>
                  {biletVising === "rutenett" && (
                    <button onClick={() => setVisRutenettInfo(v => !v)} title={tr("logg.rutenettInfo")}
                      style={{ padding:"5px 10px", borderRadius:20, fontSize:12, fontWeight:"600", cursor:"pointer", border:`1px solid ${visRutenettInfo ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: visRutenettInfo ? aksent.aksent+"22" : "transparent", color: visRutenettInfo ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                      ℹ️
                    </button>
                  )}
                </div>
                {biletVising === "rutenett" ? (() => {
                  // Rutenett-vising (alternativ 1: CSS columns). Berre besøk MED
                  // bilete gir meining her — resten (utan bilete) høyrer til
                  // liste-visinga. Ingen kropping: naturleg biletforhold per
                  // bilete er sjølve poenget med "Pinterest"-kjensla.
                  const medBilete = filtrertBesøk.filter(b => b.bilde);
                  if (!medBilete.length) return (
                    <div style={{ textAlign:"center", padding:"32px 16px", color:darkMode?"#8E8E93":"#6C6C70", fontSize:14 }}>
                      <div style={{ fontSize:32, marginBottom:8 }}>🖼️</div>
                      {tr("loggf.utanBilde")}
                    </div>
                  );
                  return (
                    <div style={{ columnCount:2, columnGap:10 }}>
                      {medBilete.map(b => (
                        <div key={b.id} onClick={() => setVisBilde({ url: b.bilde })}
                          style={{ breakInside:"avoid", marginBottom:10, borderRadius:14, overflow:"hidden", cursor:"pointer", position:"relative", background:darkMode?"#2C2C2E":"#FFFFFF", border:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}` }}>
                          <img src={b.bilde} alt={tr("kart.toppbilde")} style={{ width:"100%", height:"auto", display:"block" }}/>
                          {b.erHovudbilde && visRutenettInfo && (
                            <div style={{ position:"absolute", top:8, left:8, background:"rgba(0,0,0,0.55)", backdropFilter:"blur(6px)", borderRadius:20, padding:"3px 8px", fontSize:10, fontWeight:"700", color:"#fff" }}>
                              {tr("hb.tag")}
                            </div>
                          )}
                          {visRutenettInfo && (
                          <div style={{ position:"absolute", left:0, right:0, bottom:0, padding:"20px 8px 6px", background:"linear-gradient(transparent, rgba(0,0,0,0.7))" }}>
                            <div style={{ fontSize:11, fontWeight:"700", color:"#fff", textShadow:"0 1px 2px rgba(0,0,0,0.5)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{b.namn}</div>
                          </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })() : (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  {filtrertBesøk.map(b => {
                    const datoObj = new Date(b.dato);
                    const datoStr = datoObj.toLocaleDateString("nb-NO", { day:"numeric", month:"short", year:"numeric" });
                    const tidStr  = datoObj.toLocaleTimeString("nb-NO", { hour:"2-digit", minute:"2-digit" });
                    const besøkNr = (() => { const total = besøk.filter(x => x.toppId === b.toppId).length; const idx = besøk.filter(x => x.toppId === b.toppId).findIndex(x => x.id === b.id); return total > 1 ? total - idx : null; })();
                    return (
                      <div key={b.id}
                        onClick={() => { if (!b.bilde) setBildeModal({ toppNamn:b.namn, besøkId:b.id, toppId:b.toppId }); }}
                        style={{
                        background:darkMode?"#2C2C2E":"#FFFFFF",
                        borderRadius:16,
                        overflow:"hidden",
                        boxShadow:darkMode?"none":"0 1px 4px rgba(0,0,0,0.08)",
                        border:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}`,
                        cursor: b.bilde ? "default" : "pointer",
                      }}>
                        {/* Bilete-seksjon — reint, ingen knappar oppå */}
                        {b.bilde && (
                          <div style={{ position:"relative" }}>
                            <img src={b.bilde} alt={tr("kart.toppbilde")}
                              onClick={() => setVisBilde({ url: b.bilde })}
                              style={{ width:"100%", height:200, objectFit:"cover", display:"block", cursor:"pointer" }}/>
                            {b.temp && (
                              <div style={{ position:"absolute", bottom:10, right:10, background:"rgba(255,255,255,0.92)", backdropFilter:"blur(8px)", borderRadius:20, padding:"4px 10px", fontSize:13, fontWeight:"600", color:"#1C1C1E", display:"flex", alignItems:"center", gap:4 }}>
                                {b.tempEmoji} {b.temp}°C
                              </div>
                            )}
                            {b.erHovudbilde && (
                              <div style={{ position:"absolute", bottom:10, left:10, background:"rgba(0,0,0,0.55)", backdropFilter:"blur(6px)", borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:"700", color:"#fff", display:"flex", alignItems:"center", gap:4 }}>
                                {tr("hb.tag")}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Info-seksjon — klikk opnar toppens profil (historikk + vis på kart) */}
                        {loggKortStil === "nytt" ? (
                        <div style={{ padding:"12px 14px" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:8 }}>
                            <div
                              onClick={() => {
                                const topp = ALLE_TOPPER.find(t => String(t.id) === String(b.toppId));
                                if (!topp) return;
                                setToppProfilFraKart(false);
                                setToppProfil(topp);
                              }}
                              style={{ display:"flex", alignItems:"center", gap:6, border:`1px solid ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}`, borderRadius:20, padding:"7px 14px", cursor:"pointer", minWidth:0 }}>
                              <span style={{ fontSize:15, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.namn}</span>
                              {besøkNr && <span style={{ fontSize:11, color:"#fbbf24", fontFamily:"monospace", flexShrink:0 }}>#{besøkNr}</span>}
                              <span style={{ color:darkMode?"#8E8E93":"#6C6C70", fontSize:13, flexShrink:0 }}>›</span>
                            </div>
                            {b.bilde && (
                              <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                                <button
                                  onClick={e => { e.stopPropagation(); delBilde(b); }}
                                  style={{ background:"transparent", border:`1px solid ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}`, borderRadius:20, padding:"7px 14px", color:darkMode?"#8E8E93":"#6C6C70", fontSize:13, fontWeight:"600", cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                                  ↗ Del
                                </button>
                                <button
                                  onClick={e => { e.stopPropagation(); if (b.erHovudbilde) { setSlettHovudbileteMål(b); } else if (window.confirm(tr("logg.slettBilde"))) { slettToppbilete(brukerId, b.id); setBesøk(prev => prev.map(x => x.id===b.id ? {...x, bilde:null, erHovudbilde:false} : x)); } }}
                                  style={{ background:"transparent", border:`1px solid ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}`, borderRadius:20, padding:"7px 14px", color:darkMode?"#8E8E93":"#6C6C70", fontSize:13, cursor:"pointer" }}>
                                  🗑
                                </button>
                              </div>
                            )}
                          </div>
                          <div
                            onClick={() => {
                              const topp = ALLE_TOPPER.find(t => String(t.id) === String(b.toppId));
                              if (!topp) return;
                              setToppProfilFraKart(false);
                              setToppProfil(topp);
                            }}
                            style={{ cursor:"pointer", paddingLeft:14 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:darkMode?"#8E8E93":"#6C6C70" }}>
                              <span>↑ {b.høgde} moh</span>
                              <span>·</span>
                              <span>{datoStr} {tidStr}</span>
                              {b.poeng && (
                                <>
                                  <span>·</span>
                                  <span style={{ color:darkMode?"#fbbf24":"#CA8A04", fontWeight:"700", fontFamily:"monospace" }}>+{b.poeng}p</span>
                                  {b.streakBonus > 0 && <span style={{ color:"#fb923c" }}>🔥</span>}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        ) : (
                        <div
                          onClick={() => {
                            const topp = ALLE_TOPPER.find(t => String(t.id) === String(b.toppId));
                            if (!topp) return; // topp fjerna frå lista → ingen handling
                            setToppProfilFraKart(false);
                            setToppProfil(topp);
                          }}
                          style={{ padding:"12px 14px", display:"flex", justifyContent:"space-between", alignItems:"flex-start", cursor:"pointer" }}>
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                              <span style={{ fontSize:16, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{b.namn}</span>
                              {besøkNr && <span style={{ fontSize:11, color:"#fbbf24", fontFamily:"monospace" }}>#{besøkNr}</span>}
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:darkMode?"#8E8E93":"#6C6C70" }}>
                              <span>↑ {b.høgde} moh</span>
                              <span>·</span>
                              <span>{datoStr} {tidStr}</span>
                            </div>
                            {b.poeng && (
                              <div style={{ marginTop:4, fontSize:12, color:"#fbbf24", fontFamily:"monospace" }}>
                                +{b.poeng}p {b.streakBonus > 0 && <span style={{ color:"#fb923c" }}>🔥</span>}
                              </div>
                            )}
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                            {b.bilde && (
                              <>
                                <button
                                  onClick={e => { e.stopPropagation(); delBilde(b); }}
                                  style={{ background:"transparent", border:`1px solid ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}`, borderRadius:20, padding:"5px 10px", color:darkMode?"#8E8E93":"#6C6C70", fontSize:12, fontWeight:"600", cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                                  ↗ Del
                                </button>
                                <button
                                  onClick={e => { e.stopPropagation(); if (b.erHovudbilde) { setSlettHovudbileteMål(b); } else if (window.confirm(tr("logg.slettBilde"))) { slettToppbilete(brukerId, b.id); setBesøk(prev => prev.map(x => x.id===b.id ? {...x, bilde:null, erHovudbilde:false} : x)); } }}
                                  style={{ background:"transparent", border:`1px solid ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}`, borderRadius:20, padding:"5px 9px", color:darkMode?"#8E8E93":"#6C6C70", fontSize:12, cursor:"pointer" }}>
                                  🗑
                                </button>
                              </>
                            )}
                            {testModus && (
                              <button style={s.slettBtn} onClick={e => { e.stopPropagation(); setBesøk(prev => prev.filter(x => x.id!==b.id)); }}>✕</button>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
                </>
                  );
                })()}
                </>}
              </>
            )}

            {/* KALENDER */}
            {besøk.length > 0 && loggFane === "kalender" && (() => {
              const idag = new Date();
              const tidlegasteBesøk = besøk.length > 0 ? new Date(besøk[besøk.length-1].dato) : idag;
              const besøkDagar = new Set(besøk.map(b => new Date(b.dato).toDateString()));
              const månNamn = ["Januar","Februar","Mars","April","Mai","Juni","Juli","August","September","Oktober","November","Desember"];
              // Generer månader frå tidlegaste besøk til i dag, nyaste først
              const månader = [];
              let d = new Date(idag.getFullYear(), idag.getMonth(), 1);
              const stopp = new Date(tidlegasteBesøk.getFullYear(), tidlegasteBesøk.getMonth(), 1);
              while (d >= stopp) {
                månader.push({ år: d.getFullYear(), mnd: d.getMonth() });
                d = new Date(d.getFullYear(), d.getMonth() - 1, 1);
              }
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  {månader.map(({ år, mnd }) => {
                    const førsteDag = new Date(år, mnd, 1).getDay();
                    const offset = førsteDag === 0 ? 6 : førsteDag - 1;
                    const dagar = new Date(år, mnd+1, 0).getDate();
                    const erIdagsMånad = år === idag.getFullYear() && mnd === idag.getMonth();
                    return (
                      <div key={`${år}-${mnd}`}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
                          <div style={{ fontSize:13, fontWeight:"700", color: erIdagsMånad ? aksent.aksent : darkMode?"#FFFFFF":"#1C1C1E", letterSpacing:0.5 }}>{månNamn[mnd]}</div>
                          {erIdagsMånad && <div style={{ fontSize:10, color:aksent.aksent, fontWeight:"600" }}>{tr("logg.idag")}</div>}
                          {!erIdagsMånad && <div style={{ fontSize:11, color:darkMode?"#48484A":"#C7C7CC" }}>{år}</div>}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:3 }}>
                          {["M","T","O","T","F","L","S"].map((d,i) => (
                            <div key={i} style={{ textAlign:"center", fontSize:9, color:darkMode?"#48484A":"#C7C7CC", fontWeight:"600", paddingBottom:3 }}>{d}</div>
                          ))}
                          {Array.from({length:offset}).map((_,i) => <div key={"x"+i}/>)}
                          {Array.from({length:dagar}, (_,i) => {
                            const dag = new Date(år, mnd, i+1);
                            const harTur = besøkDagar.has(dag.toDateString());
                            const antall = besøk.filter(b => new Date(b.dato).toDateString() === dag.toDateString()).length;
                            const erIdag = erIdagsMånad && i+1 === idag.getDate();
                            return (
                              <div key={i} style={{
                                aspectRatio:"1", borderRadius:8,
                                background: harTur ? aksent.aksent : erIdag ? aksent.aksentMut : darkMode?"#2C2C2E":"#EFEFEF",
                                position:"relative",
                                outline: erIdag && !harTur ? `2px solid ${aksent.aksent}` : "none",
                                outlineOffset: 1,
                                display:"flex", flexDirection:"column",
                                padding:"4px 5px",
                                boxSizing:"border-box",
                              }}>
                                {/* Dato øvst til venstre */}
                                <div style={{
                                  fontSize:13, fontWeight: harTur||erIdag ? "700":"500",
                                  color: harTur ? "#fff" : erIdag ? aksent.aksent : darkMode?"#AAAAAA":"#444444",
                                  lineHeight:1,
                                }}>
                                  {i+1}
                                </div>
                                {/* Antal turar i midten */}
                                {antall > 0 && (
                                  <div style={{
                                    position:"absolute", inset:0,
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    fontSize:15, fontWeight:"800",
                                    color: harTur ? "rgba(255,255,255,0.9)" : aksent.aksent,
                                  }}>
                                    {antall}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* STATISTIKK PER ÅR */}
            {besøk.length > 0 && loggFane === "år" && (() => {
              const årsData = {};
              besøk.forEach(b => {
                const å = new Date(b.dato).getFullYear();
                if (!årsData[å]) årsData[å] = { turar:0, unike:new Set(), høgde:0, poeng:0, månMap:{} };
                årsData[å].turar++;
                årsData[å].unike.add(b.toppId);
                årsData[å].høgde += b.høgde || 0;
                årsData[å].poeng += b.poeng || 0;
                const mnd = new Date(b.dato).getMonth();
                årsData[å].månMap[mnd] = (årsData[å].månMap[mnd]||0)+1;
              });
              const årsListe = Object.entries(årsData).sort((a,b)=>b[0]-a[0]);
              const månNamn = ["Jan","Feb","Mar","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Des"];
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  {årsListe.map(([år, d]) => {
                    const maks = Math.max(...Object.values(d.månMap), 1);
                    return (
                      <div key={år} style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:16, padding:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}` }}>
                        <div style={{ fontSize:22, fontWeight:"800", color:aksent.aksent, marginBottom:12 }}>{år}</div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
                          {[
                            ["🏔", tr("stat.turar"), d.turar],
                            ["⛰", tr("logg.unikeToppar"), d.unike.size],
                            ["📈", tr("stat.hogdemeter"), `${(d.høgde/1000).toFixed(1)} km`],
                            ["⭐", tr("logg.poeng"), d.poeng],
                          ].map(([ikon, label, verdi]) => (
                            <div key={label} style={{ background:darkMode?"#3A3A3C":"#F2F2F7", borderRadius:10, padding:"10px 12px" }}>
                              <div style={{ fontSize:18, marginBottom:2 }}>{ikon}</div>
                              <div style={{ fontSize:16, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{verdi}</div>
                              <div style={{ fontSize:10, color:darkMode?"#8E8E93":"#6C6C70" }}>{label}</div>
                            </div>
                          ))}
                        </div>
                        {/* Månadsbar */}
                        <div style={{ display:"flex", gap:4, alignItems:"flex-end", height:48 }}>
                          {Array.from({length:12},(_,i) => {
                            const v = d.månMap[i]||0;
                            const h = v > 0 ? Math.max(8, Math.round((v/maks)*44)) : 3;
                            return (
                              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                                <div style={{ width:"100%", height:h, borderRadius:3, background: v>0 ? aksent.aksent : darkMode?"#3A3A3C":"#F2F2F7", transition:"height 0.3s" }}/>
                                <div style={{ fontSize:7, color:darkMode?"#48484A":"#C7C7CC" }}>{månNamn[i][0]}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

          </div>
        )}

        {/* BADGES */}
        {aktiv==="badge" && !harTilgang && (
          <div style={panelStil}>
            <InnloggingsKrav tekst={tr("utforsk.trofeKrev")} onLoggInn={() => setVisAuth(true)} darkMode={darkMode} aksent={aksent} />
          </div>
        )}
        {aktiv==="badge" && harTilgang && (() => {
          const { låst: låsteBadges, årlege:årlegeBadges } = sjekkBadges(besøk, besøktIds, ALLE_TOPPER);
          const kategoriar = [
            { id:"topper", namn:tr("kat.topper") },
            { id:"hogde",  namn:tr("kat.hogde") },
            { id:"lister", namn:tr("kat.lister") },
            { id:"streak", namn:tr("kat.streak") },
            { id:"tid",    namn:tr("kat.tid") },
            { id:"spesial",  namn:tr("kat.spesial") },
            { id:"hemmeleg", namn:tr("kat.hemmelege") },
          ];
          return (
            <div style={panelStil}>
              {kjedeRekord && (
                <div
                  style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, padding:"14px 16px", marginBottom:16, border:`1px solid ${aksent.aksent}44` }}>
                  <div onClick={() => kjedeRekord.brukerId && kjedeRekord.brukerId !== brukerId && setBrukarProfil(kjedeRekord.brukerId)}
                    style={{ cursor: kjedeRekord.brukerId && kjedeRekord.brukerId !== brukerId ? "pointer" : "default" }}>
                    <div style={{ fontSize:10, fontWeight:"700", letterSpacing:0.5, textTransform:"uppercase", color:aksent.aksent, marginBottom:3 }}>{tr("kjede.eyebrow")}</div>
                    <div style={{ fontSize:14, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E", lineHeight:1.3 }}>
                      {kjedeRekord.brukerId === brukerId
                        ? tr("kjede.duEigDen", { n: kjedeRekord.lengde })
                        : tr("kjede.eigd", { namn: kjedeRekord.visingsnamn || "—", n: kjedeRekord.lengde })}
                    </div>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10, gap:10 }}>
                    <span onClick={visKjedePaaKart} style={{ fontSize:12, color:aksent.aksent, fontWeight:"600", cursor:"pointer" }}>
                      🗺 {tr("kjede.visPaaKart")}
                    </span>
                    <span onClick={() => setVisKjedeTopp(true)} style={{ fontSize:12, color:aksent.aksent, fontWeight:"600", cursor:"pointer" }}>
                      {tr("kjede.seFullListe")}
                    </span>
                  </div>
                </div>
              )}
              {kjedeRekord === false && (
                // FIX: utan denne var banneret HEILT usynleg inntil nokon
                // faktisk nådde 3+ toppar — ingen ville vite at rekorden
                // fanst i det heile. Vis ei oppdagingsmelding i staden, så
                // funksjonen er synleg og noko å strekke seg etter frå dag 1.
                <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, padding:"14px 16px", marginBottom:16, border:`1px dashed ${darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.12)"}` }}>
                  <div style={{ fontSize:10, fontWeight:"700", letterSpacing:0.5, textTransform:"uppercase", color:darkMode?"#8E8E93":"#6C6C70", marginBottom:3 }}>{tr("kjede.eyebrow")}</div>
                  <div style={{ fontSize:14, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E", lineHeight:1.3 }}>🔗 {tr("kjede.ingenEnno")}</div>
                  <div onClick={() => setVisKjedeTopp(true)} style={{ textAlign:"right", marginTop:10, fontSize:12, color:aksent.aksent, fontWeight:"600", cursor:"pointer" }}>
                    {tr("kjede.seFullListe")}
                  </div>
                </div>
              )}
              <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, padding:"14px 16px", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"700", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("trofe.tittel")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>Låste opp {låsteBadges.size} av {BADGES.length}</div>
                </div>
                <div style={{ fontSize:24, fontWeight:"700", color:aksent.aksent }}>
                  {låsteBadges.size + Object.values(årlegeBadges).reduce((s,a)=>s+a.length,0)}
                  <span style={{ fontSize:13, color:darkMode?"#48484A":"#C7C7CC" }}>/{BADGES.length}+</span>
                </div>
              </div>
              {kategoriar.map(kat => {
                const katBadges = kat.id === "hemmeleg" ? BADGES.filter(b => b.hemmeleg)
                  : kat.id === "lister" ? genererListeBadges(ALLE_TOPPER, besøktIds)
                  : BADGES.filter(b => b.kategori === kat.id);
                if (!katBadges.length) return null;
                return (
                  <div key={kat.id} style={{ marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:"600", color:darkMode?"#48484A":"#C7C7CC", letterSpacing:1.5, textTransform:"uppercase", marginBottom:10 }}>{kat.namn}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      {katBadges.map(badge => {
                        const låst = låsteBadges.has(badge.id);
                        const år = badge.årleg ? (årlegeBadges[badge.id] || []) : [];
                        const harÅrlege = år.length > 0;
                        const kommunar = badge.kommunar || [];
                        const harKommunar = kommunar.length > 0;
                        const erHemmeleg = badge.hemmeleg && !låst && !harÅrlege;
                        const kanSamanlikne = erBadgeSamanliknbar(badge.id);
                        return (
                          <div key={badge.id}
                            onClick={() => kanSamanlikne && setVisBadgeEigar(badge)}
                            style={{ background: låst || harÅrlege ? (darkMode ? "#2C2C2E" : "#FFFFFF") : "transparent", border: `1.5px solid ${låst || harÅrlege ? aksent.aksentBord : darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, borderRadius:12, padding:"14px 12px", display:"flex", flexDirection:"column", alignItems:"center", gap:8, transition:"all 0.2s", position:"relative", cursor: kanSamanlikne ? "pointer" : "default" }}>
                            {!låst && !harÅrlege && !erHemmeleg && (<div style={{ position:"absolute", top:6, right:8, fontSize:10, opacity:0.4 }}>🔒</div>)}
                            {kanSamanlikne && (<div style={{ position:"absolute", top:6, left:8, fontSize:10, opacity:0.4 }}>👥</div>)}
                            <div style={{ position:"relative", display:"inline-block" }}>
                              <div style={{ fontSize:32, filter: (låst || harÅrlege) ? "none" : "grayscale(1)", opacity: (låst || harÅrlege) ? 1 : 0.5 }}>
                                {badge.id === "syttande" ? (
                                  <svg width="40" height="30" viewBox="0 0 22 16" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="22" height="16" rx="2" fill="#EF2B2D"/>
                                    <rect x="6" y="0" width="3" height="16" fill="white"/>
                                    <rect x="0" y="6" width="22" height="3" fill="white"/>
                                    <rect x="7" y="0" width="1" height="16" fill="#002868"/>
                                    <rect x="0" y="7" width="22" height="1" fill="#002868"/>
                                  </svg>
                                ) : badge.id === "sju" ? (
                                  <svg width="44" height="44" viewBox="0 0 100 110" fill="none">
                                    <path d="M50 95 L8 45 L15 20 L25 28 L28 18 L35 26 L38 14 L44 24 L47 10 L50 22 L53 10 L56 24 L62 14 L65 26 L72 18 L75 28 L85 20 L92 45 Z" fill={(låst || harÅrlege) ? aksent.aksent : (darkMode?"#48484A":"#C7C7CC")}/>
                                  </svg>
                                ) : erHemmeleg ? "🔒" : badge.ikon}
                              </div>
                              {harÅrlege && år.length > 1 && (
                                <div style={{ position:"absolute", top:-4, right:-8, background:aksent.aksent, color:"white", fontSize:10, fontWeight:"800", width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid "+(darkMode?"#1C1C1E":"#F2F2F7"), lineHeight:1 }}>{år.length}</div>
                              )}
                              {harKommunar && kommunar.length > 1 && (
                                <div style={{ position:"absolute", top:-4, right:-8, background:aksent.aksent, color:"white", fontSize:10, fontWeight:"800", width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid "+(darkMode?"#1C1C1E":"#F2F2F7"), lineHeight:1 }}>{kommunar.length}</div>
                              )}
                            </div>
                            <div style={{ textAlign:"center" }}>
                              <div style={{ fontSize:13, fontWeight:"700", color: (låst || harÅrlege) ? (darkMode?"#FFFFFF":"#1C1C1E") : (darkMode?"#8E8E93":"#6C6C70") }}>
                                {erHemmeleg ? "???" : badgeNamn(badge)}
                                {badge.hemmeleg && (låst || harÅrlege) && (<span style={{ fontSize:9, marginLeft:4, color:aksent.aksent }}>{tr("trofe.hemmeleg")}</span>)}
                              </div>
                              <div style={{ fontSize:11, color:darkMode?"#636366":"#8E8E93", marginTop:3, lineHeight:1.4 }}>
                                {erHemmeleg ? badgeHemSkildring(badge) : badge.hemmeleg && (låst || harÅrlege) ? badgeHemAvsloring(badge) || badgeSkildring(badge) : badgeSkildring(badge)}
                              </div>
                              {harÅrlege && (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginTop:6, justifyContent:"center" }}>
                                  {år.map(y => (<span key={y} style={{ fontSize:9, fontWeight:"700", color:aksent.aksent, background:aksent.aksentMut, padding:"2px 6px", borderRadius:8, letterSpacing:0.5 }}>{y}</span>))}
                                </div>
                              )}
                              {harKommunar && (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginTop:6, justifyContent:"center" }}>
                                  {kommunar.map(k => (<span key={k} style={{ fontSize:9, fontWeight:"700", color:aksent.aksent, background:aksent.aksentMut, padding:"2px 6px", borderRadius:8, letterSpacing:0.5 }}>{k}</span>))}
                                </div>
                              )}
                            </div>
                            {låst && !badge.årleg && (<div style={{ fontSize:9, fontWeight:"700", color:aksent.aksent, letterSpacing:1, textTransform:"uppercase", background:aksent.aksentMut, padding:"2px 8px", borderRadius:10 }}>{tr("trofe.lastOpp")}</div>)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* INNSTILLINGAR */}
        {aktiv==="innst" && (
          <div style={panelStil}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:darkMode?"#48484A":"#C7C7CC", marginBottom:16, fontWeight:"600" }}>{tr("innst.tittel")}</div>

            {/* PROFIL */}
            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
                  <div onClick={() => profil?.bilde_url && setVisBilde({ url: profil.bilde_url })} style={{ width:40, height:40, borderRadius:20, overflow:"hidden", flexShrink:0, cursor:profil?.bilde_url?"pointer":"default", background:darkMode?"#3A3A3C":"#F2F2F7", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {profil?.bilde_url
                      ? <img src={profil.bilde_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      : <span style={{ fontSize:18 }}>👤</span>}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{profil?.visingsnamn || tr("profil.tittel")}</div>
                    <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>
                      {(() => {
                        const bits = [];
                        if (profil?.adresse) bits.push(profil.adresse);
                        if (profil?.bursdag) { const [mm,dd] = profil.bursdag.split("-"); bits.push(`${parseInt(dd,10)}. ${MAANADER[parseInt(mm,10)-1]}`); }
                        return bits.length ? bits.join(" · ") : tr("profil.fyllUt");
                      })()}
                    </div>
                  </div>
                </div>
                <button onClick={() => setVisProfil(true)} style={{ flexShrink:0, marginLeft:12, padding:"8px 16px", background:aksent.aksentMut, color:aksent.aksent, border:`1px solid ${aksent.aksentBord}`, borderRadius:20, fontSize:13, fontWeight:"600", cursor:"pointer" }}>{tr("profil.rediger")}</button>
              </div>
            </div>

            {/* SPRÅKVAL */}
            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.sprak")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.sprakSkildring")}</div>
                </div>
                <div style={{ display:"flex", background:darkMode?"#1C1C1E":"#F2F2F7", borderRadius:10, padding:3, gap:2 }}>
                  {[["nb", tr("sprak.nb")], ["nn", tr("sprak.nn")]].map(([kode, namn]) => (
                    <button key={kode} onClick={() => byttSpråk(kode)}
                      style={{ padding:"6px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:13, fontWeight:"600",
                        background: språk===kode ? aksent.aksent : "transparent",
                        color: språk===kode ? "#FFFFFF" : darkMode?"#8E8E93":"#6C6C70", transition:"all 0.2s" }}>{namn}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* ══════════ UTSJÅNAD ══════════ */}
            <div style={{ fontSize:12, fontWeight:"700", letterSpacing:1, textTransform:"uppercase", color:darkMode?"#8E8E93":"#8E8E93", margin:"20px 4px 8px" }}>🎨 {tr("innst.katUtsjaanad")}</div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", marginBottom:4 }}>{tr("innst.fargetema")}</div>
                <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginBottom:14 }}>{tr("innst.fargetemaSkildring")}</div>
                <div style={{ display:"flex", gap:10 }}>
                  {[{ id:"oransje", farge:"#FC4C02", namn:tr("tema.oransje") }, { id:"blå", farge:"#023FC9", namn:tr("tema.bla") }].map(f => (
                    <button key={f.id} onClick={() => setFarge(f.id)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, background:"none", border:"none", cursor:"pointer", padding:0 }}>
                      <div style={{ width:44, height:44, borderRadius:12, background: f.farge, border: farge===f.id ? `3px solid ${darkMode?"#fff":"#1C1C1E"}` : "3px solid transparent", boxShadow: farge===f.id ? `0 0 0 2px ${f.farge}` : "none", transition:"all 0.2s" }}/>
                      <div style={{ fontSize:11, color: farge===f.id ? f.farge : darkMode?"#8E8E93":"#6C6C70", fontWeight: farge===f.id?"700":"400" }}>{f.namn}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"}` }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.morktTema")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.morktSkildring")}</div>
                </div>
                <div onClick={() => setDarkMode(d => !d)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: darkMode ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: darkMode ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.kompakt")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.kompaktSkildring")}</div>
                </div>
                <div onClick={() => setKompakt(k => !k)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: kompakt ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: kompakt ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
            </div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", marginBottom:10 }}>{tr("innst.listeLayout")}</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => setListeLayoutStil("nytt")}
                    style={{ flex:1, padding:"10px 8px", borderRadius:12, fontSize:13, fontWeight:"600", cursor:"pointer", border:`1.5px solid ${listeLayoutStil==="nytt" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: listeLayoutStil==="nytt" ? aksent.aksentMut : "transparent", color: listeLayoutStil==="nytt" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    🖼 {tr("innst.listeLayoutNytt")}
                  </button>
                  <button onClick={() => setListeLayoutStil("gammalt")}
                    style={{ flex:1, padding:"10px 8px", borderRadius:12, fontSize:13, fontWeight:"600", cursor:"pointer", border:`1.5px solid ${listeLayoutStil==="gammalt" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: listeLayoutStil==="gammalt" ? aksent.aksentMut : "transparent", color: listeLayoutStil==="gammalt" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    📋 {tr("innst.listeLayoutGammalt")}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", marginBottom:10 }}>{tr("innst.loggKort")}</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => setLoggKortStil("nytt")}
                    style={{ flex:1, padding:"10px 8px", borderRadius:12, fontSize:13, fontWeight:"600", cursor:"pointer", border:`1.5px solid ${loggKortStil==="nytt" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: loggKortStil==="nytt" ? aksent.aksentMut : "transparent", color: loggKortStil==="nytt" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    🏷 {tr("innst.loggKortNytt")}
                  </button>
                  <button onClick={() => setLoggKortStil("gammalt")}
                    style={{ flex:1, padding:"10px 8px", borderRadius:12, fontSize:13, fontWeight:"600", cursor:"pointer", border:`1.5px solid ${loggKortStil==="gammalt" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: loggKortStil==="gammalt" ? aksent.aksentMut : "transparent", color: loggKortStil==="gammalt" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    📋 {tr("innst.loggKortGammalt")}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", marginBottom:10 }}>{tr("innst.regStil")}</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => setRegStil("skriv")}
                    style={{ flex:1, padding:"10px 8px", borderRadius:12, fontSize:13, fontWeight:"600", cursor:"pointer", border:`1.5px solid ${regStil==="skriv" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: regStil==="skriv" ? aksent.aksentMut : "transparent", color: regStil==="skriv" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    📖 {tr("innst.regStilSkriv")}
                  </button>
                  <button onClick={() => setRegStil("skyv")}
                    style={{ flex:1, padding:"10px 8px", borderRadius:12, fontSize:13, fontWeight:"600", cursor:"pointer", border:`1.5px solid ${regStil==="skyv" ? aksent.aksent : darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, background: regStil==="skyv" ? aksent.aksentMut : "transparent", color: regStil==="skyv" ? aksent.aksent : darkMode?"#8E8E93":"#6C6C70" }}>
                    👉 {tr("innst.regStilSkyv")}
                  </button>
                </div>
              </div>
            </div>

            {/* ══════════ KART ══════════ */}
            <div style={{ fontSize:12, fontWeight:"700", letterSpacing:1, textTransform:"uppercase", color:darkMode?"#8E8E93":"#8E8E93", margin:"20px 4px 8px" }}>🗺 {tr("innst.katKart")}</div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E", marginBottom:4 }}>{tr("innst.punktFilter")}</div>
                <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginBottom:10 }}>{tr("innst.punktFilterSkildring")}</div>
                <div style={{ display:"flex", gap:6, background:darkMode?"#1C1C1E":"#F2F2F7", borderRadius:10, padding:3 }}>
                  {[{ id:"begge", namn:tr("punkt.begge") }, { id:"toppar", namn:tr("punkt.toppar") }, { id:"hytter", namn:tr("punkt.hytter") }].map(o => (
                    <button key={o.id} onClick={() => setPunktFilter(o.id)} style={{ flex:1, padding:"8px 0", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight: punktFilter===o.id?"700":"500",
                      background: punktFilter===o.id ? aksent.aksent : "transparent",
                      color: punktFilter===o.id ? "#FFFFFF" : darkMode?"#8E8E93":"#6C6C70", transition:"all 0.2s" }}>{o.namn}</button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"}` }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.verPunkt")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.verPunktSkildring")}</div>
                </div>
                <div onClick={() => setVisKartVær(v => !v)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: visKartVær ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: visKartVær ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"}` }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.visHogde")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.visHogdeSkildring")}</div>
                </div>
                <div onClick={() => setVisHøgde(v => !v)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: visHøgde ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: visHøgde ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"}` }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.visMalestokk")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.visMalestokkSkildring")}</div>
                </div>
                <div onClick={() => setVisMålestokk(v => !v)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: visMålestokk ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: visMålestokk ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.fullskjerm")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.fullskjermSkildring")}</div>
                </div>
                <div onClick={() => { if(!fullskjerm){ gåFullskjerm(); } else { avsluttFullskjerm(); } }} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: fullskjerm ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: fullskjerm ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
            </div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("filter.autoKommune")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("filter.autoKommuneSkildring")}</div>
                </div>
                <div onClick={() => setAutoKommuneFilter(v => !v)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: autoKommuneFilter ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: autoKommuneFilter ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
            </div>

            {/* ══════════ LOGG ══════════ */}
            <div style={{ fontSize:12, fontWeight:"700", letterSpacing:1, textTransform:"uppercase", color:darkMode?"#8E8E93":"#8E8E93", margin:"20px 4px 8px" }}>📋 {tr("innst.katLogg")}</div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"}` }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.filterLogg")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.filterLoggSkildring")}</div>
                </div>
                <div onClick={() => setVisLoggFilter(v => !v)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: visLoggFilter ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: visLoggFilter ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.skjulUtanBilde")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.skjulUtanBildeSkildring")}</div>
                </div>
                <div onClick={() => setSkjulUtanBilde(v => !v)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: skjulUtanBilde ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: skjulUtanBilde ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
            </div>

            {/* ══════════ PERSONVERN ══════════ */}
            <div style={{ fontSize:12, fontWeight:"700", letterSpacing:1, textTransform:"uppercase", color:darkMode?"#8E8E93":"#8E8E93", margin:"20px 4px 8px" }}>🔒 {tr("innst.katPersonvern")}</div>

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.delStatistikk")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.delStatistikkSkildring")}</div>
                </div>
                <div onClick={() => setDelStatistikk(profil?.del_statistikk === false)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: profil?.del_statistikk === false ? "#E5E5EA" : aksent.aksent, position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: profil?.del_statistikk === false ? 2 : 22, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
            </div>

            {/* ══════════ TEST & ADMIN ══════════ */}
            <div style={{ fontSize:12, fontWeight:"700", letterSpacing:1, textTransform:"uppercase", color:darkMode?"#8E8E93":"#8E8E93", margin:"20px 4px 8px" }}>🧪 {tr("innst.katTest")}</div>

            {harTestSuffiks && (
            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("innst.testmodus")}</div>
                  <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("innst.testSkildring")}</div>
                </div>
                <div onClick={() => setTestModus(t => !t)} style={{ width:51, height:31, borderRadius:16, cursor:"pointer", background: testModus ? aksent.aksent : "#E5E5EA", position:"relative", transition:"background 0.3s" }}>
                  <div style={{ position:"absolute", top:2, left: testModus ? 22 : 2, width:27, height:27, borderRadius:"50%", background:"#FFFFFF", transition:"left 0.3s", boxShadow:"0 2px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
              {testModus && (
                <div style={{ borderTop:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}`, padding:"10px 16px", display:"flex", gap:8 }}>
                  <button onClick={() => setFanfare({ liste: LISTER.find(l=>l.id===10), poeng: 100, bonus: LISTE_BONUS[10] })}
                    style={{ padding:"8px 14px", background:aksent.aksentMut, border:`1px solid ${aksent.aksentBord}`, borderRadius:10, color:"#FC4C02", fontSize:12, cursor:"pointer", fontWeight:"600" }}>{tr("innst.testFanfare")}</button>
                </div>
              )}
            </div>
            )}

            {ADMIN_EPOSTAR.includes((profil?.epost || "").toLowerCase()) && (
            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", marginBottom:16, border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <AdminModusInnhald darkMode={darkMode} adminModus={adminModus} setAdminModus={setAdminModus}
                adminLeggTilModus={adminLeggTilModus} setAdminLeggTilModus={setAdminLeggTilModus}
              adminLeggTilType={adminLeggTilType} setAdminLeggTilType={setAdminLeggTilType}
                setAdminFlyttModus={setAdminFlyttModus} setAdminValtId={setAdminValtId}
                adminEndra={adminEndra} setAdminPeaks={setAdminPeaks} setAdminEndra={setAdminEndra} />
            </div>
            )}

            <div style={{ background:darkMode?"#2C2C2E":"#FFFFFF", borderRadius:14, overflow:"hidden", border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)"}` }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}` }}>
                <div style={{ fontSize:15, fontWeight:"600", color:darkMode?"#FFFFFF":"#1C1C1E" }}>{tr("merke.namn")}</div>
                <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70", marginTop:2 }}>{tr("merke.kommune")}</div>
              </div>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}` }}>
                <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70" }}>{tr("innst.kartdata")}</div>
                <div style={{ fontSize:13, color:darkMode?"#FFFFFF":"#1C1C1E", marginTop:2 }}>© Kartverket · © OpenStreetMap</div>
              </div>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ fontSize:12, color:darkMode?"#8E8E93":"#6C6C70" }}>Toppar</div>
                <div style={{ fontSize:13, color:darkMode?"#FFFFFF":"#1C1C1E", marginTop:2 }}>{ALLE_TOPPER.length} topper i databasen</div>
              </div>
            </div>

            <button onClick={() => { if(window.confirm(tr("innst.loggUtSporsmal"))) { if (supabaseKlar()) supabase.auth.signOut(); else window.location.reload(); }}}
              style={{ width:"100%", padding:"14px", marginBottom:10, background:darkMode?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.04)", border:`1px solid ${darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.08)"}`, borderRadius:14, color:darkMode?"#8E8E93":"#6C6C70", fontSize:15, cursor:"pointer", fontWeight:"600" }}>{tr("innst.loggUt")}</button>
          </div>
        )}

      </div>

      <style>{`
        @keyframes navPop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.28) translateY(-3px); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes navGlow {
          0%   { box-shadow: 0 0 0 0 rgba(var(--nav-glow-rgb), 0); transform: scale(1); }
          35%  { box-shadow: 0 0 0 10px rgba(var(--nav-glow-rgb), 0.35); transform: scale(1.18); }
          100% { box-shadow: 0 0 0 0 rgba(var(--nav-glow-rgb), 0); transform: scale(1); }
        }
        .nav-btn { transition: transform 0.15s ease; -webkit-tap-highlight-color: transparent; }
        .nav-btn:active { transform: scale(0.88); }
        .nav-icon { display:inline-block; transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1); border-radius:50%; }
        .nav-icon.pop { animation: navPop 0.38s cubic-bezier(0.34,1.56,0.64,1); }
        .nav-icon.glow { animation: navGlow 0.5s cubic-bezier(0.34,1.56,0.64,1); }
        .nav-label { transition: opacity 0.2s ease, transform 0.2s ease; }
      `}</style>

      {/* Fyller gapet under nav (bottom:10) med same temafarge, så det ikkje ser ut som ein feil */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, height:10, background:s.nav.background, maxWidth:KOMPAKT_BREDDE, margin:"0 auto", zIndex:999 }} />

      <nav ref={navRef} style={s.nav}>
        {[
          { tab:"kart",  ikon:"🗺",  tekst:"Kart" },
          { tab:"liste", ikon:"⛰",  tekst:"Topper" },
          { tab:"logg",  ikon:"📋", tekst: besøk.length > 0 ? `Logg (${besøk.length})` : "Logg" },
          { tab:"badge", ikon:"🏆", tekst:"Trofear" },
          { tab:"innst", ikon:"⚙️", tekst:"Innst." },
        ].map(({ tab, ikon, tekst }) => {
          const aktiv2 = aktiv === tab;
          const glowRgb = aksent.aksent
            ? aksent.aksent.replace("#","").match(/.{1,2}/g).map(h => parseInt(h,16)).join(",")
            : "252,76,2";
          return (
            <button key={tab}
              className="nav-btn"
              style={{ ...s.navBtn, ...(aktiv2 ? { ...s.navAktiv, color: aksent.aksent } : { color: darkMode?"#8E8E93":"#6C6C70" }) }}
              onClick={() => {
                // FIX: fullskjerm er no ein VARIG standardmodus for Kart-fana
                // (etter avtale), ikkje eit forbigåande tab-scoped konsept.
                // Held han difor ved like sjølv om ein besøkjer andre faner —
                // trykkjer du deg attende til Kart, skal han framleis stå på.
                setAktiv(tab); setValtTopp(null);
                setNyleigTrykt(tab);
                setTimeout(() => setNyleigTrykt(prev => prev === tab ? null : prev), 500);
              }}>
              <span
                className={`nav-icon${nyleigTrykt === tab ? " pop glow" : ""}`}
                style={{ fontSize:22, transform: aktiv2 ? "scale(1.08)" : "scale(1)", "--nav-glow-rgb": glowRgb }}>
                {ikon}
              </span>
              <span className="nav-label" style={{ fontSize:10, fontWeight:"600", opacity: aktiv2 ? 1 : 0.85 }}>{tekst}</span>
            </button>
          );
        })}
      </nav>

      {/* FIX: PwaBanner fjerna frå React — finst som standalone-script i index.html.
          Dobbel banner + dobbel SW-registrering på iOS Safari elles. */}

      {/* Auth-overlay (utforskande modus → innlogging på trigger) */}
      {visAuth && <AuthSkjerm darkMode={darkMode} aksent={aksent} onClose={() => setVisAuth(false)} />}
      {visProfil && (() => {
        const { låst: eigeLåsteBadges } = sjekkBadges(besøk, besøktIds, ALLE_TOPPER);
        const talToppbilde = besøk.filter(b => b.harVoreHovudbilde).length;
        const talGjeldandeHovudbilete = besøk.filter(b => b.erHovudbilde).length;
        return (
          <ProfilModal darkMode={darkMode} aksent={aksent} brukerId={brukerId} profil={profil} onLagra={setProfil} onClose={() => setVisProfil(false)}
            låsteBadges={eigeLåsteBadges}
            alleTopper={ALLE_TOPPER}
            talToppbilde={talToppbilde}
            talGjeldandeHovudbilete={talGjeldandeHovudbilete}
            onVisMineHovudbilete={() => { setVisProfil(false); setVisMineHovudbilete(true); }}
            onVisTrofear={() => { setVisProfil(false); setAktiv("badge"); }}
            onOpneBrukar={(bid) => { setVisProfil(false); setBrukarProfil(bid); }} />
        );
      })()}
      {toppProfil && (
        <ToppProfil topp={toppProfil} darkMode={darkMode} aksent={aksent} harTilgang={harTilgang}
          brukerId={brukerId}
          alleTopper={ALLE_TOPPER}
          eigeLåsteBadges={sjekkBadges(besøk, besøktIds, ALLE_TOPPER).låst}
          eigeBesøk={besøk}
          visRutenettInfo={visRutenettInfo}
          setVisRutenettInfo={setVisRutenettInfo}
          visLukkKnapp={!toppProfilFraKart}
          skjulUtanBilde={skjulUtanBilde}
          onClose={() => setToppProfil(null)}
          onVisBilde={(url, ctx) => setVisBilde({ url, ...(ctx||{}) })}
          onOpneBrukar={(bid) => setBrukarProfil(bid)}
          onVisKart={() => {
            const topp = toppProfil;
            setToppProfil(null);
            setValtTopp(topp);
            setAktiv("kart");
            setTimeout(() => flyTilRef.current?.(topp.lat, topp.lng, 14), 150);
          }} />
      )}
      {brukarProfil && (
        <BrukarProfil brukerId={brukarProfil} darkMode={darkMode} aksent={aksent} harTilgang={harTilgang}
          mittBrukerId={brukerId}
          erAdmin={ADMIN_EPOSTAR.includes((profil?.epost || "").toLowerCase())}
          alleTopper={ALLE_TOPPER}
          skjulUtanBilde={skjulUtanBilde}
          onClose={() => setBrukarProfil(null)}
          onVisBilde={(url, ctx) => setVisBilde({ url, ...(ctx||{}) })}
          onOpneTopp={(toppId) => {
            const topp = ALLE_TOPPER.find(t => String(t.id) === String(toppId));
            if (!topp) return;
            setBrukarProfil(null);
            setToppProfilFraKart(false);
            setToppProfil(topp);
          }}
          onOpneBrukar={(bid) => setBrukarProfil(bid)} />
      )}
      {visFølgjarListeTop && (
        <FølgjarListe mittBrukerId={brukerId} alleTopper={ALLE_TOPPER} darkMode={darkMode} aksent={aksent}
          onOpneProfil={(bid) => { setVisFølgjarListeTop(false); setBrukarProfil(bid); }}
          onClose={() => setVisFølgjarListeTop(false)} />
      )}
    </div>
  );
}

// ─── TEMA-SYSTEM ─────────────────────────────────────────────────────────────
const AKSENTAR = {
  oransje: { aksent:"#FC4C02", aksentMut:"rgba(252,76,2,0.15)", aksentBord:"rgba(252,76,2,0.4)", navAktiv:"rgba(252,76,2,0.15)", aksentMutLight:"rgba(252,76,2,0.08)", navAktivLight:"rgba(252,76,2,0.08)", aksentBordLight:"rgba(252,76,2,0.3)" },
  blå:     { aksent:"#023FC9", aksentMut:"rgba(2,63,201,0.15)",  aksentBord:"rgba(2,63,201,0.4)",  navAktiv:"rgba(2,63,201,0.15)",  aksentMutLight:"rgba(2,63,201,0.08)",  navAktivLight:"rgba(2,63,201,0.08)",  aksentBordLight:"rgba(2,63,201,0.3)" },
};

const TEMA = {
  dark: (a) => ({
    bg:"#1C1C1E", bgCard:"#2C2C2E", bgCard2:"#3A3A3C", border:"rgba(255,255,255,0.1)",
    tekst:"#FFFFFF", tekstMut:"#8E8E93", tekstDim:"#48484A",
    aksent:a.aksent, aksentMut:a.aksentMut, aksentBord:a.aksentBord,
    grøn:"#30D158", grønMut:"rgba(48,209,88,0.15)", grønBord:"rgba(48,209,88,0.35)",
    raud:"#FF453A", headerBord:"rgba(255,255,255,0.08)",
    navAktiv:a.navAktiv, kortBg:"#2C2C2E", søkBg:"#3A3A3C",
  }),
  light: (a) => ({
    bg:"#F2F2F7", bgCard:"#FFFFFF", bgCard2:"#F2F2F7", border:"rgba(0,0,0,0.08)",
    tekst:"#1C1C1E", tekstMut:"#6C6C70", tekstDim:"#C7C7CC",
    aksent:a.aksent, aksentMut:a.aksentMutLight, aksentBord:a.aksentBordLight,
    grøn:"#34C759", grønMut:"rgba(52,199,89,0.1)", grønBord:"rgba(52,199,89,0.3)",
    raud:"#FF3B30", headerBord:"rgba(0,0,0,0.08)",
    navAktiv:a.navAktivLight, kortBg:"#FFFFFF", søkBg:"#FFFFFF",
  })
};

function lagStil(t) { return {
  app: { position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", flexDirection:"column", background:t.bg, color:t.tekst, fontFamily:"-apple-system,'SF Pro Display','Helvetica Neue',sans-serif", maxWidth:"min(100vw, 760px)", margin:"0 auto", overflow:"hidden" },
  header: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px 12px", paddingTop:"calc(14px + env(safe-area-inset-top))", borderBottom:`1px solid ${t.headerBord}`, flexShrink:0, background:t.bgCard, transition:"opacity 0.25s ease, transform 0.25s ease", overflow:"hidden" },
  logo: { display:"flex", alignItems:"center", gap:10 },
  logoTitle: { fontSize:18, fontWeight:"700", letterSpacing:0.5, color:t.tekst, lineHeight:1.1 },
  logoSub: { fontSize:11, color:t.tekstMut, letterSpacing:2, textTransform:"uppercase", marginTop:1 },
  headerRett: { display:"flex", alignItems:"center", gap:10 },
  gpsKnapp: { padding:"8px 14px", background:t.aksentMut, border:`1px solid ${t.aksentBord}`, borderRadius:20, color:t.aksent, fontSize:13, cursor:"pointer", fontWeight:"600" },
  gpsOk: { background:t.grønMut, borderColor:t.grønBord, color:t.grøn },
  gpsErr: { background:"rgba(255,59,48,0.1)", borderColor:"rgba(255,59,48,0.3)", color:t.raud },
  meldingBanner: { position:"fixed", top:"20%", left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg, ${t.bgCard} 0%, ${t.bgCard2} 100%)`, backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)", border:`1.5px solid ${t.grønBord}`, borderRadius:20, padding:"12px 24px", fontSize:15, color:t.grøn, fontWeight:"700", textAlign:"center", zIndex:99999, boxShadow:`0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px ${t.grønBord}`, maxWidth:"80vw", pointerEvents:"none", animation:"toastPop 0.25s cubic-bezier(0.34,1.56,0.64,1) both" },
  nav: { display:"flex", gap:0, padding:"6px 8px 6px", background:t.bgCard, borderTop:`1px solid ${t.border}`, position:"fixed", bottom:10, left:0, right:0, maxWidth:KOMPAKT_BREDDE, margin:"0 auto", zIndex:1000 },
  navBtn: { flex:1, padding:"6px 4px 6px", background:"transparent", border:"none", borderRadius:10, color:t.tekstMut, fontSize:11, cursor:"pointer", fontWeight:"600", transition:"all 0.2s", display:"flex", flexDirection:"column", alignItems:"center", gap:4 },
  navAktiv: { background:t.navAktiv, color:t.aksent },
  innhald: { flex:1, display:"flex", flexDirection:"column", overflow:"hidden", position:"relative" },
  panel: { flex:1, overflowY:"auto", padding:"12px 16px", background:t.bg, paddingTop:"calc(16px + 155px + env(safe-area-inset-top))" },
  søkInput: { width:"100%", boxSizing:"border-box", background:t.søkBg, border:`1px solid ${t.border}`, borderRadius:12, color:t.tekst, padding:"10px 14px", fontSize:14, marginBottom:10, outline:"none", boxShadow:t.søkBg==="#FFFFFF"?"0 1px 3px rgba(0,0,0,0.08)":"none" },
  toppListe: { display:"flex", flexDirection:"column", gap:8 },
  toppKort: { background:t.kortBg, border:`1px solid ${t.border}`, borderLeft:"3px solid", borderRadius:12, padding:"12px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", boxShadow:t.kortBg==="#FFFFFF"?"0 1px 3px rgba(0,0,0,0.06)":"none" },
  toppKortBesøkt: { background:t.grønMut, borderColor:t.grønBord },
  toppVenstre: { display:"flex", alignItems:"center", gap:10 },
  toppNamn: { fontSize:15, color:t.tekst, fontWeight:"600", marginBottom:3 },
  toppMeta: { display:"flex", gap:6, alignItems:"center" },
  høgdeBadge: { fontSize:11, background:t.bgCard2, padding:"2px 7px", borderRadius:8, color:t.tekstMut, fontWeight:"500" },
  avstandCol: { fontSize:13, fontWeight:"700", minWidth:50, textAlign:"right" },
  loggKort: { background:t.kortBg, border:`1px solid ${t.border}`, borderRadius:12, padding:"12px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, boxShadow:t.kortBg==="#FFFFFF"?"0 1px 3px rgba(0,0,0,0.06)":"none" },
  slettBtn: { background:"rgba(255,59,48,0.1)", border:"1px solid rgba(255,59,48,0.2)", borderRadius:8, color:t.raud, width:30, height:30, cursor:"pointer", fontSize:13, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" },
  tomLogg: { textAlign:"center", padding:"60px 20px" },
}; }
